import { useEffect, useMemo, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import {
    Search, Store, TriangleAlert, ShieldCheck, Users, Armchair, ShoppingBag, Copy, ExternalLink,
    Palette, ChevronDown, Upload, Pencil, Receipt, Link2, Ellipsis, KeyRound, RefreshCw, LogOut,
    Stethoscope, FileDown, FileUp, Trash2, Ban, Plus, X, Briefcase, QrCode, Smartphone, Printer, Bell, Plug, Globe, Camera, MessageCircle, Star, ImageIcon,
} from 'lucide-react';
import toast from 'react-hot-toast';
import api from '../../api/axios';
import { toAssetUrl } from '../../utils/assetBase';
import compressImage from '../../utils/compressImage';
import ImageCropModal from '../../components/ImageCropModal';
import SuperadminLayout from '../../components/SuperadminLayout';
import CustomSortReset from './CustomSortReset';
import { confirm } from '../../components/ConfirmDialog';

// QR cover iş saatları (strukturlu)
const HOURS_DAY_ORDER = ['mon', 'tue', 'wed', 'thu', 'fri', 'sat', 'sun'];
const HOURS_DAY_LABELS = {
    mon: 'Bazar ertəsi', tue: 'Çərşənbə axşamı', wed: 'Çərşənbə',
    thu: 'Cümə axşamı', fri: 'Cümə', sat: 'Şənbə', sun: 'Bazar',
};
const defaultHoursObj = () => ({
    mode: 'everyday',
    everyday: { open: '09:00', close: '23:00' },
    days: HOURS_DAY_ORDER.reduce((acc, k) => { acc[k] = { on: true, open: '09:00', close: '23:00' }; return acc; }, {}),
});
const parseHoursObj = (json) => {
    try {
        if (json) {
            const o = typeof json === 'string' ? JSON.parse(json) : json;
            if (o && o.mode) {
                const base = defaultHoursObj();
                return {
                    mode: o.mode === 'perday' ? 'perday' : 'everyday',
                    everyday: { ...base.everyday, ...(o.everyday || {}) },
                    days: HOURS_DAY_ORDER.reduce((acc, k) => { acc[k] = { ...base.days[k], ...((o.days || {})[k] || {}) }; return acc; }, {}),
                };
            }
        }
    } catch { /* yanlış JSON */ }
    return defaultHoursObj();
};

const PLANS = [
    { value: 'free', label: 'Pulsuz', color: 'bg-slate-100 text-slate-700' },
    { value: 'basic', label: 'Sadə', color: 'bg-blue-100 text-blue-700' },
    { value: 'pro', label: 'Pro', color: 'bg-emerald-100 text-emerald-700' }
];

const FISCAL_PROVIDERS = [
    { value: 'caspos', label: 'Caspos' },
    { value: 'smartone', label: 'SmartOne' }
];

const inputClass =
    'w-full rounded-xl border border-slate-200 bg-white px-3 py-2.5 text-sm text-slate-800 transition placeholder:text-slate-400 focus:border-sky-400 focus:outline-none focus:ring-4 focus:ring-sky-100';

const defaultFiscalForm = {
    provider: 'caspos',
    smartone: {
        enabled: false,
        manage_only: false,
        pos_terminal: false,
        send_mode: 'both',
        protocol: 'http',
        host: '5.134.48.45',
        port: '10155',
        merchantId: '',
        wsName: '',
        departmentName: '',
        departmentCode: '',
        employeeName: 'Fiscal POS',
        taxProfileCode: '4'
    },
    caspos: {
        enabled: false,
        manage_only: false,
        pos_terminal: false,
        send_mode: 'both',
        posBank: 'kapital',
        transport: 'local',
        protocol: 'http',
        host: '',
        port: '5544',
        username: '',
        password: '',
        cashierName: 'Fiscal POS',
        vatType: '1',
        codeType: '1',
        quantityTypePiece: '1',
        quantityTypeWeight: '3',
        shiftOpenAmount: '0.00'
    }
};

const defaultCreateForm = {
    name: '',
    username: '',
    username_new_system: '',
    license_dukan_code: '',
    password: '',
    phone: '',
    service_percent: '0',
    plan: 'pro',
    kitchen_display_enabled: true,
    qr_menu_enabled: true
};

const defaultEditForm = {
    name: '',
    username_new_system: '',
    license_dukan_code: '',
    phone: '',
    service_percent: '0',
    plan: 'pro',
    active: 1,
    kitchen_display_enabled: true,
    qr_menu_enabled: true,
    whatsapp_enabled: false,
    whatsapp_api_key: '',
    whatsapp_recipients: '',
    otp_login_enabled: false,
    otp_numbers: '',
    otp_login_blocked: 0,
    notify_channel: 'apns'
};

// ── Lisenziya vəziyyəti (kart + filtr + dashboard ilə eyni məntiq) ──
const LIC_TONE = {
    emerald: { chip: 'bg-emerald-50 text-emerald-700 ring-emerald-200', bar: 'bg-emerald-500' },
    amber:   { chip: 'bg-amber-50 text-amber-700 ring-amber-200',       bar: 'bg-amber-500' },
    red:     { chip: 'bg-red-50 text-red-700 ring-red-200',             bar: 'bg-red-500' },
    slate:   { chip: 'bg-slate-100 text-slate-500 ring-slate-200',      bar: 'bg-slate-300' },
};

const licenseState = (lic, loading = false) => {
    if (!lic) return { tone: 'slate', label: loading ? 'Yoxlanılır' : 'Məlumat yoxdur', end: null, pct: null };
    const d = lic.days_left;
    if (lic.ok) {
        const warn = d != null && d <= 7;
        return {
            tone: warn ? 'amber' : 'emerald',
            label: warn ? (d <= 0 ? 'Bu gün bitir' : `${d} gün qalıb`) : (d != null ? `Aktiv · ${d} gün` : 'Aktiv'),
            end: lic.end_date,
            pct: d == null ? null : Math.max(4, Math.min(100, Math.round((d / 30) * 100))),
        };
    }
    const unknown = ['timeout', 'fetch_error', 'license_fetch_failed'].includes(lic.reason);
    const label = lic.reason === 'license_expired' ? 'Vaxtı bitib'
        : lic.reason === 'missing_dukan' ? 'Dükan kodu yoxdur'
        : lic.reason === 'license_not_found' ? 'Tapılmadı'
        : unknown ? 'Yoxlanmadı'
        : 'Deaktiv';
    return { tone: unknown ? 'slate' : 'red', label, end: lic.end_date, pct: unknown ? null : 100 };
};

// Diqqət tələb edir: aktiv restoranın lisenziya problemi və ya təsdiq gözləyən giriş şəkli
const needsAttention = (tenant, lic) => {
    const s = licenseState(lic);
    return (Boolean(tenant.active) && (s.tone === 'red' || s.tone === 'amber')) || Boolean(tenant.login_image_pending_url);
};

const fmtYmd = (ymd) => (ymd ? String(ymd).slice(0, 10).split('-').reverse().join('.') : '');

// ── Kartdakı «⋯» menyusu (qruplaşdırılmış əməliyyatlar) ──
const TenantMenu = ({ groups }) => {
    const [open, setOpen] = useState(false);
    const ref = useRef(null);
    useEffect(() => {
        if (!open) return undefined;
        const onDown = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
        const onKey = (e) => { if (e.key === 'Escape') setOpen(false); };
        document.addEventListener('mousedown', onDown);
        document.addEventListener('keydown', onKey);
        return () => { document.removeEventListener('mousedown', onDown); document.removeEventListener('keydown', onKey); };
    }, [open]);
    return (
        <div className="relative" ref={ref}>
            <button
                type="button"
                onClick={() => setOpen((o) => !o)}
                aria-label="Digər əməliyyatlar"
                title="Digər əməliyyatlar"
                className={`flex h-9 w-9 items-center justify-center rounded-xl border transition-colors ${open ? 'border-slate-300 bg-slate-100 text-slate-800' : 'border-slate-200 bg-white text-slate-500 hover:bg-slate-50 hover:text-slate-800'}`}
            >
                <Ellipsis className="h-4 w-4" />
            </button>
            {open && (
                <div className="absolute right-0 top-full z-30 mt-2 w-64 rounded-2xl border border-slate-200 bg-white p-1.5 shadow-xl shadow-slate-900/10">
                    {groups.map((g, gi) => (
                        <div key={g.title} className={gi ? 'mt-1 border-t border-slate-100 pt-1' : ''}>
                            <div className="px-2.5 pb-1 pt-1.5 text-[10px] font-semibold uppercase tracking-wider text-slate-400">{g.title}</div>
                            {g.items.map((it) => (
                                <button
                                    key={it.label}
                                    type="button"
                                    disabled={it.disabled}
                                    onClick={() => { setOpen(false); it.onClick(); }}
                                    className={`flex w-full items-center gap-2.5 rounded-lg px-2.5 py-2 text-left text-[13px] font-medium transition-colors disabled:cursor-not-allowed disabled:opacity-50 ${
                                        it.danger ? 'text-red-600 hover:bg-red-50' : 'text-slate-700 hover:bg-slate-50'
                                    }`}
                                >
                                    <it.Icon className={`h-4 w-4 flex-shrink-0 ${it.danger ? 'text-red-500' : 'text-slate-400'}`} />
                                    <span className="truncate">{it.label}</span>
                                </button>
                            ))}
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
};

const SecTitle = ({ children, hint }) => (
    <div className="mb-3 flex items-baseline justify-between gap-3">
        <div className="text-[11px] font-semibold uppercase tracking-[0.12em] text-slate-400">{children}</div>
        {hint && <p className="text-xs text-slate-400">{hint}</p>}
    </div>
);

// Redaktə pəncərəsinin bölmələri
const EDIT_TABS = [
    { key: 'info',         label: 'Restoran',      hint: 'Ad, login, plan, status',        Icon: Store },
    { key: 'business',     label: 'Biznes',        hint: 'Kassa, iş günü, funksiyalar',    Icon: Briefcase },
    { key: 'menu',         label: 'QR menyu',      hint: 'Public linklər, rənglər',        Icon: QrCode },
    { key: 'cover',        label: 'Giriş ekranı',  hint: 'QR cover, fon, reklam',          Icon: Smartphone },
    { key: 'print',        label: 'Çap və mətbəx', hint: 'Çek, LAN çap, mətbəx ekranı',    Icon: Printer },
    { key: 'staff',        label: 'İşçilər',       hint: 'Kassir və cərimə qaydaları',     Icon: Users },
    { key: 'waiter_perms', label: 'İcazələr',      hint: 'Silmə, giriş, fiskal idarə',     Icon: ShieldCheck },
    { key: 'notify',       label: 'Bildirişlər',   hint: 'WhatsApp, OTP, iOS push',        Icon: Bell },
    { key: 'more',         label: 'İnteqrasiya',   hint: 'Payriff, Qızıl Farel',           Icon: Plug },
];

const STATUS_FILTERS = [
    { key: 'all',       label: 'Hamısı' },
    { key: 'active',    label: 'Aktiv' },
    { key: 'inactive',  label: 'Passiv' },
    { key: 'attention', label: 'Diqqət' },
];

const Tenants = () => {
    const [tenants, setTenants] = useState([]);
    const [loading, setLoading] = useState(true);
    const [search, setSearch] = useState('');
    const [licenses, setLicenses] = useState({});      // { tenantId: { ok, days_left, end_date, reason } }
    const [licensesLoading, setLicensesLoading] = useState(true);
    const location = useLocation();
    const [statusFilter, setStatusFilter] = useState(() => {
        const f = new URLSearchParams(location.search).get('filter');
        return STATUS_FILTERS.some((x) => x.key === f) ? f : 'all';
    });
    const [brandOpen, setBrandOpen] = useState({});

    const [selectedTenant, setSelectedTenant] = useState(null);

    const [createModal, setCreateModal] = useState(false);
    const [editModal, setEditModal] = useState(false);
    const [passModal, setPassModal] = useState(false);
    const [fiscalModal, setFiscalModal] = useState(false);
    const [diagModal, setDiagModal] = useState(false);
    const [diagData, setDiagData] = useState(null);
    const [diagLoading, setDiagLoading] = useState(false);

    const [createForm, setCreateForm] = useState(defaultCreateForm);
    const [licNextBusy, setLicNextBusy] = useState(false);   // növbəti lisenziya nömrəsi gətirilir
    const [licConflict, setLicConflict] = useState(null);    // mövcud lisenziyalar: [{ dukan, ad, status, bitme, usedBy }]
    const [creating, setCreating] = useState(false);
    const [editForm, setEditForm] = useState(defaultEditForm);
    const [waTestNum, setWaTestNum] = useState('');
    const [waTesting, setWaTesting] = useState(false);
    const [newPass, setNewPass] = useState('');
    const [kassaOpening, setKassaOpening] = useState('');
    const [kassaOpeningBusy, setKassaOpeningBusy] = useState(false);

    const [uploadingLogoId,        setUploadingLogoId]        = useState(null);
    const [uploadingCoverLogo,     setUploadingCoverLogo]     = useState(false);
    const [uploadingAdImage,       setUploadingAdImage]       = useState(false);
    const [uploadingCoverBg,       setUploadingCoverBg]       = useState(false);
    const [cropReq,                setCropReq]                = useState(null); // { file, kind: 'bg' | 'ad' } — kəsmə pəncərəsi
    const [editTab,                setEditTab]                = useState('info');
    const [kitchenLinks,           setKitchenLinks]           = useState(null); // { tenant, token, printers, enabled }
    const [kitchenLinksLoading,    setKitchenLinksLoading]    = useState(false);
    const [importingMenuId,        setImportingMenuId]        = useState(null);
    const [importingIngredientId,  setImportingIngredientId]  = useState(null);
    const [savingFiscal, setSavingFiscal] = useState(false);
    const [testingFiscal, setTestingFiscal] = useState(false);
    const [fiscalForm, setFiscalForm] = useState(defaultFiscalForm);
    const [fiscalTestResult, setFiscalTestResult] = useState(null);
    const [fiscalNotes, setFiscalNotes] = useState([]);
    const [fiscalTaxProfiles, setFiscalTaxProfiles] = useState([]);
    const [casposNotes, setCasposNotes] = useState([]);
    const [casposVatTypes, setCasposVatTypes] = useState([]);

    useEffect(() => {
        loadTenants();
        loadLicenses();
    }, []);

    const loadLicenses = async () => {
        setLicensesLoading(true);
        try {
            const res = await api.get('/superadmin/licenses');
            setLicenses(res.data?.data || {});
        } catch {
            setLicenses({});
        } finally {
            setLicensesLoading(false);
        }
    };

    const loadTenants = async () => {
        try {
            const res = await api.get('/superadmin/tenants');
            const nextTenants = res.data?.data || [];
            setTenants(nextTenants);

            if (selectedTenant?.id) {
                const nextSelected = nextTenants.find((tenant) => tenant.id === selectedTenant.id);
                if (nextSelected) {
                    setSelectedTenant(nextSelected);
                }
            }
        } catch (err) {
            toast.error('Restoranlar yüklənmədi.');
        } finally {
            setLoading(false);
        }
    };

    const filteredTenants = useMemo(() => {
        const query = search.trim().toLowerCase();
        if (!query) return tenants;

        return tenants.filter((tenant) =>
            String(tenant.name || '').toLowerCase().includes(query)
            || String(tenant.username || '').toLowerCase().includes(query)
            || String(tenant.username_new_system || '').toLowerCase().includes(query)
            || String(tenant.license_dukan_code || '').toLowerCase().includes(query)
        );
    }, [search, tenants]);

    const statusCounts = useMemo(() => ({
        all: filteredTenants.length,
        active: filteredTenants.filter((t) => t.active).length,
        inactive: filteredTenants.filter((t) => !t.active).length,
        attention: filteredTenants.filter((t) => needsAttention(t, licenses[t.id])).length,
    }), [filteredTenants, licenses]);

    const visibleTenants = useMemo(() => {
        if (statusFilter === 'active') return filteredTenants.filter((t) => t.active);
        if (statusFilter === 'inactive') return filteredTenants.filter((t) => !t.active);
        if (statusFilter === 'attention') return filteredTenants.filter((t) => needsAttention(t, licenses[t.id]));
        return filteredTenants;
    }, [statusFilter, filteredTenants, licenses]);

    const getPlanColor = (plan) => PLANS.find((p) => p.value === plan)?.color || 'bg-gray-100 text-gray-600';
    const getPlanLabel = (plan) => PLANS.find((p) => p.value === plan)?.label || 'Pulsuz';

    const getWaiterUrl = (token) => `${window.location.origin}/?t=${token}`;
    const getQrMenuUrl = (token) => `${window.location.origin}/qrmenu/?t=${token}`;

    const copyPublicLink = async (url, successMessage, enabled = true) => {
        if (!enabled) {
            toast.error('Bu public link üçün icazə verilməyib.');
            return;
        }

        await navigator.clipboard.writeText(url);
        toast.success(successMessage);
    };

    const openEdit = (tenant) => {
        setSelectedTenant(tenant);
        setEditForm({
            name: tenant.name || '',
            username_new_system: tenant.username_new_system || '',
            license_dukan_code: tenant.license_dukan_code || '',
            phone: tenant.phone || '',
            service_percent: String(tenant.service_percent ?? 0),
            plan: tenant.plan || 'pro',
            active: tenant.active ?? 1,
            kitchen_display_enabled: Boolean(tenant.kitchen_display_enabled ?? 1),
            qr_menu_enabled: Boolean(tenant.qr_menu_enabled ?? 1),
            qr_menu_ordering_enabled: Boolean(tenant.qr_menu_ordering_enabled ?? 1),
            qr_menu_design: tenant.qr_menu_design || 'v1',
            qr_theme: (() => { try { return tenant.qr_theme ? (typeof tenant.qr_theme === 'string' ? JSON.parse(tenant.qr_theme) : tenant.qr_theme) : {}; } catch { return {}; } })(),
            qr_cover_booking_enabled: Boolean(tenant.qr_cover_booking_enabled ?? 0),
            qr_cover_booking_label: tenant.qr_cover_booking_label || '',
            qr_cover_banquet_enabled: Boolean(tenant.qr_cover_banquet_enabled ?? 0),
            qr_cover_banquet_label: tenant.qr_cover_banquet_label || '',
            qr_cover_address: tenant.qr_cover_address || '',
            qr_cover_hours: tenant.qr_cover_hours || '',
            qr_cover_whatsapp: tenant.qr_cover_whatsapp || '',
            qr_cover_hours_obj: parseHoursObj(tenant.qr_cover_hours_json),
            qr_menu_sort: tenant.qr_menu_sort || 'default',
            qr_cover_logo_url: tenant.qr_cover_logo_url || '',
            qr_cover_facebook: tenant.qr_cover_facebook || '',
            qr_cover_instagram: tenant.qr_cover_instagram || '',
            google_place_id: tenant.google_place_id || '',
            twogis_url: tenant.twogis_url || '',
            google_review_enabled: Boolean(tenant.google_review_enabled ?? 0),
            gift_ticket_enabled: Boolean(tenant.gift_ticket_enabled ?? 0),
            gift_ticket_threshold: tenant.gift_ticket_threshold ?? 100,
            business_day_start_hour: tenant.business_day_start_hour ?? 0,
            manual_cost_enabled: Boolean(tenant.manual_cost_enabled ?? 0),
            rental_auto_ofis: Boolean(tenant.rental_auto_ofis ?? 0),
            kassir_manage_waiters: Boolean(tenant.kassir_manage_waiters ?? 0),
            default_waiter_payment_type: tenant.default_waiter_payment_type || 'service_pct',
            default_waiter_salary: tenant.default_waiter_salary ?? 0,
            default_waiter_service_pct: tenant.default_waiter_service_pct ?? 0,
            fine_to_ofis: Boolean(tenant.fine_to_ofis ?? 0),
            waiter_add_custom_item: Boolean(tenant.waiter_add_custom_item ?? 0),
            waiter_no_delete: Boolean(tenant.waiter_no_delete ?? 0),
            kassir_pin_no_delete: Boolean(tenant.kassir_pin_no_delete ?? 0),
            admin_no_delete: Boolean(tenant.admin_no_delete ?? 0),
            kassir_stock_view: Boolean(tenant.kassir_stock_view ?? 0),
            administrator_can_delete: Boolean(tenant.administrator_can_delete ?? 1),
            kassir_can_delete: Boolean(tenant.kassir_can_delete ?? 1),
            administrator_can_add: Boolean(Number(tenant.administrator_can_add ?? 1)),
            kassir_can_add: Boolean(Number(tenant.kassir_can_add ?? 1)),
            pin_only_login: Boolean(tenant.pin_only_login ?? 0),
            qr_ad_enabled: Boolean(tenant.qr_ad_enabled ?? 0),
            qr_ad_image_url: tenant.qr_ad_image_url || '',
            qr_cover_bg_url: tenant.qr_cover_bg_url || '',
            qizilfarel_enabled: Boolean(tenant.qizilfarel_enabled ?? 0),
            qizilfarel_api_key: tenant.qizilfarel_api_key || '',
            payriff_enabled: Boolean(tenant.payriff_enabled ?? 0),
            payriff_secret: tenant.payriff_secret || '',
            booking_deposit_percent: tenant.booking_deposit_percent ?? 30,
            web_print_enabled: Boolean(tenant.web_print_enabled ?? 0),
            web_native_print_enabled: Boolean(tenant.web_native_print_enabled ?? 0),
            menu_unlimited: Boolean(tenant.menu_unlimited ?? 0),
            receipt_big_font: Boolean(tenant.receipt_big_font ?? 0),
            kitchen_big_font: Boolean(tenant.kitchen_big_font ?? 0),
            kitchen_qty_x: Boolean(tenant.kitchen_qty_x ?? 0),
            hourly_menu_enabled: Boolean(tenant.hourly_menu_enabled ?? 0),
            fiscal_waiter_admin: Boolean(tenant.fiscal_waiter_admin ?? 0),
            fiscal_waiter_ofisiant: Boolean(tenant.fiscal_waiter_ofisiant ?? 0),
            kitchen_confirm_on_exit: Boolean(tenant.kitchen_confirm_on_exit ?? 0),
            fast_food_enabled: Boolean(tenant.fast_food_enabled ?? 0),
            offline_mode_enabled: Boolean(tenant.offline_mode_enabled ?? 0),
            food_delivery_enabled: Boolean(tenant.food_delivery_enabled ?? 0),
            food_delivery_roles: Array.isArray(tenant.food_delivery_roles) ? tenant.food_delivery_roles : ['admin', 'kassir', 'administrator'],
            food_delivery_rooms: Boolean(tenant.food_delivery_rooms ?? 0),
            floor_plan_enabled: Boolean(tenant.floor_plan_enabled ?? 0),
            kitchen_display_mode: tenant.kitchen_display_mode === 'v2' ? 'v2' : 'v1',
            pc_print_ip: tenant.pc_print_ip || '',
            apns_enabled: Boolean(tenant.apns_enabled ?? 0),
            apns_config: {
                key_id:        tenant.apns_config?.key_id || '',
                team_id:       tenant.apns_config?.team_id || '',
                topic:         tenant.apns_config?.topic || '',
                p8_path:       tenant.apns_config?.p8_path || '',
                use_sandbox:   Boolean(tenant.apns_config?.use_sandbox ?? 0),
                token_api_url: tenant.apns_config?.token_api_url || '',
                token_api_key: tenant.apns_config?.token_api_key || '',
                async_secret:  tenant.apns_config?.async_secret || '',
                recipient_ids: tenant.apns_config?.recipient_ids || ''
            },
            whatsapp_enabled: Boolean(tenant.whatsapp_enabled ?? 0),
            whatsapp_api_key: tenant.whatsapp_api_key || '',
            whatsapp_recipients: tenant.whatsapp_recipients || '',
            otp_login_enabled: Boolean(tenant.otp_login_enabled ?? 0),
            otp_numbers: tenant.otp_numbers || '',
            otp_login_blocked: Number(tenant.otp_login_blocked ?? 0),
            notify_channel: tenant.notify_channel || 'apns',
            daily_summary_hour: Number.isFinite(Number(tenant.daily_summary_hour)) ? Number(tenant.daily_summary_hour) : 23,
            daily_summary_minute: Number.isFinite(Number(tenant.daily_summary_minute)) ? Number(tenant.daily_summary_minute) : 0,
            kassa_report_enabled: Boolean(tenant.kassa_report_enabled ?? 0),
            kassa_report_recipients: tenant.kassa_report_recipients || '',
            kassa_report_hour: Number.isFinite(Number(tenant.kassa_report_hour)) ? Number(tenant.kassa_report_hour) : 23,
            kassa_report_minute: Number.isFinite(Number(tenant.kassa_report_minute)) ? Number(tenant.kassa_report_minute) : 0
        });
        setKassaOpening('');
        setEditTab('info');
        setEditModal(true);
    };

    const openPasswordReset = (tenant) => {
        setSelectedTenant(tenant);
        setNewPass('');
        setPassModal(true);
    };

    const resetCreateForm = () => setCreateForm(defaultCreateForm);

    // Növbəti boş lisenziya nömrəsi (api.fiscalbusiness.net) → «Lisenziya dukan kodu» sahəsinə
    const fetchNextLicense = async () => {
        setLicNextBusy(true);
        try {
            const res = await api.get('/superadmin/license/next');
            if (res.data?.ok) setCreateForm((f) => ({ ...f, license_dukan_code: String(res.data.dukan) }));
            else toast.error(res.data?.msg || 'Növbəti lisenziya nömrəsi alınmadı.');
        } catch (err) {
            toast.error(err.response?.data?.msg || 'Növbəti lisenziya nömrəsi alınmadı.');
        } finally {
            setLicNextBusy(false);
        }
    };

    // «Yeni restoran» açılanda nömrə boşdursa avtomatik doldur
    useEffect(() => {
        if (createModal && !String(createForm.license_dukan_code || '').trim()) fetchNextLicense();
        if (!createModal) setLicConflict(null);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [createModal]);

    // mode: undefined | 'link' (mövcuda bağla) | 'new' (yeni ID ver)
    const createTenant = async (mode) => {
        if (creating) return;
        setCreating(true);
        try {
            const res = await api.post('/superadmin/tenants', { ...createForm, ...(mode ? { lisenziya_mode: mode } : {}) });
            if (res.data?.code === 'license_exists') {
                setLicConflict(res.data.existing || []);
                return;
            }
            if (!res.data?.ok) {
                toast.error(res.data?.msg || 'Xəta baş verdi.');
                return;
            }
            setLicConflict(null);
            toast.success(res.data.msg || 'Restoran yaradıldı.', { duration: 9000 });
            setCreateModal(false);
            resetCreateForm();
            await loadTenants();
        } catch (err) {
            toast.error(err.response?.data?.msg || 'Xəta baş verdi.');
        } finally {
            setCreating(false);
        }
    };

    const handleCreate = (e) => {
        e.preventDefault();
        createTenant();
    };

    // İş saatları redaktoru üçün köməkçilər
    const updateHours = (patch) => setEditForm((f) => ({ ...f, qr_cover_hours_obj: { ...(f.qr_cover_hours_obj || defaultHoursObj()), ...patch } }));
    const updateHoursDay = (k, patch) => setEditForm((f) => {
        const cur = f.qr_cover_hours_obj || defaultHoursObj();
        return { ...f, qr_cover_hours_obj: { ...cur, days: { ...cur.days, [k]: { ...cur.days[k], ...patch } } } };
    });

    // Saxlamaq üçün payload: strukturlu saatları JSON string-ə çevir
    const editPayload = () => ({
        ...editForm,
        qr_cover_hours_json: editForm.qr_cover_hours_obj ? JSON.stringify(editForm.qr_cover_hours_obj) : '',
    });

    const handleEdit = async (e) => {
        e.preventDefault();
        if (!selectedTenant) return;

        try {
            const putRes = await api.put(`/superadmin/tenants/${selectedTenant.id}`, editPayload());
            if (putRes.data?.warning) toast.error(putRes.data.warning, { duration: 9000 }); // lisenziya yenilənmədi
            // QR rəng teması (ayrıca endpoint)
            try { await api.put(`/superadmin/tenants/${selectedTenant.id}/qr-theme`, { qr_theme: editForm.qr_theme || {} }); } catch { /* ignore */ }
            toast.success('Restoran yeniləndi.');
            setEditModal(false);
            await loadTenants();
        } catch (err) {
            toast.error(err.response?.data?.msg || 'Xəta baş verdi.');
        }
    };

    const saveKassaOpening = async () => {
        if (!selectedTenant) return;
        const val = Number(String(kassaOpening).replace(',', '.'));
        if (!Number.isFinite(val)) { toast.error('Düzgün məbləğ yazın.'); return; }
        setKassaOpeningBusy(true);
        try {
            const res = await api.post(`/superadmin/tenants/${selectedTenant.id}/kassa-opening`, { opening_balance: val });
            toast.success(res.data?.msg || 'Kassa başlanğıc qalığı təyin edildi.');
        } catch (err) {
            toast.error(err.response?.data?.msg || 'Alınmadı.');
        } finally {
            setKassaOpeningBusy(false);
        }
    };

    const sendWhatsappTest = async () => {
        if (!selectedTenant) return;
        if (!waTestNum.trim()) { toast.error('Test nömrəsi yazın.'); return; }
        setWaTesting(true);
        try {
            // Əvvəlcə açar/nömrələr saxlanmalıdır ki, test düzgün olsun
            await api.put(`/superadmin/tenants/${selectedTenant.id}`, editPayload());
            const res = await api.post(`/superadmin/tenants/${selectedTenant.id}/whatsapp-test`, { to: waTestNum.trim() });
            toast.success(res.data.msg || 'Test göndərildi.');
        } catch (err) {
            toast.error(err.response?.data?.msg || 'Test göndərilmədi.');
        } finally {
            setWaTesting(false);
        }
    };

    const handleCoverLogoUpload = async (e) => {
        const file = e.target.files?.[0];
        e.target.value = '';
        if (!file || !selectedTenant) return;
        if (file.size > 5 * 1024 * 1024) {
            toast.error('Şəkil ölçüsü maksimum 5 MB ola bilər.');
            return;
        }
        const fd = new FormData();
        fd.append('image', file, file.name);
        setUploadingCoverLogo(true);
        try {
            const res = await api.post(`/superadmin/tenants/${selectedTenant.id}/qr-cover-logo`, fd);
            setEditForm((p) => ({ ...p, qr_cover_logo_url: res.data?.logo_url || '' }));
            toast.success('Logo yükləndi.');
        } catch (err) {
            toast.error(err.response?.data?.msg || 'Yüklənmədi.');
        } finally {
            setUploadingCoverLogo(false);
        }
    };

    const handleCoverLogoRemove = async () => {
        if (!selectedTenant) return;
        setUploadingCoverLogo(true);
        try {
            await api.delete(`/superadmin/tenants/${selectedTenant.id}/qr-cover-logo`);
            setEditForm((p) => ({ ...p, qr_cover_logo_url: '' }));
            toast.success('Logo silindi.');
        } catch (err) {
            toast.error(err.response?.data?.msg || 'Silinmədi.');
        } finally {
            setUploadingCoverLogo(false);
        }
    };

    const handleAdImageUpload = (e) => {
        const raw = e.target.files?.[0];
        e.target.value = '';
        if (!raw || !selectedTenant) return;
        setCropReq({ file: raw, kind: 'ad' });
    };

    const uploadAdImage = async (raw) => {
        if (!raw || !selectedTenant) return;
        setUploadingAdImage(true);
        try {
            // Böyük şəkil → avtomatik sıxılır (limit xətası yoxdur)
            const file = await compressImage(raw);
            const fd = new FormData();
            fd.append('image', file, file.name);
            const res = await api.post(`/superadmin/tenants/${selectedTenant.id}/qr-ad-image`, fd);
            setEditForm((p) => ({ ...p, qr_ad_image_url: res.data?.image_url || '' }));
            toast.success('Reklam şəkli yükləndi.');
        } catch (err) {
            toast.error(err.response?.data?.msg || err.message || 'Yüklənmədi.');
        } finally {
            setUploadingAdImage(false);
        }
    };

    // QR giriş (cover) fon şəkli — dərhal yüklənir/silinir (Yadda saxla lazım deyil)
    const handleCoverBgUpload = (e) => {
        const raw = e.target.files?.[0];
        e.target.value = '';
        if (!raw || !selectedTenant) return;
        setCropReq({ file: raw, kind: 'bg' });
    };

    const uploadCoverBg = async (raw) => {
        if (!raw || !selectedTenant) return;
        setUploadingCoverBg(true);
        try {
            // Böyük şəkil → avtomatik sıxılır (limit xətası yoxdur)
            const file = await compressImage(raw);
            const fd = new FormData();
            fd.append('image', file, file.name);
            const res = await api.post(`/superadmin/tenants/${selectedTenant.id}/qr-cover-bg`, fd);
            setEditForm((p) => ({ ...p, qr_cover_bg_url: res.data?.image_url || '' }));
            toast.success('Fon şəkli yükləndi.');
        } catch (err) {
            toast.error(err.response?.data?.msg || err.message || 'Yüklənmədi.');
        } finally {
            setUploadingCoverBg(false);
        }
    };

    const handleCoverBgRemove = async () => {
        if (!selectedTenant) return;
        setUploadingCoverBg(true);
        try {
            await api.delete(`/superadmin/tenants/${selectedTenant.id}/qr-cover-bg`);
            setEditForm((p) => ({ ...p, qr_cover_bg_url: '' }));
            toast.success('Fon şəkli silindi. Standart fon qayıtdı.');
        } catch (err) {
            toast.error(err.response?.data?.msg || 'Silinmədi.');
        } finally {
            setUploadingCoverBg(false);
        }
    };

    const handleAdImageRemove = async () => {
        if (!selectedTenant) return;
        setUploadingAdImage(true);
        try {
            await api.delete(`/superadmin/tenants/${selectedTenant.id}/qr-ad-image`);
            setEditForm((p) => ({ ...p, qr_ad_image_url: '' }));
            toast.success('Reklam şəkli silindi.');
        } catch (err) {
            toast.error(err.response?.data?.msg || 'Silinmədi.');
        } finally {
            setUploadingAdImage(false);
        }
    };

    const handleResetPass = async (e) => {
        e.preventDefault();
        if (!selectedTenant) return;

        try {
            const rp = await api.post(`/superadmin/tenants/${selectedTenant.id}/reset-password`, { password: newPass });
            toast.success('Şifrə sıfırlandı.');
            if (rp.data?.warning) toast.error(rp.data.warning, { duration: 9000 }); // lisenziya yenilənmədi
            setPassModal(false);
        } catch (err) {
            toast.error(err.response?.data?.msg || 'Xəta baş verdi.');
        }
    };

    const handleResetToken = async (tenant) => {
        if (!(await confirm(`"${tenant.name}" üçün yeni ofisiant tokeni yaradılsın?`))) return;

        try {
            const res = await api.post(`/superadmin/tenants/${tenant.id}/reset-token`);
            toast.success(`Yeni token: ${res.data.waiter_token}`, { duration: 8000 });
            await loadTenants();
        } catch (err) {
            toast.error(err.response?.data?.msg || 'Xəta baş verdi.');
        }
    };

    const handleForceLogout = async (tenant) => {
        if (!(await confirm(`"${tenant.name}" bütün cihazlardan (PC, telefon) çıxarılsın?\n\nAçıq sessiyalar ~1 dəqiqə ərzində login səhifəsinə atılacaq. Yenidən daxil olmaq üçün şifrə/PIN lazım olacaq.`))) return;

        try {
            await api.post(`/superadmin/tenants/${tenant.id}/force-logout`);
            toast.success('Bütün cihazlardan çıxış edildi.');
        } catch (err) {
            toast.error(err.response?.data?.msg || 'Xəta baş verdi.');
        }
    };

    const handleDeactivate = async (tenant) => {
        if (!(await confirm(`"${tenant.name}" deaktiv edilsin?`))) return;

        try {
            await api.delete(`/superadmin/tenants/${tenant.id}`);
            toast.success('Restoran deaktiv edildi.');
            await loadTenants();
        } catch (err) {
            toast.error(err.response?.data?.msg || 'Xəta baş verdi.');
        }
    };

    const handleHardDelete = async (tenant) => {
        if (!(await confirm(`"${tenant.name}" tam silinsin?\n\nBu əməliyyat bütün məlumatları birdəfəlik siləcək.`))) return;

        try {
            await api.delete(`/superadmin/tenants/${tenant.id}/hard`);
            toast.success('Restoran tam silindi.');
            await loadTenants();
        } catch (err) {
            toast.error(err.response?.data?.msg || 'Xəta baş verdi.');
        }
    };

    const handleApproveLoginImage = async (tenant) => {
        try {
            await api.post(`/superadmin/tenants/${tenant.id}/login-screen/approve`);
            toast.success('Ekran şəkli təsdiqləndi.');
            await loadTenants();
        } catch (err) {
            toast.error(err.response?.data?.msg || 'Xəta baş verdi.');
        }
    };

    const handleRejectLoginImage = async (tenant) => {
        try {
            await api.delete(`/superadmin/tenants/${tenant.id}/login-screen/pending`);
            toast.success('Gözləyən ekran şəkli rədd edildi.');
            await loadTenants();
        } catch (err) {
            toast.error(err.response?.data?.msg || 'Xəta baş verdi.');
        }
    };

    const handleDeleteActiveLoginImage = async (tenant) => {
        try {
            await api.delete(`/superadmin/tenants/${tenant.id}/login-screen/active`);
            toast.success('Aktiv ekran şəkli silindi.');
            await loadTenants();
        } catch (err) {
            toast.error(err.response?.data?.msg || 'Xəta baş verdi.');
        }
    };

    const handleReceiptLogoUpload = async (tenant, file) => {
        if (!file) return;

        const fileName = String(file.name || '').toLowerCase();
        const mimeType = String(file.type || '').toLowerCase();
        const isPng = fileName.endsWith('.png') || mimeType === 'image/png' || mimeType === 'image/x-png';

        if (!isPng) {
            toast.error('Yalnız PNG logo yüklənə bilər.');
            return;
        }

        const formData = new FormData();
        formData.append('logo', file, file.name || 'receipt-logo.png');
        setUploadingLogoId(tenant.id);

        try {
            await api.post(`/superadmin/tenants/${tenant.id}/receipt-logo`, formData);
            toast.success('Çek logosu yükləndi.');
            await loadTenants();
        } catch (err) {
            toast.error(err.response?.data?.msg || 'Xəta baş verdi.');
        } finally {
            setUploadingLogoId(null);
        }
    };

    const handleDeleteReceiptLogo = async (tenant) => {
        try {
            await api.delete(`/superadmin/tenants/${tenant.id}/receipt-logo`);
            toast.success('Çek logosu silindi.');
            await loadTenants();
        } catch (err) {
            toast.error(err.response?.data?.msg || 'Xəta baş verdi.');
        }
    };

    const handleMenuTemplateDownload = async (tenant) => {
        try {
            const res = await api.get(`/superadmin/tenants/${tenant.id}/menu-template`, {
                responseType: 'blob'
            });
            const url = URL.createObjectURL(new Blob([res.data]));
            const a = document.createElement('a');
            a.href = url;
            a.download = `menyu_sablonu_${tenant.name.replace(/[^a-z0-9]/gi, '_')}.xlsx`;
            document.body.appendChild(a);
            a.click();
            a.remove();
            URL.revokeObjectURL(url);
        } catch (err) {
            toast.error('Şablon yüklənmədi.');
        }
    };

    const handleMenuImport = async (tenant, file) => {
        if (!file) return;
        const ext = file.name.split('.').pop().toLowerCase();
        if (!['xlsx', 'xls'].includes(ext)) {
            toast.error('Yalnız Excel (.xlsx / .xls) fayl seçin.');
            return;
        }
        const formData = new FormData();
        formData.append('file', file, file.name);
        setImportingMenuId(tenant.id);
        try {
            const res = await api.post(`/superadmin/tenants/${tenant.id}/menu-import`, formData);
            toast.success(res.data?.msg || 'İdxal tamamlandı.');
            if (res.data?.errors?.length > 0) {
                res.data.errors.forEach((e) => toast.error(e, { duration: 5000 }));
            }
        } catch (err) {
            toast.error(err.response?.data?.msg || 'İdxal xətası.');
        } finally {
            setImportingMenuId(null);
        }
    };

    const handleMenuClear = async (tenant) => {
        if (!(await confirm(`"${tenant.name}" restoranının BÜTÜN menyusu silinsin?\n\nKateqoriyalar, məhsullar və texkartalar birdəfəlik silinəcək.`))) return;
        try {
            const res = await api.delete(`/superadmin/tenants/${tenant.id}/menu-clear`);
            toast.success(res.data?.msg || 'Menyu silindi.');
        } catch (err) {
            toast.error(err.response?.data?.msg || 'Xəta baş verdi.');
        }
    };

    const handleIngredientTemplateDownload = async (tenant) => {
        try {
            const res = await api.get(`/superadmin/tenants/${tenant.id}/ingredients-template`, {
                responseType: 'blob'
            });
            const url = URL.createObjectURL(new Blob([res.data]));
            const a = document.createElement('a');
            a.href = url;
            a.download = `ingredient_sablonu_${tenant.name.replace(/[^a-z0-9]/gi, '_')}.xlsx`;
            document.body.appendChild(a);
            a.click();
            a.remove();
            URL.revokeObjectURL(url);
        } catch {
            toast.error('Şablon yüklənmədi.');
        }
    };

    const handleIngredientClear = async (tenant) => {
        if (!(await confirm(`"${tenant.name}" restoranının BÜTÜN inqredientləri silinsin?\n\nAnbar məlumatları və bağlı reseptlər birdəfəlik silinəcək.`))) return;
        try {
            const res = await api.delete(`/superadmin/tenants/${tenant.id}/ingredients-clear`);
            toast.success(res.data?.msg || 'İnqredientlər silindi.');
        } catch (err) {
            toast.error(err.response?.data?.msg || 'Xəta baş verdi.');
        }
    };

    const handleIngredientImport = async (tenant, file) => {
        if (!file) return;
        const ext = file.name.split('.').pop().toLowerCase();
        if (!['xlsx', 'xls'].includes(ext)) {
            toast.error('Yalnız Excel (.xlsx / .xls) fayl seçin.');
            return;
        }
        const formData = new FormData();
        formData.append('file', file, file.name);
        setImportingIngredientId(tenant.id);
        try {
            const res = await api.post(`/superadmin/tenants/${tenant.id}/ingredients-import`, formData);
            toast.success(res.data?.msg || 'İdxal tamamlandı.');
            if (res.data?.errors?.length > 0) {
                res.data.errors.forEach((e) => toast.error(e, { duration: 5000 }));
            }
        } catch (err) {
            toast.error(err.response?.data?.msg || 'İdxal xətası.');
        } finally {
            setImportingIngredientId(null);
        }
    };

    const openDiagnostics = async (tenant) => {
        setSelectedTenant(tenant);
        setDiagModal(true);
        setDiagLoading(true);
        try {
            const res = await api.get(`/superadmin/tenants/${tenant.id}/diagnostics`);
            setDiagData(res.data.data);
        } catch (err) {
            toast.error(err.response?.data?.msg || 'Yüklənmədi.');
            setDiagModal(false);
        } finally {
            setDiagLoading(false);
        }
    };

    const reloadDiagnostics = async () => {
        if (!selectedTenant) return;
        setDiagLoading(true);
        try {
            const res = await api.get(`/superadmin/tenants/${selectedTenant.id}/diagnostics`);
            setDiagData(res.data.data);
        } catch (err) {
            toast.error(err.response?.data?.msg || 'Yenilənmədi.');
        } finally {
            setDiagLoading(false);
        }
    };

    const deleteOneTransfer = async (transferId) => {
        if (!selectedTenant) return;
        if (!(await confirm(`Bu transfer silinsin (ID: ${transferId})?`))) return;
        try {
            await api.delete(`/superadmin/tenants/${selectedTenant.id}/ofis-transfers/${transferId}`);
            toast.success('Silindi.');
            await reloadDiagnostics();
        } catch (err) {
            toast.error(err.response?.data?.msg || 'Silinmədi.');
        }
    };

    const deleteAllTransfers = async () => {
        if (!selectedTenant) return;
        const cnt = diagData?.ofis_transfers?.count || 0;
        if (!(await confirm(`DİQQƏT! "${selectedTenant.name}" tenantında ${cnt} ofis transferin HAMISI silinəcək. Bu əməliyyat geri qaytarılmazdır. Davam?`))) return;
        if (!(await confirm('Bir daha təsdiqlə — HƏQİQƏTƏN hamısını silmək istəyirsən?'))) return;
        try {
            const res = await api.delete(`/superadmin/tenants/${selectedTenant.id}/ofis-transfers/all`);
            toast.success(res.data?.msg || 'Hamısı silindi.');
            await reloadDiagnostics();
        } catch (err) {
            toast.error(err.response?.data?.msg || 'Silinmədi.');
        }
    };

    // Mətbəx ekranı şifrəsiz linkləri (printerlərə görə)
    const openKitchenLinks = async (tenant) => {
        setKitchenLinks({ tenant, token: '', printers: [], enabled: 1 });
        setKitchenLinksLoading(true);
        try {
            const res = await api.get(`/superadmin/tenants/${tenant.id}/kitchen-links`);
            setKitchenLinks({
                tenant,
                token: res.data?.token || '',
                printers: res.data?.printers || [],
                enabled: Number(res.data?.kitchen_display_enabled ?? 0),
            });
        } catch (err) {
            toast.error(err.response?.data?.msg || 'Linklər yüklənmədi.');
            setKitchenLinks(null);
        } finally {
            setKitchenLinksLoading(false);
        }
    };

    const openFiscalConfig = async (tenant) => {
        setSelectedTenant(tenant);
        setFiscalModal(true);
        setFiscalTestResult(null);
        setFiscalNotes([]);
        setFiscalTaxProfiles([]);
        setCasposNotes([]);
        setCasposVatTypes([]);
        setFiscalForm(defaultFiscalForm);

        try {
            const res = await api.get(`/superadmin/tenants/${tenant.id}/fiscal-config`);
            const data = res.data?.data || {};
            setFiscalForm({
                provider: data.provider || 'caspos',
                smartone: {
                    ...defaultFiscalForm.smartone,
                    ...(data.smartone || {})
                },
                caspos: {
                    ...defaultFiscalForm.caspos,
                    ...(data.caspos || {})
                }
            });
            setFiscalNotes(Array.isArray(data.smartone_notes) ? data.smartone_notes : []);
            setFiscalTaxProfiles(Array.isArray(data.smartone_tax_profiles) ? data.smartone_tax_profiles : []);
            setCasposNotes(Array.isArray(data.caspos_notes) ? data.caspos_notes : []);
            setCasposVatTypes(Array.isArray(data.caspos_vat_types) ? data.caspos_vat_types : []);
        } catch (err) {
            toast.error(err.response?.data?.msg || 'Fiskal konfiqurasiya yüklənmədi.');
        }
    };

    const updateSmartoneField = (field, value) => {
        setFiscalForm((prev) => ({
            ...prev,
            smartone: {
                ...prev.smartone,
                [field]: value
            }
        }));
    };

    const updateCasposField = (field, value) => {
        setFiscalForm((prev) => ({
            ...prev,
            caspos: {
                ...prev.caspos,
                [field]: value
            }
        }));
    };

    const handleSaveFiscalConfig = async (e) => {
        e.preventDefault();
        if (!selectedTenant) return;

        setSavingFiscal(true);
        try {
            await api.put(`/superadmin/tenants/${selectedTenant.id}/fiscal-config`, fiscalForm);
            toast.success('Fiskal kassa konfiqurasiyası yadda saxlanıldı.');
            setFiscalModal(false);
            await loadTenants();
        } catch (err) {
            toast.error(err.response?.data?.msg || 'Fiskal konfiqurasiya saxlanılmadı.');
        } finally {
            setSavingFiscal(false);
        }
    };

    const handleTestFiscalConfig = async () => {
        if (!selectedTenant) return;

        setTestingFiscal(true);
        setFiscalTestResult(null);

        try {
            const res = await api.post(`/superadmin/tenants/${selectedTenant.id}/fiscal-config/test`, fiscalForm);
            setFiscalTestResult(res.data?.data?.result || null);
            toast.success(fiscalForm.provider === 'smartone'
                ? 'SmartOne bağlantı testi cavab verdi.'
                : 'Caspos bağlantı testi cavab verdi.');
        } catch (err) {
            setFiscalTestResult({
                ok: false,
                error: err.response?.data?.msg || err.message || 'Test xətası.'
            });
            toast.error(err.response?.data?.msg || 'Test xətası.');
        } finally {
            setTestingFiscal(false);
        }
    };

    return (
        <SuperadminLayout
            title="Restoranlar"
            subtitle={`Cəmi: ${tenants.length}`}
            actions={
                <button
                    onClick={() => setCreateModal(true)}
                    className="flex items-center gap-1.5 rounded-xl bg-[#0090bb] px-4 py-2 text-sm font-semibold text-white shadow-sm shadow-sky-900/20 transition-colors hover:bg-[#007ea4]"
                >
                    <Plus className="h-4 w-4" />
                    <span className="hidden sm:inline">Yeni restoran</span>
                    <span className="sm:hidden">Yeni</span>
                </button>
            }
        >
            {/* QR fon / reklam şəkli üçün kəsmə pəncərəsi */}
            {cropReq && (() => {
                const isBg = cropReq.kind === 'bg';
                const v2 = editForm.qr_theme?.cover_design !== 'v1'; // standart V2
                const aspects = isBg
                    ? [
                        { label: 'Telefon 9:16 (V1)', value: 9 / 16 },
                        { label: '4:5 (V2)', value: 4 / 5 },
                        { label: 'Kvadrat 1:1', value: 1 },
                        { label: 'Geniş 16:9', value: 16 / 9 },
                    ]
                    : null;
                return (
                    <ImageCropModal
                        file={cropReq.file}
                        aspect={isBg ? (v2 ? 4 / 5 : 9 / 16) : 9 / 16}
                        aspects={aspects}
                        outMax={1920}
                        allowSkip
                        title={isBg ? 'Giriş ekranının fonunu kəs' : 'Reklam şəklini kəs (9:16)'}
                        onCancel={() => setCropReq(null)}
                        onConfirm={async (f) => {
                            const kind = cropReq.kind;
                            setCropReq(null);
                            if (kind === 'bg') await uploadCoverBg(f);
                            else await uploadAdImage(f);
                        }}
                    />
                );
            })()}
            <div>
                {/* ── Axtarış + filtr ── */}
                <div className="mb-6 flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
                    <div className="relative w-full lg:max-w-md">
                        <Search className="pointer-events-none absolute left-3.5 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
                        <input
                            type="text"
                            value={search}
                            onChange={(e) => setSearch(e.target.value)}
                            placeholder="Restoran, istifadəçi adı və ya dükan kodu"
                            className="w-full rounded-xl border border-slate-200 bg-white py-2.5 pl-10 pr-3 text-sm text-slate-800 shadow-sm transition placeholder:text-slate-400 focus:border-sky-400 focus:outline-none focus:ring-4 focus:ring-sky-100"
                        />
                    </div>
                    <div className="flex gap-1 overflow-x-auto rounded-xl border border-slate-200 bg-white p-1 shadow-sm">
                        {STATUS_FILTERS.map((f) => {
                            const active = statusFilter === f.key;
                            return (
                                <button
                                    key={f.key}
                                    type="button"
                                    onClick={() => setStatusFilter(f.key)}
                                    className={`flex flex-shrink-0 items-center gap-1.5 whitespace-nowrap rounded-lg px-3 py-1.5 text-xs font-semibold transition-colors ${
                                        active ? 'bg-slate-900 text-white' : 'text-slate-600 hover:bg-slate-100'
                                    }`}
                                >
                                    {f.key === 'attention' && <TriangleAlert className={`h-3.5 w-3.5 ${active ? 'text-amber-300' : 'text-amber-500'}`} />}
                                    {f.label}
                                    <span className={`rounded-md px-1.5 text-[10px] ${active ? 'bg-white/15' : 'bg-slate-100 text-slate-500'}`}>{statusCounts[f.key]}</span>
                                </button>
                            );
                        })}
                    </div>
                </div>

                {loading ? (
                    <div className="grid grid-cols-1 gap-5 md:grid-cols-2 xl:grid-cols-3">
                        {[0, 1, 2].map((i) => (
                            <div key={i} className="h-80 animate-pulse rounded-2xl border border-slate-200/70 bg-white" />
                        ))}
                    </div>
                ) : visibleTenants.length === 0 ? (
                    <div className="flex flex-col items-center justify-center rounded-2xl border border-dashed border-slate-300 bg-white py-16 text-center">
                        <div className="flex h-12 w-12 items-center justify-center rounded-full bg-slate-100 text-slate-400">
                            <Store className="h-6 w-6" />
                        </div>
                        <div className="mt-3 text-sm font-semibold text-slate-700">Restoran tapılmadı</div>
                        <div className="mt-1 text-xs text-slate-400">Axtarışı və ya filtri dəyişin.</div>
                    </div>
                ) : (
                    <div className="grid grid-cols-1 gap-5 md:grid-cols-2 xl:grid-cols-3">
                        {visibleTenants.map((tenant) => {
                            const lic = licenseState(licenses[tenant.id], licensesLoading);
                            const tone = LIC_TONE[lic.tone];
                            const qrOn = Boolean(tenant.qr_menu_enabled);
                            const qrOrdering = Number(tenant.qr_menu_ordering_enabled ?? 1) === 1;
                            const pendingImg = Boolean(tenant.login_image_pending_url);
                            const brandingOpen = Boolean(brandOpen[tenant.id]);
                            const menuImportId = `menu-import-${tenant.id}`;
                            const ingImportId = `ing-import-${tenant.id}`;
                            const initials = String(tenant.name || '?').trim().split(/\s+/).slice(0, 2).map((w) => w[0]?.toUpperCase() || '').join('') || '?';

                            const linkRow = (label, path, url, { disabled = false, badge = null, copyMsg }) => (
                                <div className="flex items-center gap-2 rounded-lg bg-slate-50 px-2.5 py-1.5">
                                    <span className="w-16 flex-shrink-0 text-[11px] font-medium text-slate-500">{label}</span>
                                    <span className={`min-w-0 flex-1 truncate font-mono text-[11px] ${disabled ? 'text-slate-300 line-through' : 'text-slate-600'}`}>{path}</span>
                                    {badge}
                                    <button
                                        type="button"
                                        onClick={() => copyPublicLink(url, copyMsg, !disabled)}
                                        title={disabled ? 'Bağlıdır' : 'Kopyala'}
                                        className={`flex h-6 w-6 flex-shrink-0 items-center justify-center rounded-md transition-colors ${disabled ? 'text-slate-300' : 'text-slate-400 hover:bg-white hover:text-slate-700'}`}
                                    >
                                        <Copy className="h-3.5 w-3.5" />
                                    </button>
                                    {!disabled && (
                                        <a href={url} target="_blank" rel="noopener noreferrer" title="Aç"
                                            className="flex h-6 w-6 flex-shrink-0 items-center justify-center rounded-md text-slate-400 transition-colors hover:bg-white hover:text-sky-600">
                                            <ExternalLink className="h-3.5 w-3.5" />
                                        </a>
                                    )}
                                </div>
                            );

                            return (
                                <div
                                    key={tenant.id}
                                    className={`flex flex-col rounded-2xl border bg-white p-5 shadow-[0_1px_2px_rgba(15,23,42,0.04)] transition-shadow hover:shadow-lg hover:shadow-slate-900/5 ${
                                        tenant.active ? 'border-slate-200/80' : 'border-slate-200 bg-slate-50/60'
                                    }`}
                                >
                                    {/* Başlıq */}
                                    <div className="flex items-start gap-3">
                                        <div className={`flex h-11 w-11 flex-shrink-0 items-center justify-center overflow-hidden rounded-xl ${tenant.receipt_logo_url ? 'border border-slate-200 bg-white p-1' : 'bg-gradient-to-br from-sky-500 to-indigo-500 text-sm font-bold text-white'} ${tenant.active ? '' : 'opacity-60 grayscale'}`}>
                                            {tenant.receipt_logo_url
                                                ? <img src={toAssetUrl(tenant.receipt_logo_url)} alt="" className="h-full w-full object-contain" />
                                                : initials}
                                        </div>
                                        <div className="min-w-0 flex-1">
                                            <h3 className={`truncate text-[15px] font-semibold ${tenant.active ? 'text-slate-900' : 'text-slate-500'}`}>{tenant.name}</h3>
                                            <p className="truncate font-mono text-xs text-slate-400">@{tenant.username}</p>
                                        </div>
                                        <div className="flex flex-shrink-0 flex-col items-end gap-1.5">
                                            <span className={`rounded-full px-2 py-0.5 text-[11px] font-semibold ${getPlanColor(tenant.plan)}`}>
                                                {getPlanLabel(tenant.plan)}
                                            </span>
                                            <span className={`flex items-center gap-1 text-[11px] font-medium ${tenant.active ? 'text-emerald-600' : 'text-slate-400'}`}>
                                                <span className={`h-1.5 w-1.5 rounded-full ${tenant.active ? 'bg-emerald-500' : 'bg-slate-300'}`} />
                                                {tenant.active ? 'Aktiv' : 'Passiv'}
                                            </span>
                                        </div>
                                    </div>

                                    {/* Lisenziya */}
                                    <div className="mt-4">
                                        <div className="flex items-center justify-between text-xs">
                                            <span className={`inline-flex items-center gap-1.5 rounded-full px-2 py-0.5 font-semibold ring-1 ${tone.chip}`}>
                                                <ShieldCheck className="h-3.5 w-3.5" />
                                                {lic.label}
                                            </span>
                                            {lic.end && <span className="font-mono text-[11px] text-slate-400">{fmtYmd(lic.end)}</span>}
                                        </div>
                                        {lic.pct != null && (
                                            <div className="mt-2 h-1 overflow-hidden rounded-full bg-slate-100">
                                                <div className={`h-full rounded-full ${tone.bar}`} style={{ width: `${lic.pct}%` }} />
                                            </div>
                                        )}
                                    </div>

                                    {/* Statistika */}
                                    <div className="mt-4 grid grid-cols-3 divide-x divide-slate-100 rounded-xl border border-slate-100 py-2.5 text-center">
                                        {[
                                            { Icon: Users, v: tenant.user_count, l: 'istifadəçi' },
                                            { Icon: Armchair, v: tenant.table_count, l: 'masa' },
                                            { Icon: ShoppingBag, v: Number(tenant.total_orders || 0).toLocaleString('az-AZ'), l: 'sifariş' },
                                        ].map(({ Icon, v, l }) => (
                                            <div key={l} className="px-1">
                                                <div className="flex items-center justify-center gap-1 text-sm font-semibold text-slate-800">
                                                    <Icon className="h-3.5 w-3.5 text-slate-400" />{v ?? 0}
                                                </div>
                                                <div className="text-[10px] text-slate-400">{l}</div>
                                            </div>
                                        ))}
                                    </div>

                                    {/* Məlumat */}
                                    <dl className="mt-4 grid grid-cols-2 gap-x-4 gap-y-2.5 text-xs">
                                        <div className="min-w-0">
                                            <dt className="text-slate-400">Admin login</dt>
                                            <dd className="truncate font-medium text-slate-700">{tenant.username_new_system || tenant.username}</dd>
                                        </div>
                                        <div className="min-w-0">
                                            <dt className="text-slate-400">Dükan kodu</dt>
                                            <dd className="truncate font-mono font-medium text-slate-700">{tenant.license_dukan_code || 'qeyd edilməyib'}</dd>
                                        </div>
                                        <div className="min-w-0">
                                            <dt className="text-slate-400">Telefon</dt>
                                            <dd className="truncate font-medium text-slate-700">{tenant.phone || 'qeyd edilməyib'}</dd>
                                        </div>
                                        <div className="min-w-0">
                                            <dt className="text-slate-400">Servis faizi</dt>
                                            <dd className="font-medium text-slate-700">%{Number(tenant.service_percent || 0).toFixed(0)}</dd>
                                        </div>
                                    </dl>

                                    {/* Public linklər */}
                                    {tenant.waiter_token && (
                                        <div className="mt-4 space-y-1.5">
                                            {linkRow('Ofisiant', `/?t=${tenant.waiter_token}`, getWaiterUrl(tenant.waiter_token), { copyMsg: 'Ofisiant linki kopyalandı!' })}
                                            {linkRow('QR menyu', `/qrmenu/?t=${tenant.waiter_token}`, getQrMenuUrl(tenant.waiter_token), {
                                                disabled: !qrOn,
                                                copyMsg: 'QR menu linki kopyalandı!',
                                                badge: qrOn ? (
                                                    <span className={`flex-shrink-0 rounded-md px-1.5 py-0.5 text-[10px] font-semibold ${qrOrdering ? 'bg-emerald-100 text-emerald-700' : 'bg-amber-100 text-amber-700'}`}
                                                        title={qrOrdering ? 'Müştəri POS-a sifariş göndərə bilir' : 'Yalnız səbət (sifariş bağlı)'}>
                                                        {qrOrdering ? 'Sifariş' : 'Səbət'}
                                                    </span>
                                                ) : null,
                                            })}
                                        </div>
                                    )}

                                    {/* Brendinq: çek loqosu + giriş ekranı şəkli (açılıb-yığılır) */}
                                    <button
                                        type="button"
                                        onClick={() => setBrandOpen((p) => ({ ...p, [tenant.id]: !p[tenant.id] }))}
                                        className="mt-4 flex w-full items-center justify-between rounded-lg px-1 py-1 text-xs font-medium text-slate-500 transition-colors hover:text-slate-800"
                                    >
                                        <span className="flex items-center gap-1.5">
                                            <Palette className="h-3.5 w-3.5" />
                                            Brendinq
                                            {pendingImg && (
                                                <span className="ml-1 rounded-full bg-amber-100 px-1.5 py-0.5 text-[10px] font-semibold text-amber-700">şəkil təsdiq gözləyir</span>
                                            )}
                                        </span>
                                        <ChevronDown className={`h-4 w-4 transition-transform ${brandingOpen ? 'rotate-180' : ''}`} />
                                    </button>

                                    {brandingOpen && (
                                        <div className="mt-2 space-y-3 rounded-xl border border-slate-100 bg-slate-50/70 p-3">
                                            {/* Çek loqosu */}
                                            <div>
                                                <div className="mb-2 flex items-center justify-between">
                                                    <span className="text-xs font-medium text-slate-600">Çek loqosu</span>
                                                    <span className="text-[10px] text-slate-400">yalnız PNG</span>
                                                </div>
                                                <div className={`flex aspect-[3/2] w-full items-center justify-center rounded-lg border bg-white p-3 ${tenant.receipt_logo_url ? 'border-slate-200' : 'border-dashed border-slate-300'}`}>
                                                    <img
                                                        src={tenant.receipt_logo_url ? toAssetUrl(tenant.receipt_logo_url) : '/fiscallogo_blue.png'}
                                                        alt={`${tenant.name} çek logosu`}
                                                        className={`h-full w-full object-contain ${tenant.receipt_logo_url ? '' : 'opacity-40'}`}
                                                    />
                                                </div>
                                                <div className="mt-2 flex flex-wrap gap-2">
                                                    <label className="inline-flex cursor-pointer items-center gap-1.5 rounded-lg bg-slate-900 px-3 py-1.5 text-xs font-semibold text-white transition-colors hover:bg-slate-800">
                                                        <Upload className="h-3.5 w-3.5" />
                                                        {uploadingLogoId === tenant.id ? 'Yüklənir...' : 'PNG yüklə'}
                                                        <input
                                                            type="file"
                                                            accept=".png,image/png,image/x-png"
                                                            className="hidden"
                                                            disabled={uploadingLogoId === tenant.id}
                                                            onChange={(e) => {
                                                                const selectedFile = e.target.files?.[0];
                                                                e.target.value = '';
                                                                handleReceiptLogoUpload(tenant, selectedFile);
                                                            }}
                                                        />
                                                    </label>
                                                    {tenant.receipt_logo_url && (
                                                        <button type="button" onClick={() => handleDeleteReceiptLogo(tenant)}
                                                            className="rounded-lg bg-red-50 px-3 py-1.5 text-xs font-semibold text-red-600 transition-colors hover:bg-red-100">
                                                            Sil
                                                        </button>
                                                    )}
                                                </div>
                                            </div>

                                            {/* Giriş ekranı şəkli */}
                                            {(tenant.login_image_url || tenant.login_image_pending_url) && (
                                                <div className="border-t border-slate-200/70 pt-3">
                                                    <div className="mb-2 text-xs font-medium text-slate-600">Giriş ekranı şəkli</div>
                                                    <div className="grid grid-cols-1 gap-3">
                                                        {tenant.login_image_url && (
                                                            <div>
                                                                <div className="mb-1 text-[11px] font-semibold text-emerald-600">Aktiv</div>
                                                                <div className="flex aspect-[16/10] w-full items-center justify-center rounded-lg border border-slate-200 bg-white p-2">
                                                                    <img src={toAssetUrl(tenant.login_image_url)} alt={`${tenant.name} aktiv ekran şəkli`} className="h-full w-full object-contain" />
                                                                </div>
                                                            </div>
                                                        )}
                                                        {tenant.login_image_pending_url && (
                                                            <div>
                                                                <div className="mb-1 text-[11px] font-semibold text-amber-600">Təsdiq gözləyir</div>
                                                                <div className="flex aspect-[16/10] w-full items-center justify-center rounded-lg border border-amber-200 bg-white p-2">
                                                                    <img src={toAssetUrl(tenant.login_image_pending_url)} alt={`${tenant.name} gözləyən ekran şəkli`} className="h-full w-full object-contain" />
                                                                </div>
                                                            </div>
                                                        )}
                                                    </div>
                                                    <div className="mt-2 flex flex-wrap gap-2">
                                                        {tenant.login_image_url && (
                                                            <button type="button" onClick={() => handleDeleteActiveLoginImage(tenant)}
                                                                className="flex-1 rounded-lg bg-slate-900 px-3 py-1.5 text-xs font-semibold text-white hover:bg-slate-800">
                                                                Aktiv şəkli sil
                                                            </button>
                                                        )}
                                                        {tenant.login_image_pending_url && (
                                                            <>
                                                                <button type="button" onClick={() => handleApproveLoginImage(tenant)}
                                                                    className="flex-1 rounded-lg bg-emerald-600 px-3 py-1.5 text-xs font-semibold text-white hover:bg-emerald-700">
                                                                    Təsdiq et
                                                                </button>
                                                                <button type="button" onClick={() => handleRejectLoginImage(tenant)}
                                                                    className="flex-1 rounded-lg bg-red-50 px-3 py-1.5 text-xs font-semibold text-red-600 hover:bg-red-100">
                                                                    Rədd et
                                                                </button>
                                                            </>
                                                        )}
                                                    </div>
                                                </div>
                                            )}
                                        </div>
                                    )}

                                    {/* Gizli fayl seçiciləri («⋯» menyusundan açılır; menyu bağlansa da yerində qalmalıdır) */}
                                    <input id={menuImportId} type="file" accept=".xlsx,.xls" className="hidden"
                                        disabled={importingMenuId === tenant.id}
                                        onChange={(e) => { const f = e.target.files?.[0]; e.target.value = ''; handleMenuImport(tenant, f); }} />
                                    <input id={ingImportId} type="file" accept=".xlsx,.xls" className="hidden"
                                        disabled={importingIngredientId === tenant.id}
                                        onChange={(e) => { const f = e.target.files?.[0]; e.target.value = ''; handleIngredientImport(tenant, f); }} />

                                    {/* Əsas əməliyyatlar */}
                                    <div className="mt-auto flex items-center gap-2 border-t border-slate-100 pt-4">
                                        <button type="button" onClick={() => openEdit(tenant)}
                                            className="flex flex-1 items-center justify-center gap-1.5 rounded-xl bg-slate-900 px-3 py-2 text-xs font-semibold text-white shadow-sm transition-colors hover:bg-slate-800">
                                            <Pencil className="h-3.5 w-3.5" /> Redaktə
                                        </button>
                                        <button type="button" onClick={() => openFiscalConfig(tenant)}
                                            className="flex flex-1 items-center justify-center gap-1.5 rounded-xl border border-slate-200 bg-white px-3 py-2 text-xs font-semibold text-slate-700 transition-colors hover:bg-slate-50">
                                            <Receipt className="h-3.5 w-3.5 text-emerald-600" /> Kassa
                                        </button>
                                        <button type="button" onClick={() => openKitchenLinks(tenant)}
                                            className="flex flex-1 items-center justify-center gap-1.5 rounded-xl border border-slate-200 bg-white px-3 py-2 text-xs font-semibold text-slate-700 transition-colors hover:bg-slate-50">
                                            <Link2 className="h-3.5 w-3.5 text-sky-600" /> Linklər
                                        </button>
                                        <TenantMenu
                                            groups={[
                                                {
                                                    title: 'Hesab',
                                                    items: [
                                                        { label: 'Şifrəni sıfırla', Icon: KeyRound, onClick: () => openPasswordReset(tenant) },
                                                        { label: 'Token yenilə', Icon: RefreshCw, onClick: () => handleResetToken(tenant) },
                                                        { label: 'Bütün cihazlardan çıxış etdir', Icon: LogOut, onClick: () => handleForceLogout(tenant) },
                                                        { label: 'Diaqnostika', Icon: Stethoscope, onClick: () => openDiagnostics(tenant) },
                                                    ],
                                                },
                                                {
                                                    title: 'Menyu',
                                                    items: [
                                                        { label: 'Şablonu endir', Icon: FileDown, onClick: () => handleMenuTemplateDownload(tenant) },
                                                        { label: importingMenuId === tenant.id ? 'İdxal edilir...' : 'Exceldən idxal et', Icon: FileUp, disabled: importingMenuId === tenant.id, onClick: () => document.getElementById(menuImportId)?.click() },
                                                        { label: 'Menyunu sil', Icon: Trash2, danger: true, onClick: () => handleMenuClear(tenant) },
                                                    ],
                                                },
                                                {
                                                    title: 'İnqredientlər',
                                                    items: [
                                                        { label: 'Şablonu endir', Icon: FileDown, onClick: () => handleIngredientTemplateDownload(tenant) },
                                                        { label: importingIngredientId === tenant.id ? 'İdxal edilir...' : 'Exceldən idxal et', Icon: FileUp, disabled: importingIngredientId === tenant.id, onClick: () => document.getElementById(ingImportId)?.click() },
                                                        { label: 'İnqredientləri sil', Icon: Trash2, danger: true, onClick: () => handleIngredientClear(tenant) },
                                                    ],
                                                },
                                                {
                                                    title: 'Təhlükəli',
                                                    items: [
                                                        { label: 'Deaktiv et', Icon: Ban, danger: true, onClick: () => handleDeactivate(tenant) },
                                                        { label: 'Tam sil', Icon: Trash2, danger: true, onClick: () => handleHardDelete(tenant) },
                                                    ],
                                                },
                                            ]}
                                        />
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                )}
            </div>

            {/* Lisenziya nömrəsi artıq var → nə edək? */}
            {createModal && licConflict && (
                <div className="fixed inset-0 z-[70] flex items-center justify-center bg-slate-900/50 backdrop-blur-sm p-4">
                    <div className="w-full max-w-sm rounded-2xl bg-white p-6 shadow-2xl">
                        <h3 className="text-lg font-bold text-gray-800">
                            {licConflict.length > 1 ? `Bu lisenziyalar artıq var: ${licConflict.map((c) => c.dukan).join(', ')}` : `Lisenziya ${licConflict[0]?.dukan || ''} artıq var`}
                        </h3>
                        <div className="mt-3 max-h-56 space-y-2 overflow-y-auto">
                            {licConflict.map((c) => (
                                <div key={c.dukan} className="rounded-xl bg-gray-50 p-3 text-sm text-gray-600 space-y-0.5">
                                    {licConflict.length > 1 && <p className="font-bold text-gray-800">{c.dukan}</p>}
                                    {c.ad && <p>Ad: <b className="text-gray-800">{c.ad}</b></p>}
                                    {c.status && <p>Status: <b className="text-gray-800">{c.status}</b>{c.bitme ? ` · bitmə ${String(c.bitme).slice(0, 10).split('-').reverse().join('.')}` : ''}</p>}
                                    {c.usedBy && <p className="text-amber-700">Bu nömrə POS-da artıq «{c.usedBy}» restoranına bağlıdır.</p>}
                                </div>
                            ))}
                        </div>
                        <p className="mt-3 text-sm text-gray-500">Nə edək?</p>
                        <div className="mt-3 space-y-2">
                            <button type="button" disabled={creating} onClick={() => createTenant('link')}
                                className="w-full rounded-xl bg-[#0090bb] py-2.5 text-sm font-bold text-white hover:bg-[#007ea3] disabled:opacity-50">
                                Mövcuda bağla <span className="font-normal opacity-80">(ad və şifrələr yenilənir)</span>
                            </button>
                            <button type="button" disabled={creating} onClick={() => createTenant('new')}
                                className="w-full rounded-xl border border-[#0090bb] py-2.5 text-sm font-bold text-[#0090bb] hover:bg-[#0090bb]/5 disabled:opacity-50">
                                Yeni ID ver <span className="font-normal opacity-80">(növbəti boş nömrə)</span>
                            </button>
                            <button type="button" disabled={creating} onClick={() => setLicConflict(null)}
                                className="w-full rounded-xl border border-gray-200 py-2.5 text-sm font-semibold text-gray-600 hover:bg-gray-50">
                                Ləğv et
                            </button>
                        </div>
                    </div>
                </div>
            )}
            {createModal && (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/50 backdrop-blur-sm p-4" onClick={(e) => e.target === e.currentTarget && setCreateModal(false)}>
                    <div className="flex max-h-[92vh] w-full max-w-3xl flex-col rounded-2xl bg-white shadow-xl">
                        <div className="flex items-center justify-between border-b border-gray-100 px-6 py-4 shrink-0">
                            <h3 className="text-base font-bold text-gray-800">+ Yeni restoran</h3>
                            <button type="button" onClick={() => setCreateModal(false)} className="text-gray-400 hover:text-gray-600 text-xl">✕</button>
                        </div>
                        <form onSubmit={handleCreate} className="flex flex-1 min-h-0 flex-col">
                        <div className="flex-1 min-h-0 overflow-y-auto px-6 py-5">
                        <div className="grid grid-cols-1 gap-4 lg:grid-cols-2 lg:gap-6">
                            <div>
                                <label className="mb-1.5 block text-xs font-medium text-slate-600">Restoran adı</label>
                                <input type="text" value={createForm.name} onChange={(e) => setCreateForm({ ...createForm, name: e.target.value })} placeholder="Məs: Kapsalon" className={inputClass} required autoFocus />
                            </div>
                            <div>
                                <label className="mb-1.5 block text-xs font-medium text-slate-600">İstifadəçi adı</label>
                                <input type="text" value={createForm.username} onChange={(e) => setCreateForm({ ...createForm, username: e.target.value.toLowerCase() })} placeholder="Məs: kapsalon" className={inputClass} required />
                            </div>
                            <div>
                                <label className="mb-1.5 block text-xs font-medium text-slate-600">Yeni sistem admin login</label>
                                <input type="text" value={createForm.username_new_system} onChange={(e) => setCreateForm({ ...createForm, username_new_system: e.target.value.toLowerCase() })} placeholder="Məs: diamond_roastery" className={inputClass} required />
                            </div>
                            <div>
                                <label className="mb-1.5 block text-xs font-medium text-slate-600">Lisenziya dukan kodu</label>
                                <div className="flex gap-2">
                                    <input type="text" value={createForm.license_dukan_code} onChange={(e) => setCreateForm({ ...createForm, license_dukan_code: e.target.value })} placeholder={licNextBusy ? 'Yüklənir...' : 'Tək kod: 10143  və ya  çox kod: 10143, 10144'} className={`${inputClass} min-w-0 flex-1`} />
                                    <button type="button" onClick={fetchNextLicense} disabled={licNextBusy} title="Növbəti boş lisenziya nömrəsi"
                                        className="shrink-0 rounded-xl border border-gray-200 px-3 text-xs font-bold text-gray-600 hover:bg-gray-50 disabled:opacity-50">
                                        {licNextBusy ? '...' : '↻ Növbəti'}
                                    </button>
                                </div>
                                <p className="mt-1 text-[10px] text-gray-400">
                                    Növbəti boş nömrə avtomatik gəlir. Yaradanda lisenziya 2 günlük aktiv açılır, şifrə lisenziyaya da yazılır.
                                    Bir obyektdə bir neçə POS varsa nömrələri vergüllə yazın.
                                </p>
                                {String(createForm.name || '').trim() && (
                                    <p className="mt-1 text-[11px] text-gray-500">Lisenziyada ad: <b className="text-gray-700">{String(createForm.name).trim().slice(0, 100)}</b></p>
                                )}
                            </div>
                            <div>
                                <label className="mb-1.5 block text-xs font-medium text-slate-600">Şifrə</label>
                                <input type="password" value={createForm.password} onChange={(e) => setCreateForm({ ...createForm, password: e.target.value })} placeholder="Minimum 4 simvol" className={inputClass} required />
                            </div>
                            <div>
                                <label className="mb-1.5 block text-xs font-medium text-slate-600">Telefon</label>
                                <input type="text" value={createForm.phone} onChange={(e) => setCreateForm({ ...createForm, phone: e.target.value })} placeholder="Məs: 0505550700" className={inputClass} />
                            </div>
                            <div>
                                <label className="mb-1.5 block text-xs font-medium text-slate-600">Servis faizi (%)</label>
                                <input type="number" min="0" step="0.01" value={createForm.service_percent} onChange={(e) => setCreateForm({ ...createForm, service_percent: e.target.value })} placeholder="0" className={inputClass} />
                            </div>
                            <div className="rounded-xl border border-gray-200 bg-gray-50 p-3 lg:col-span-2">
                                <div className="mb-2 text-xs font-semibold uppercase tracking-wider text-gray-500">Public icazələr</div>
                                <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
                                    <label className="flex items-center justify-between rounded-xl border border-gray-200 bg-white px-3 py-2 text-sm font-medium text-gray-700">
                                        <span>Mətbəx ekranı</span>
                                        <input type="checkbox" checked={Boolean(createForm.kitchen_display_enabled)} onChange={(e) => setCreateForm({ ...createForm, kitchen_display_enabled: e.target.checked })} className="sa-switch" />
                                    </label>
                                    <label className="flex items-center justify-between rounded-xl border border-gray-200 bg-white px-3 py-2 text-sm font-medium text-gray-700">
                                        <span>QR menu</span>
                                        <input type="checkbox" checked={Boolean(createForm.qr_menu_enabled)} onChange={(e) => setCreateForm({ ...createForm, qr_menu_enabled: e.target.checked })} className="sa-switch" />
                                    </label>
                                </div>
                            </div>
                            <div className="lg:col-span-2">
                                <label className="mb-2 block text-xs font-medium text-slate-600">Plan</label>
                                <div className="grid grid-cols-3 gap-2">
                                    {PLANS.map((plan) => (
                                        <button
                                            key={plan.value}
                                            type="button"
                                            onClick={() => setCreateForm({ ...createForm, plan: plan.value })}
                                            className={`rounded-xl border-2 py-2 text-xs font-bold transition-all ${
                                                createForm.plan === plan.value
                                                    ? 'border-[#0090bb] bg-sky-50 text-[#007ea4]'
                                                    : 'border-gray-200 text-gray-500'
                                            }`}
                                        >
                                            {plan.label}
                                        </button>
                                    ))}
                                </div>
                            </div>
                        </div>
                        </div>
                        <div className="flex gap-3 border-t border-gray-100 px-6 py-4 shrink-0">
                            <button type="button" onClick={() => setCreateModal(false)} className="flex-1 rounded-xl border border-gray-200 py-2.5 text-sm font-semibold text-gray-500 hover:bg-gray-50">
                                Ləğv et
                            </button>
                            <button type="submit" disabled={creating} className="flex-1 rounded-xl py-2.5 text-sm font-semibold text-white disabled:opacity-60 bg-[#0090bb] font-semibold transition-colors hover:bg-[#007ea4]">
                                {creating ? 'Yaradılır...' : 'Yarat'}
                            </button>
                        </div>
                        </form>
                    </div>
                </div>
            )}

            {editModal && selectedTenant && (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/50 p-2 backdrop-blur-sm sm:p-4" onClick={(e) => e.target === e.currentTarget && setEditModal(false)}>
                    <div className="flex h-[94vh] w-full max-w-5xl flex-col overflow-hidden rounded-2xl bg-white shadow-2xl shadow-slate-900/20">
                        <div className="flex shrink-0 items-center gap-3 border-b border-slate-100 px-4 py-4 sm:px-6">
                            <div className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-xl bg-gradient-to-br from-sky-500 to-indigo-500 text-sm font-bold text-white">
                                {String(selectedTenant.name || '?').trim().split(/\s+/).slice(0, 2).map((w) => w[0]?.toUpperCase() || '').join('') || '?'}
                            </div>
                            <div className="min-w-0 flex-1">
                                <h3 className="truncate text-base font-semibold text-slate-800">{selectedTenant.name}</h3>
                                <p className="truncate text-xs text-slate-500">Restoran ayarları · @{selectedTenant.username}</p>
                            </div>
                            <button type="button" onClick={() => setEditModal(false)} aria-label="Bağla"
                                className="flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-xl text-slate-400 transition-colors hover:bg-slate-100 hover:text-slate-700">
                                <X className="h-5 w-5" />
                            </button>
                        </div>

                        {/* Mobil tab seçici */}
                        <div className="flex shrink-0 gap-1 overflow-x-auto border-b border-slate-100 px-3 py-2 lg:hidden">
                            {EDIT_TABS.map(({ key, label, Icon }) => (
                                <button key={key} type="button" onClick={() => setEditTab(key)}
                                    className={`flex flex-shrink-0 items-center gap-1.5 whitespace-nowrap rounded-lg px-3 py-1.5 text-xs font-semibold transition-colors ${
                                        editTab === key ? 'bg-slate-900 text-white' : 'text-slate-500 hover:bg-slate-100'
                                    }`}>
                                    <Icon className="h-3.5 w-3.5" />{label}
                                </button>
                            ))}
                        </div>

                        <div className="flex min-h-0 flex-1">
                        {/* Masaüstü yan tab menyusu */}
                        <nav className="hidden w-56 shrink-0 flex-col gap-0.5 overflow-y-auto border-r border-slate-100 bg-slate-50/70 p-3 lg:flex">
                            {EDIT_TABS.map(({ key, label, hint, Icon }) => {
                                const active = editTab === key;
                                return (
                                    <button key={key} type="button" onClick={() => setEditTab(key)}
                                        className={`flex items-start gap-2.5 rounded-xl px-3 py-2.5 text-left transition-colors ${
                                            active ? 'bg-white shadow-sm ring-1 ring-slate-200' : 'hover:bg-white/70'
                                        }`}>
                                        <Icon className={`mt-0.5 h-4 w-4 flex-shrink-0 ${active ? 'text-[#0090bb]' : 'text-slate-400'}`} />
                                        <span className="min-w-0">
                                            <span className={`block text-sm font-semibold ${active ? 'text-slate-800' : 'text-slate-600'}`}>{label}</span>
                                            <span className="block truncate text-[11px] text-slate-400">{hint}</span>
                                        </span>
                                    </button>
                                );
                            })}
                        </nav>

                        <form onSubmit={handleEdit} className="flex min-h-0 min-w-0 flex-1 flex-col">
                        <div className="min-h-0 flex-1 overflow-y-auto px-4 py-5 sm:px-7 sm:py-6">
                            {(() => {
                                const t = EDIT_TABS.find((x) => x.key === editTab);
                                return t ? (
                                    <div className="mb-6">
                                        <h4 className="text-lg font-semibold tracking-tight text-slate-800">{t.label}</h4>
                                        <p className="text-sm text-slate-500">{t.hint}</p>
                                    </div>
                                ) : null;
                            })()}
                        {editTab === 'info' && (
                        <div className="grid grid-cols-1 gap-4 lg:grid-cols-2 lg:gap-6">
                            <div>
                                <label className="mb-1.5 block text-xs font-medium text-slate-600">Restoran adı</label>
                                <input type="text" value={editForm.name} onChange={(e) => setEditForm({ ...editForm, name: e.target.value })} className={inputClass} required autoFocus />
                            </div>
                            <div>
                                <label className="mb-1.5 block text-xs font-medium text-slate-600">Telefon</label>
                                <input type="text" value={editForm.phone} onChange={(e) => setEditForm({ ...editForm, phone: e.target.value })} className={inputClass} />
                            </div>
                            <div>
                                <label className="mb-1.5 block text-xs font-medium text-slate-600">Yeni sistem admin login</label>
                                <input type="text" value={editForm.username_new_system} onChange={(e) => setEditForm({ ...editForm, username_new_system: e.target.value.toLowerCase() })} className={inputClass} required />
                            </div>
                            <div>
                                <label className="mb-1.5 block text-xs font-medium text-slate-600">Lisenziya dukan kodu</label>
                                <input type="text" value={editForm.license_dukan_code} onChange={(e) => setEditForm({ ...editForm, license_dukan_code: e.target.value })} placeholder="Tək kod: 10143  və ya  çox kod: 10143, 10144, 10145" className={inputClass} />
                                <p className="mt-1 text-[10px] text-gray-400">1 obyektdə 3 fərqli POS lisenziya kodu varsa vergüllə yazın</p>
                            </div>
                            <div>
                                <label className="mb-1.5 block text-xs font-medium text-slate-600">Servis faizi (%)</label>
                                <input type="number" min="0" step="0.01" value={editForm.service_percent} onChange={(e) => setEditForm({ ...editForm, service_percent: e.target.value })} className={inputClass} />
                            </div>
                            <div>
                                <label className="mb-2 block text-xs font-medium text-slate-600">Plan</label>
                                <div className="grid grid-cols-3 gap-2">
                                    {PLANS.map((plan) => (
                                        <button
                                            key={plan.value}
                                            type="button"
                                            onClick={() => setEditForm({ ...editForm, plan: plan.value })}
                                            className={`rounded-xl border-2 py-2.5 text-sm font-semibold transition-all ${
                                                editForm.plan === plan.value
                                                    ? 'border-[#0090bb] bg-sky-50 text-[#007ea4]'
                                                    : 'border-slate-200 text-slate-500 hover:border-slate-300'
                                            }`}
                                        >
                                            {plan.label}
                                        </button>
                                    ))}
                                </div>
                            </div>
                            <div className="flex items-center gap-3 lg:col-span-2">
                                <label className="text-xs font-medium text-slate-600">Status</label>
                                <button
                                    type="button"
                                    onClick={() => setEditForm({ ...editForm, active: editForm.active ? 0 : 1 })}
                                    className={`rounded-lg px-3 py-1.5 text-xs font-bold transition-all ${
                                        editForm.active ? 'bg-green-100 text-slate-600' : 'bg-red-100 text-red-500'
                                    }`}
                                >
                                    {editForm.active ? 'Aktiv' : 'Passiv'}
                                </button>
                            </div>
                        </div>
                        )}

                        {editTab === 'menu' && (
                        <div className="space-y-7">
                            <section>
                                <SecTitle>Public linklər</SecTitle>
                                <div className="space-y-3">
                            <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
                                    <label className="flex items-center justify-between rounded-xl border border-gray-200 bg-white px-3 py-2 text-sm font-medium text-gray-700">
                                        <span>Mətbəx ekranı</span>
                                        <input type="checkbox" checked={Boolean(editForm.kitchen_display_enabled)} onChange={(e) => setEditForm({ ...editForm, kitchen_display_enabled: e.target.checked })} className="sa-switch" />
                                    </label>
                                    <label className="flex items-center justify-between rounded-xl border border-gray-200 bg-white px-3 py-2 text-sm font-medium text-gray-700">
                                        <span>QR menu</span>
                                        <input type="checkbox" checked={Boolean(editForm.qr_menu_enabled)} onChange={(e) => setEditForm({ ...editForm, qr_menu_enabled: e.target.checked })} className="sa-switch" />
                                    </label>
                                    <label className={`flex items-center justify-between rounded-xl border px-3 py-2 text-sm font-medium ${editForm.qr_menu_enabled ? 'border-gray-200 bg-white text-gray-700' : 'border-gray-100 bg-gray-50 text-gray-300'}`}>
                                        <span title="Bağlı olarsa, müştəri yalnız səbətə əlavə edə bilir — sifariş POS-a göndərilmir">
                                            QR sifariş POS-a göndərilsin
                                        </span>
                                        <input type="checkbox"
                                            disabled={!editForm.qr_menu_enabled}
                                            checked={Boolean(editForm.qr_menu_ordering_enabled)}
                                            onChange={(e) => setEditForm({ ...editForm, qr_menu_ordering_enabled: e.target.checked })}
                                            className="sa-switch" />
                                    </label>
                                    <label className={`flex items-center justify-between gap-2 rounded-xl border px-3 py-2 text-sm font-medium sm:col-span-2 ${editForm.qr_menu_enabled ? 'border-gray-200 bg-white text-gray-700' : 'border-gray-100 bg-gray-50 text-gray-300'}`}>
                                        <span>QR menyu dizaynı</span>
                                        <select
                                            disabled={!editForm.qr_menu_enabled}
                                            value={editForm.qr_menu_design || 'v1'}
                                            onChange={(e) => setEditForm({ ...editForm, qr_menu_design: e.target.value })}
                                            className="rounded-lg border border-gray-200 bg-white px-2 py-1 text-sm font-semibold text-gray-700 focus:border-sky-400 focus:outline-none disabled:opacity-40"
                                        >
                                            <option value="v1">V1 · Klassik</option>
                                            <option value="v2">V2 · Modern</option>
                                        </select>
                                    </label>
                                    {/* QR menyu məhsul sıralaması (V1 + V2) */}
                                    <label className={`flex items-center justify-between gap-2 rounded-xl border px-3 py-2 text-sm font-medium sm:col-span-2 ${editForm.qr_menu_enabled ? 'border-gray-200 bg-white text-gray-700' : 'border-gray-100 bg-gray-50 text-gray-300'}`}>
                                        <span className="min-w-0">
                                            Məhsul sıralaması
                                            <span className="block text-[11px] font-normal text-gray-400">Admin əllə sıraladığı kateqoriyada adminin sırası qalır</span>
                                        </span>
                                        <select
                                            disabled={!editForm.qr_menu_enabled}
                                            value={editForm.qr_menu_sort || 'default'}
                                            onChange={(e) => setEditForm({ ...editForm, qr_menu_sort: e.target.value })}
                                            className="max-w-[60%] rounded-lg border border-gray-200 bg-white px-2 py-1 text-sm font-semibold text-gray-700 focus:border-sky-400 focus:outline-none disabled:opacity-40"
                                        >
                                            <option value="default">Standart: admin Menyu idarəsindəki sıra</option>
                                            <option value="price_asc">Qiymət: ucuzdan bahaya</option>
                                            <option value="price_desc">Qiymət: bahadan ucuza</option>
                                            <option value="date_desc">Yüklənmə tarixi: yenidən köhnəyə</option>
                                            <option value="date_asc">Yüklənmə tarixi: köhnədən yeniyə</option>
                                            <option value="sold_desc">Ən çox satılan (son 30 gün)</option>
                                            <option value="sold_asc">Ən az satılan (son 30 gün)</option>
                                        </select>
                                    </label>
                                    {selectedTenant?.id && <CustomSortReset tenantId={selectedTenant.id} />}
                            </div>
                                </div>
                            </section>
                            <section>
                                <SecTitle>Rənglər</SecTitle>
                                <div className="space-y-3">
                                    {/* QR menyu rəngləri (v1 + v2) — seçici + hex */}
                                    <div className="rounded-xl border border-gray-200 bg-white p-3 sm:col-span-2">
                                        <div className="mb-2 text-sm font-semibold text-gray-700">QR menyu rəngləri (v1 + v2)</div>
                                        {[
                                            { key: 'bg', label: 'Fon (background)', def: '#f4f8fa' },
                                            { key: 'primary', label: 'Buttonlar / vurğu', def: '#0090bb' },
                                        ].map(({ key, label, def }) => {
                                            const val = (editForm.qr_theme && editForm.qr_theme[key]) || '';
                                            const setVal = (v) => setEditForm({ ...editForm, qr_theme: { ...(editForm.qr_theme || {}), [key]: v } });
                                            return (
                                                <div key={key} className="mb-2 flex items-center gap-2">
                                                    <span className="w-36 flex-shrink-0 text-xs text-gray-500">{label}</span>
                                                    <input type="color" value={val || def}
                                                        onChange={(e) => setVal(e.target.value)}
                                                        className="h-8 w-10 flex-shrink-0 cursor-pointer rounded border border-gray-200 bg-white p-0" />
                                                    <input type="text" value={val} placeholder={def}
                                                        onChange={(e) => setVal(e.target.value)}
                                                        className="w-28 rounded-lg border border-gray-200 px-2 py-1 font-mono text-xs focus:border-sky-400 focus:outline-none" />
                                                    {val && (
                                                        <button type="button" onClick={() => setVal('')}
                                                            className="text-xs font-semibold text-gray-400 hover:text-red-500">sıfırla</button>
                                                    )}
                                                </div>
                                            );
                                        })}
                                        <p className="mt-1 text-[11px] text-gray-400">Boş = standart rəng. Seçicidən seçin və ya əl ilə #ff0001 yazın.</p>
                                    </div>
                                </div>
                            </section>
                        </div>
                        )}

                        {editTab === 'cover' && (
                        <div className="space-y-7">
                            <section>
                                <SecTitle>Məlumat və əlaqə</SecTitle>
                                <div className="space-y-3">
                                {/* QR menyu welcome (cover) ekranı */}
                                <div className={`mt-3 rounded-xl border p-3 ${editForm.qr_menu_enabled ? 'border-slate-200 bg-white' : 'border-gray-100 bg-gray-50/50 opacity-60'}`}>
                                    <div className="mb-2 flex items-center gap-1.5 text-sm font-semibold text-slate-800">
                                        <span>QR menyu welcome ekranı</span>
                                    </div>
                                    <div className="mb-3 flex items-center gap-3">
                                        <div className="h-16 w-16 flex-shrink-0 overflow-hidden rounded-2xl border border-slate-200 bg-white flex items-center justify-center">
                                            {editForm.qr_cover_logo_url ? (
                                                <img src={editForm.qr_cover_logo_url} alt="" className="h-full w-full object-contain" onError={(e) => { e.target.style.display = 'none'; }} />
                                            ) : (
                                                <ImageIcon className="h-6 w-6 text-slate-300" />
                                            )}
                                        </div>
                                        <div className="flex flex-wrap gap-2">
                                            <label className={`inline-flex cursor-pointer items-center justify-center rounded-lg px-3 py-1.5 text-xs font-semibold text-white ${editForm.qr_menu_enabled ? 'bg-slate-900 hover:bg-slate-800' : 'bg-gray-300 cursor-not-allowed'}`}>
                                                {uploadingCoverLogo ? 'Yüklənir...' : 'Logo yüklə'}
                                                <input type="file" accept="image/*" className="hidden" onChange={handleCoverLogoUpload} disabled={!editForm.qr_menu_enabled || uploadingCoverLogo} />
                                            </label>
                                            {editForm.qr_cover_logo_url && (
                                                <button type="button" onClick={handleCoverLogoRemove} disabled={uploadingCoverLogo}
                                                    className="rounded-lg border border-red-200 px-3 py-1.5 text-xs font-semibold text-red-500 hover:bg-red-50 disabled:opacity-50">
                                                    Sil
                                                </button>
                                            )}
                                        </div>
                                    </div>
                                    <div className="space-y-2">
                                        <input type="text" value={editForm.qr_cover_address || ''} disabled={!editForm.qr_menu_enabled}
                                            onChange={(e) => setEditForm({ ...editForm, qr_cover_address: e.target.value })}
                                            placeholder="Ünvan (məs: Bakı şəh., Nərimanov r., Atatürk pr. 12)"
                                            className="w-full rounded-lg border border-slate-200 bg-white px-3 py-1.5 text-sm focus:outline-none focus:border-sky-400 disabled:bg-gray-50" />
                                        {/* İş saatları — strukturlu (Hər gün / Günlərə görə) */}
                                        <div className="rounded-lg border border-slate-200 bg-white p-2.5 space-y-2">
                                            <div className="flex items-center justify-between">
                                                <span className="text-xs font-semibold text-slate-600">İş saatları</span>
                                                <div className="inline-flex overflow-hidden rounded-md border border-slate-200 text-[11px] font-bold">
                                                    {[['everyday', 'Hər gün'], ['perday', 'Günlərə görə']].map(([m, l]) => (
                                                        <button key={m} type="button" disabled={!editForm.qr_menu_enabled}
                                                            onClick={() => updateHours({ mode: m })}
                                                            className={`px-2 py-1 transition-all ${(editForm.qr_cover_hours_obj?.mode || 'everyday') === m ? 'bg-slate-900 text-white' : 'bg-white text-slate-600'}`}>
                                                            {l}
                                                        </button>
                                                    ))}
                                                </div>
                                            </div>
                                            {(editForm.qr_cover_hours_obj?.mode || 'everyday') === 'everyday' ? (
                                                <div className="flex items-center gap-2 text-sm">
                                                    <span className="w-24 text-xs text-gray-500">Hər gün</span>
                                                    <input type="time" disabled={!editForm.qr_menu_enabled} value={editForm.qr_cover_hours_obj?.everyday?.open || '09:00'}
                                                        onChange={(e) => updateHours({ everyday: { ...editForm.qr_cover_hours_obj.everyday, open: e.target.value } })}
                                                        className="rounded border border-slate-200 px-2 py-1 text-sm disabled:bg-gray-50" />
                                                    <span>–</span>
                                                    <input type="time" disabled={!editForm.qr_menu_enabled} value={editForm.qr_cover_hours_obj?.everyday?.close || '23:00'}
                                                        onChange={(e) => updateHours({ everyday: { ...editForm.qr_cover_hours_obj.everyday, close: e.target.value } })}
                                                        className="rounded border border-slate-200 px-2 py-1 text-sm disabled:bg-gray-50" />
                                                </div>
                                            ) : (
                                                <div className="space-y-1">
                                                    {HOURS_DAY_ORDER.map((k) => {
                                                        const d = (editForm.qr_cover_hours_obj?.days || {})[k] || { on: true, open: '09:00', close: '23:00' };
                                                        return (
                                                            <div key={k} className="flex items-center gap-2 text-sm">
                                                                <label className="flex w-28 items-center gap-1.5 shrink-0">
                                                                    <input type="checkbox" checked={!!d.on} disabled={!editForm.qr_menu_enabled}
                                                                        onChange={(e) => updateHoursDay(k, { on: e.target.checked })} />
                                                                    <span className="text-xs text-gray-600">{HOURS_DAY_LABELS[k]}</span>
                                                                </label>
                                                                {d.on ? (
                                                                    <>
                                                                        <input type="time" disabled={!editForm.qr_menu_enabled} value={d.open || '09:00'}
                                                                            onChange={(e) => updateHoursDay(k, { open: e.target.value })}
                                                                            className="rounded border border-slate-200 px-2 py-1 text-sm disabled:bg-gray-50" />
                                                                        <span>–</span>
                                                                        <input type="time" disabled={!editForm.qr_menu_enabled} value={d.close || '23:00'}
                                                                            onChange={(e) => updateHoursDay(k, { close: e.target.value })}
                                                                            className="rounded border border-slate-200 px-2 py-1 text-sm disabled:bg-gray-50" />
                                                                    </>
                                                                ) : <span className="text-xs font-semibold text-red-400">Bağlı</span>}
                                                            </div>
                                                        );
                                                    })}
                                                </div>
                                            )}
                                        </div>
                                        <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
                                            <div className="relative">
                                                <Globe className="pointer-events-none absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
                                                <input type="url" value={editForm.qr_cover_facebook || ''} disabled={!editForm.qr_menu_enabled}
                                                    onChange={(e) => setEditForm({ ...editForm, qr_cover_facebook: e.target.value })}
                                                    placeholder="Facebook linki"
                                                    className="w-full rounded-lg border border-slate-200 bg-white pl-8 pr-3 py-1.5 text-sm focus:outline-none focus:border-sky-400 disabled:bg-gray-50" />
                                            </div>
                                            <div className="relative">
                                                <Camera className="pointer-events-none absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
                                                <input type="url" value={editForm.qr_cover_instagram || ''} disabled={!editForm.qr_menu_enabled}
                                                    onChange={(e) => setEditForm({ ...editForm, qr_cover_instagram: e.target.value })}
                                                    placeholder="Instagram linki"
                                                    className="w-full rounded-lg border border-slate-200 bg-white pl-8 pr-3 py-1.5 text-sm focus:outline-none focus:border-sky-400 disabled:bg-gray-50" />
                                            </div>
                                            <div className="relative">
                                                <MessageCircle className="pointer-events-none absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
                                                <input type="tel" value={editForm.qr_cover_whatsapp || ''} disabled={!editForm.qr_menu_enabled}
                                                    onChange={(e) => setEditForm({ ...editForm, qr_cover_whatsapp: e.target.value })}
                                                    placeholder="WhatsApp nömrəsi (məs: 0501234567)"
                                                    className="w-full rounded-lg border border-slate-200 bg-white pl-8 pr-3 py-1.5 text-sm focus:outline-none focus:border-sky-400 disabled:bg-gray-50" />
                                            </div>
                                        </div>
                                        <div className="relative">
                                            <Star className="pointer-events-none absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
                                            <input type="text" value={editForm.google_place_id || ''} disabled={!editForm.qr_menu_enabled}
                                                onChange={(e) => setEditForm({ ...editForm, google_place_id: e.target.value })}
                                                placeholder="Google Place ID (məs. ChIJ...)"
                                                className="w-full rounded-lg border border-slate-200 bg-white pl-8 pr-3 py-1.5 text-sm focus:outline-none focus:border-sky-400 disabled:bg-gray-50" />
                                        </div>
                                        <label className="flex items-center gap-2 text-xs font-semibold text-slate-600 cursor-pointer">
                                            <input type="checkbox"
                                                checked={Boolean(editForm.google_review_enabled)}
                                                disabled={!editForm.qr_menu_enabled || !String(editForm.google_place_id || '').trim()}
                                                onChange={(e) => setEditForm({ ...editForm, google_review_enabled: e.target.checked })}
                                                className="sa-switch" />
                                            QR cover-də Google rəy ulduzlarını göstər
                                        </label>
                                        <p className="text-[10px] text-slate-500">
                                            Telefon yuxarıdakı "Telefon" sahəsindən götürülür. Place ID + yuxarıdakı tik aktivdirsə, QR cover-də ulduzlar görünür və klik Google rəy səhifəsinə yönləndirir (pulsuz, API lazım deyil). Place ID-ni pulsuz "Place ID Finder" ilə tapırsınız.
                                        </p>
                                        {/* 2GIS firma səhifəsi */}
                                        <div className="relative">
                                            <img src="/2gis_icon.png" alt="" className="pointer-events-none absolute left-2 top-1/2 h-4 w-4 -translate-y-1/2 rounded" />
                                            <input type="url" value={editForm.twogis_url || ''} disabled={!editForm.qr_menu_enabled}
                                                onChange={(e) => setEditForm({ ...editForm, twogis_url: e.target.value })}
                                                placeholder="2GIS linki (məs. https://2gis.az/baku/firm/700...)"
                                                className="w-full rounded-lg border border-slate-200 bg-white pl-8 pr-3 py-1.5 text-sm focus:outline-none focus:border-sky-400 disabled:bg-gray-50" />
                                        </div>
                                        <p className="text-[10px] text-slate-500">
                                            Link yazılıbsa QR cover-də «2GIS» düyməsi görünür (firma səhifəsi açılır). 2GIS-dən istənilən linki yapışdırın, sistem firma linkini özü təmizləyir. Silsəniz düymə gizlənir.
                                        </p>
                                    </div>
                                </div>
                                </div>
                            </section>
                            <section>
                                <SecTitle>Fon və dizayn</SecTitle>
                                <div className="space-y-3">
                            {/* QR giriş (cover) fon şəkli */}
                            <div className="rounded-2xl border border-slate-200 bg-white p-4">
                                <p className="flex items-center gap-1.5 text-sm font-medium text-slate-800">
                                    <span>QR giriş ekranının fonu</span>
                                </p>
                                <p className="mt-1 text-[11px] text-slate-500">
                                    Seçilən şəkil QR menyunun giriş ekranında arxa fon olur. Şəkil yoxdursa standart fon göstərilir.
                                </p>
                                {/* Giriş ekranı dizaynı: V2 standart (yeni və köhnə tenantlar), V1 yalnız açıq seçiləndə. qr_theme.cover_design, «Yadda saxla» ilə */}
                                <div className="mt-3">
                                    <p className="mb-1.5 text-[11px] font-semibold text-slate-800">Giriş ekranı dizaynı</p>
                                    <div className="grid grid-cols-2 gap-2">
                                        {[
                                            { key: 'v1', title: 'V1', sub: 'Klassik' },
                                            { key: 'v2', title: 'V2', sub: 'Modern (standart)' },
                                        ].map((o) => {
                                            const cur = editForm.qr_theme?.cover_design === 'v1' ? 'v1' : 'v2'; // seçilməyibsə V2
                                            const active = cur === o.key;
                                            return (
                                                <button key={o.key} type="button"
                                                    onClick={() => setEditForm({ ...editForm, qr_theme: { ...(editForm.qr_theme || {}), cover_design: o.key } })}
                                                    className={`rounded-lg border px-3 py-2 text-left transition-all ${active ? 'border-sky-500 bg-white ring-1 ring-sky-400' : 'border-gray-200 bg-white/60 hover:bg-white'}`}>
                                                    <span className={`block text-sm font-black ${active ? 'text-sky-700' : 'text-gray-700'}`}>{o.title}</span>
                                                    <span className="block text-[10px] text-gray-500">{o.sub}</span>
                                                </button>
                                            );
                                        })}
                                    </div>
                                </div>
                                <div className="mt-3 flex items-start gap-3">
                                    <div className="relative h-24 w-14 flex-shrink-0 overflow-hidden rounded-lg border border-slate-200 bg-white flex items-center justify-center">
                                        {editForm.qr_cover_bg_url ? (
                                            <>
                                                <img src={editForm.qr_cover_bg_url} alt="" className="h-full w-full object-cover" onError={(e) => { e.target.style.display = 'none'; }} />
                                                {/* canlı önizləmə: seçilmiş örtük */}
                                                <div className="absolute inset-0" style={{
                                                    background: (editForm.qr_theme?.cover_overlay === 'blue' ? (editForm.qr_theme?.primary || '#0090bb') : '#ffffff'),
                                                    opacity: (Number.isFinite(Number(editForm.qr_theme?.cover_overlay_opacity)) && editForm.qr_theme?.cover_overlay_opacity !== '' ? Number(editForm.qr_theme.cover_overlay_opacity) : 20) / 100,
                                                }} />
                                            </>
                                        ) : (
                                            <ImageIcon className="h-6 w-6 text-slate-300" />
                                        )}
                                    </div>
                                    <div className="space-y-2">
                                        <div className="flex flex-wrap gap-2">
                                            <label className="inline-flex cursor-pointer items-center justify-center rounded-lg bg-slate-900 hover:bg-slate-800 px-3 py-1.5 text-xs font-semibold text-white">
                                                {uploadingCoverBg ? 'Yüklənir...' : (editForm.qr_cover_bg_url ? 'Şəkli dəyiş' : 'Şəkil seç')}
                                                <input type="file" accept="image/*" className="hidden" onChange={handleCoverBgUpload} disabled={uploadingCoverBg} />
                                            </label>
                                            {editForm.qr_cover_bg_url && (
                                                <button type="button" onClick={handleCoverBgRemove} disabled={uploadingCoverBg}
                                                    className="rounded-lg border border-red-200 px-3 py-1.5 text-xs font-semibold text-red-500 hover:bg-red-50 disabled:opacity-50">
                                                    Sil
                                                </button>
                                            )}
                                        </div>
                                        <p className="text-[10px] text-slate-500 leading-relaxed">
                                            Tövsiyə: 1080 × 1920 px (portret). JPG / PNG / WEBP.<br />
                                            Böyük şəkil avtomatik sıxılır, ölçü limiti yoxdur.<br />
                                            Şəkil seçən kimi dərhal tətbiq olunur.
                                        </p>
                                    </div>
                                </div>

                                {/* Şəklin üstündəki örtük: rəng + şəffaflıq (qr_theme-də, «Yadda saxla» ilə) */}
                                {editForm.qr_cover_bg_url && (() => {
                                    const th = editForm.qr_theme || {};
                                    const ovColor = th.cover_overlay === 'blue' ? 'blue' : 'white';
                                    const ovOp = (th.cover_overlay_opacity !== undefined && th.cover_overlay_opacity !== '' && Number.isFinite(Number(th.cover_overlay_opacity)))
                                        ? Number(th.cover_overlay_opacity) : 20;
                                    const setTh = (patch) => setEditForm({ ...editForm, qr_theme: { ...(editForm.qr_theme || {}), ...patch } });
                                    const blueHex = th.primary || '#0090bb';
                                    return (
                                        <div className="mt-3 rounded-lg border border-slate-200 bg-white/70 p-3 space-y-3">
                                            <div>
                                                <p className="mb-1.5 text-[11px] font-semibold text-slate-800">Örtük rəngi</p>
                                                <div className="flex gap-2">
                                                    {[
                                                        { key: 'white', label: 'Ağ', swatch: '#ffffff' },
                                                        { key: 'blue', label: 'Mavi (brend)', swatch: blueHex },
                                                    ].map((o) => (
                                                        <button key={o.key} type="button" onClick={() => setTh({ cover_overlay: o.key })}
                                                            className={`flex items-center gap-2 rounded-lg border px-3 py-1.5 text-xs font-semibold transition-all ${
                                                                ovColor === o.key ? 'border-sky-500 bg-sky-50 text-slate-800 ring-1 ring-sky-400' : 'border-gray-200 bg-white text-gray-600 hover:bg-gray-50'
                                                            }`}>
                                                            <span className="h-4 w-4 rounded-full border border-gray-300" style={{ background: o.swatch }} />
                                                            {o.label}
                                                        </button>
                                                    ))}
                                                </div>
                                            </div>
                                            <div>
                                                <div className="mb-1 flex items-center justify-between">
                                                    <p className="text-[11px] font-semibold text-slate-800">Örtük şəffaflığı</p>
                                                    <span className="rounded-md bg-sky-100 px-2 py-0.5 font-mono text-[11px] font-bold text-slate-800">{ovOp}%</span>
                                                </div>
                                                <input type="range" min="0" max="90" step="5" value={ovOp}
                                                    onChange={(e) => setTh({ cover_overlay_opacity: Number(e.target.value) })}
                                                    className="w-full cursor-pointer accent-sky-600" />
                                                <div className="flex justify-between text-[10px] text-gray-400">
                                                    <span>0% (şəkil təmiz)</span><span>90% (rəng güclü)</span>
                                                </div>
                                            </div>
                                            <p className="text-[10px] text-slate-500">Solda kiçik önizləmə dərhal dəyişir. Saytda görünməsi üçün aşağıda «Yadda saxla» basın.</p>
                                        </div>
                                    );
                                })()}
                            </div>
                                </div>
                            </section>
                            <section>
                                <SecTitle>Əlavə düymələr</SecTitle>
                                <div className="space-y-3">
                                    {/* QR cover-də əlavə düymə (məs. "Evlərə bax") */}
                                    <div className="rounded-2xl border border-slate-200 bg-white p-3 sm:col-span-2">
                                        <label className="flex cursor-pointer items-center justify-between gap-2 text-sm font-semibold text-slate-800">
                                            <span className="flex items-center gap-1.5"><span>Menyuya bax altında əlavə düymə</span></span>
                                            <input
                                                type="checkbox"
                                                checked={Boolean(editForm.qr_cover_booking_enabled)}
                                                onChange={(e) => setEditForm({ ...editForm, qr_cover_booking_enabled: e.target.checked })}
                                                className="sa-switch"
                                            />
                                        </label>
                                        {editForm.qr_cover_booking_enabled && (
                                            <div className="mt-2">
                                                <label className="mb-1 block text-xs font-medium text-slate-600">Düymənin adı</label>
                                                <input
                                                    type="text"
                                                    maxLength={100}
                                                    value={editForm.qr_cover_booking_label || ''}
                                                    onChange={(e) => setEditForm({ ...editForm, qr_cover_booking_label: e.target.value })}
                                                    placeholder="məs. Aframelərə bax"
                                                    className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm focus:border-sky-400 focus:outline-none"
                                                />
                                                <p className="mt-1 text-[10px] text-slate-500">
                                                    QR cover-də "Menyuya bax" düyməsinin altında görünür → ev vitrini səhifəsini (/evler) açır.
                                                </p>
                                            </div>
                                        )}
                                    </div>
                                    {/* Banket menyu — QR cover düyməsi + admin bölməsi (hər ikisini idarə edir) */}
                                    <div className="rounded-2xl border border-slate-200 bg-white p-3 sm:col-span-2">
                                        <label className="flex cursor-pointer items-center justify-between gap-2 text-sm font-semibold text-slate-800">
                                            <span className="flex items-center gap-1.5"><span>Banket menyu</span></span>
                                            <input
                                                type="checkbox"
                                                checked={Boolean(editForm.qr_cover_banquet_enabled)}
                                                onChange={(e) => setEditForm({ ...editForm, qr_cover_banquet_enabled: e.target.checked })}
                                                className="sa-switch"
                                            />
                                        </label>
                                        {editForm.qr_cover_banquet_enabled && (
                                            <div className="mt-2">
                                                <label className="mb-1 block text-xs font-medium text-slate-600">Düymənin adı</label>
                                                <input
                                                    type="text"
                                                    maxLength={100}
                                                    value={editForm.qr_cover_banquet_label || ''}
                                                    onChange={(e) => setEditForm({ ...editForm, qr_cover_banquet_label: e.target.value })}
                                                    placeholder="məs. Banket menyu"
                                                    className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm focus:border-sky-400 focus:outline-none"
                                                />
                                                <p className="mt-1 text-[10px] text-slate-500">
                                                    Aktivdirsə: QR cover-də banket düyməsi (→ /banket) VƏ admin panelində "🍽️ Banket menyu" bölməsi görünür. Söndürülsə hər ikisi gizlənir.
                                                </p>
                                            </div>
                                        )}
                                    </div>
                                </div>
                            </section>
                            <section>
                                <SecTitle>Reklam</SecTitle>
                                <div className="space-y-3">
                            {/* QR Reklam ekranı */}
                            <div className="rounded-2xl border border-slate-200 bg-white p-4">
                                <label className="flex items-center justify-between text-sm font-medium text-slate-800">
                                    <span className="flex items-center gap-1.5">
                                        <span>Reklam ekranı (menyudan əvvəl 5 san)</span>
                                    </span>
                                    <input
                                        type="checkbox"
                                        checked={Boolean(editForm.qr_ad_enabled)}
                                        onChange={(e) => setEditForm({ ...editForm, qr_ad_enabled: e.target.checked })}
                                        className="sa-switch"
                                    />
                                </label>
                                {editForm.qr_ad_enabled && (
                                    <div className="mt-3">
                                        <div className="flex items-start gap-3 mb-2">
                                            <div className="h-24 w-14 flex-shrink-0 overflow-hidden rounded-lg border border-slate-200 bg-white flex items-center justify-center">
                                                {editForm.qr_ad_image_url ? (
                                                    <img src={editForm.qr_ad_image_url} alt="" className="h-full w-full object-cover" onError={(e) => { e.target.style.display = 'none'; }} />
                                                ) : (
                                                    <ImageIcon className="h-6 w-6 text-slate-300" />
                                                )}
                                            </div>
                                            <div className="flex flex-wrap gap-2">
                                                <label className="inline-flex cursor-pointer items-center justify-center rounded-lg bg-slate-900 hover:bg-slate-800 px-3 py-1.5 text-xs font-semibold text-white">
                                                    {uploadingAdImage ? 'Yüklənir...' : 'Şəkil yüklə'}
                                                    <input type="file" accept="image/*" className="hidden" onChange={handleAdImageUpload} disabled={uploadingAdImage} />
                                                </label>
                                                {editForm.qr_ad_image_url && (
                                                    <button type="button" onClick={handleAdImageRemove} disabled={uploadingAdImage}
                                                        className="rounded-lg border border-red-200 px-3 py-1.5 text-xs font-semibold text-red-500 hover:bg-red-50 disabled:opacity-50">
                                                        Sil
                                                    </button>
                                                )}
                                            </div>
                                        </div>
                                        <div className="rounded-lg bg-white/70 border border-slate-200 p-2.5 space-y-1">
                                            <p className="text-[11px] font-semibold text-slate-800">Tövsiyə olunan ölçü:</p>
                                            <ul className="text-[10px] text-slate-500 leading-relaxed list-disc pl-4">
                                                <li><strong>1080 × 1920 px</strong> (portrait, 9:16) — mobil üçün ideal</li>
                                                <li>JPG / PNG / WEBP (böyük şəkil avtomatik sıxılır)</li>
                                                <li>Müştəri QR linkini açanda şəkil 5 saniyə göstərilir, sonra "Menyuya keç" düyməsi çıxır</li>
                                            </ul>
                                        </div>
                                    </div>
                                )}
                            </div>
                                </div>
                            </section>
                        </div>
                        )}

                        {editTab === 'business' && (
                        <div className="space-y-7">
                            <section>
                                <SecTitle>Kassa və hesabat</SecTitle>
                                <div className="space-y-3">
                            {/* Kassa başlanğıc qalığı — köhnə proqramdan keçəndə açılış nağd qalığı */}
                            <div className="rounded-2xl border border-slate-200 bg-white p-3">
                                <div className="flex items-center gap-1.5 text-sm font-semibold text-slate-800">
                                    <span>Kassa başlanğıc qalığı (əvvəlki qalıq)</span>
                                </div>
                                <div className="mt-2 flex items-end gap-2">
                                    <div className="flex-1">
                                        <label className="mb-1 block text-xs font-medium text-slate-600">Nağd qalıq (bugün üçün)</label>
                                        <input
                                            type="number"
                                            step="0.01"
                                            inputMode="decimal"
                                            value={kassaOpening}
                                            onChange={(e) => setKassaOpening(e.target.value)}
                                            placeholder="məs. 394"
                                            className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm font-semibold focus:border-sky-400 focus:outline-none"
                                        />
                                    </div>
                                    <button
                                        type="button"
                                        onClick={saveKassaOpening}
                                        disabled={kassaOpeningBusy}
                                        className="rounded-lg bg-slate-900 px-3 py-2 text-sm font-semibold text-white hover:bg-slate-800 disabled:opacity-50"
                                    >
                                        {kassaOpeningBusy ? '...' : 'Təyin et'}
                                    </button>
                                </div>
                                <p className="mt-1 text-[10px] text-slate-500">
                                    Bugünə sabit əvvəlki qalıq yazılır və kassa hesabatı bu gündən başlayır (əvvəlki boş günlər görünməz). Sonrakı günlərə avtomatik keçir.
                                </p>
                            </div>

                            {/* İş günü başlama saatı */}
                            <div className="rounded-2xl border border-slate-200 bg-white p-3">
                                <div className="flex flex-wrap items-center gap-2">
                                    <span className="text-sm font-semibold text-slate-800">İş günü başlama saatı</span>
                                    <select value={editForm.business_day_start_hour ?? 0}
                                        onChange={(e) => setEditForm({ ...editForm, business_day_start_hour: Number(e.target.value) })}
                                        className="rounded-lg border border-slate-200 bg-white px-2 py-1 text-sm focus:outline-none focus:border-sky-400">
                                        {Array.from({ length: 24 }, (_, h) => (<option key={h} value={h}>{String(h).padStart(2, '0')}:00</option>))}
                                    </select>
                                </div>
                                <p className="mt-1 text-[10px] text-slate-500">0:00 = default (gecə yarısı). Məs. 02:00 → kassa/hesabat günü 02:00-da keçir.</p>
                            </div>

                            {/* Manual maya dəyəri */}
                            <label className="flex items-start gap-2 rounded-2xl border border-slate-200 bg-white p-3 text-sm font-semibold text-slate-800 cursor-pointer">
                                <input type="checkbox" checked={Boolean(editForm.manual_cost_enabled)}
                                    onChange={(e) => setEditForm({ ...editForm, manual_cost_enabled: e.target.checked })}
                                    className="sa-switch mt-0.5" />
                                <span>Manual maya (alış) dəyəri<span className="block text-[10px] font-normal text-slate-500">menyuda alış qiyməti + gəlir hesabatında mənfəət</span></span>
                            </label>

                            {/* Ev ödənişləri → ofis */}
                            <label className="flex items-start gap-2 rounded-2xl border border-slate-200 bg-white p-3 text-sm font-semibold text-slate-800 cursor-pointer">
                                <input type="checkbox" checked={Boolean(editForm.rental_auto_ofis)}
                                    onChange={(e) => setEditForm({ ...editForm, rental_auto_ofis: e.target.checked })}
                                    className="sa-switch mt-0.5" />
                                <span>Ev ödənişləri (nağd) avtomatik ofisə köçsün</span>
                            </label>

                            {/* Yarışma çeki */}
                            <div className="rounded-2xl border border-slate-200 bg-white p-3 space-y-2">
                                <label className="flex items-center gap-2 text-sm font-semibold text-slate-800 cursor-pointer">
                                    <input type="checkbox" checked={Boolean(editForm.gift_ticket_enabled)}
                                        onChange={(e) => setEditForm({ ...editForm, gift_ticket_enabled: e.target.checked })}
                                        className="sa-switch" />
                                    Yarışma çeki (müştəri çapı ilə)
                                </label>
                                <div className="flex flex-wrap items-center gap-2">
                                    <span className="text-[11px] text-slate-600">Hər</span>
                                    <input type="number" min="1" step="1" value={editForm.gift_ticket_threshold ?? 100}
                                        disabled={!editForm.gift_ticket_enabled}
                                        onChange={(e) => setEditForm({ ...editForm, gift_ticket_threshold: e.target.value })}
                                        className="w-20 rounded-lg border border-slate-200 bg-white px-2 py-1 text-sm text-right focus:outline-none focus:border-sky-400 disabled:bg-gray-50" />
                                    <span className="text-[11px] text-slate-600">AZN-ə 1 çek (məs. 100 → 250 AZN hesab = 2 çek)</span>
                                </div>
                            </div>
                                </div>
                            </section>
                            <section>
                                <SecTitle>Funksiyalar</SecTitle>
                                <div className="space-y-3">
                            {/* Saatlıq / tarif menyu (PS5, bilyard) */}
                            <div className="rounded-2xl border border-slate-200 bg-white p-4">
                                <label className="flex cursor-pointer items-center justify-between gap-2 text-sm font-semibold text-slate-800">
                                    <span className="flex items-center gap-1.5"><span>Saatlıq / tarif menyu (PS5, bilyard)</span></span>
                                    <input
                                        type="checkbox"
                                        checked={Boolean(editForm.hourly_menu_enabled)}
                                        onChange={(e) => setEditForm({ ...editForm, hourly_menu_enabled: e.target.checked })}
                                        className="sa-switch"
                                    />
                                </label>
                                <p className="mt-1 text-[10px] text-slate-500">
                                    Aktivdirsə, menyuda "Saatlıq" məhsul yaratmaq olur (qiymət = AZN/saat). Sifarişə əlavə olunanda sayğac başlayır, hesab keçən vaxta görə hesablanır.
                                </p>
                            </div>
                            {/* Fast food rejimi — McDonald's kimi gündəlik sıra nömrəsi */}
                            <div className="rounded-2xl border border-slate-200 bg-white p-4">
                                <label className="flex cursor-pointer items-center justify-between gap-2 text-sm font-semibold text-slate-800">
                                    <span className="flex items-center gap-1.5"><span>Fast food rejimi (gündəlik sıra nömrəsi)</span></span>
                                    <input
                                        type="checkbox"
                                        checked={Boolean(editForm.fast_food_enabled)}
                                        onChange={(e) => setEditForm({ ...editForm, fast_food_enabled: e.target.checked })}
                                        className="sa-switch"
                                    />
                                </label>
                                <p className="mt-1 text-[10px] text-slate-500">
                                    Aktivdirsə, hər sifarişə gündəlik sıra nömrəsi verilir (hər gün 100-dən başlayır). Müştəri çekində və mətbəx ekranında böyük göstərilir (McDonald's kimi). Məs. 150 = bu gün 51-ci müştəri.
                                </p>
                            </div>
                            {/* Sadə plan menyu limiti (50) ləğvi */}
                            <div className="rounded-2xl border border-slate-200 bg-white p-4">
                                <label className="flex cursor-pointer items-center justify-between gap-2 text-sm font-semibold text-slate-800">
                                    <span className="flex items-center gap-1.5"><span>Menyu limitsiz (Sadə paketdə 50 limitini ləğv et)</span></span>
                                    <input
                                        type="checkbox"
                                        checked={Boolean(editForm.menu_unlimited)}
                                        onChange={(e) => setEditForm({ ...editForm, menu_unlimited: e.target.checked })}
                                        className="sa-switch"
                                    />
                                </label>
                                <p className="mt-1 text-[10px] text-slate-500">
                                    Aktivdirsə, Sadə paketli bu restoran limitsiz menyu məhsulu əlavə edə bilər (50 həddi tətbiq olunmur).
                                </p>
                            </div>
                            {/* Offline rejim — internet gedəndə lokal işləmə (TEST mərhələsi) */}
                            <div className="rounded-2xl border border-slate-200 bg-white p-4">
                                <label className="flex cursor-pointer items-center justify-between gap-2 text-sm font-semibold text-slate-800">
                                    <span className="flex items-center gap-1.5"><span>Offline rejim (internet gedəndə lokal işləmə)</span></span>
                                    <input
                                        type="checkbox"
                                        checked={Boolean(editForm.offline_mode_enabled)}
                                        onChange={(e) => setEditForm({ ...editForm, offline_mode_enabled: e.target.checked })}
                                        className="sa-switch"
                                    />
                                </label>
                                <p className="mt-1 text-[10px] text-slate-500">
                                    TEST mərhələsi. Aktivdirsə, internet gedəndə app lokal işləməyə davam edir (lisenziya güzəşti, sonra sifariş/çap offline). Default: sönülü. Yalnız test restoranında açın.
                                </p>
                            </div>
                            {/* Food delivery ödəniş növü — yalnız PRO paket */}
                            <div className={`rounded-2xl border border-slate-200 bg-white p-4 ${editForm.plan !== 'pro' ? 'opacity-60' : ''}`}>
                                <label className="flex cursor-pointer items-center justify-between gap-2 text-sm font-semibold text-slate-800">
                                    <span className="flex items-center gap-1.5"><span>🛵 Food delivery ödəniş növü</span></span>
                                    <input
                                        type="checkbox"
                                        checked={Boolean(editForm.food_delivery_enabled)}
                                        onChange={(e) => setEditForm({ ...editForm, food_delivery_enabled: e.target.checked })}
                                        className="sa-switch"
                                    />
                                </label>
                                <p className="mt-1 text-[10px] text-slate-500">
                                    Hesabı bağlayanda Nağd, Kart və Hesab böl yanında «Food delivery» düyməsi çıxır. Kassa hesabatında ayrıca sütundur, kassa balansına düşmür. Fiskalda nağdsız kimi vurulur, POS terminal kartı çəkmir.
                                    {editForm.plan !== 'pro' && <b className="text-amber-600"> Yalnız Pro paketdə işləyir.</b>}
                                </p>
                                {Boolean(editForm.food_delivery_enabled) && (
                                    <div className="mt-3">
                                        <div className="mb-1.5 text-[11px] font-semibold text-slate-600">Kimlər görsün?</div>
                                        <div className="flex flex-wrap gap-2">
                                            {[['admin', 'Admin'], ['kassir', 'Kassir'], ['administrator', 'Menecer'], ['waiter', 'Ofisiant']].map(([key, label]) => {
                                                const roles = editForm.food_delivery_roles || [];
                                                const on = roles.includes(key);
                                                return (
                                                    <label key={key} className={`flex cursor-pointer items-center gap-1.5 rounded-xl border px-3 py-1.5 text-xs font-semibold ${on ? 'border-orange-300 bg-orange-50 text-orange-700' : 'border-slate-200 text-slate-500'}`}>
                                                        <input
                                                            type="checkbox"
                                                            checked={on}
                                                            onChange={(e) => setEditForm({
                                                                ...editForm,
                                                                food_delivery_roles: e.target.checked ? [...roles.filter((r) => r !== key), key] : roles.filter((r) => r !== key),
                                                            })}
                                                            className="h-3.5 w-3.5"
                                                        />
                                                        {label}
                                                    </label>
                                                );
                                            })}
                                        </div>
                                        <label className="mt-3 flex cursor-pointer items-start gap-2 rounded-xl border border-slate-200 px-3 py-2 text-xs font-semibold text-slate-700">
                                            <input
                                                type="checkbox"
                                                checked={Boolean(editForm.food_delivery_rooms)}
                                                onChange={(e) => setEditForm({ ...editForm, food_delivery_rooms: e.target.checked })}
                                                className="mt-0.5 h-3.5 w-3.5"
                                            />
                                            <span>
                                                Otaqlara bağla
                                                <span className="block text-[10px] font-normal text-slate-500">
                                                    Admin «Otağı redaktə et» pəncərəsində otağı «Yalnız Food delivery» edə bilər. O otağın masalarında ödəniş yalnız Food delivery olur.
                                                </span>
                                            </span>
                                        </label>
                                    </div>
                                )}
                            </div>
                            {/* Zal planı (masaların forması və yeri) — yalnız PRO paket */}
                            <div className={`rounded-2xl border border-slate-200 bg-white p-4 ${editForm.plan !== 'pro' ? 'opacity-60' : ''}`}>
                                <label className="flex cursor-pointer items-center justify-between gap-2 text-sm font-semibold text-slate-800">
                                    <span className="flex items-center gap-1.5"><span>🗺️ Zal planı</span></span>
                                    <input
                                        type="checkbox"
                                        checked={Boolean(editForm.floor_plan_enabled)}
                                        onChange={(e) => setEditForm({ ...editForm, floor_plan_enabled: e.target.checked })}
                                        className="sa-switch"
                                    />
                                </label>
                                <p className="mt-1 text-[10px] text-slate-500">
                                    Admin masaların formasını, ölçüsünü və yerini özü dizayn edir. Ofisiant zalı həmin planda görür. Söndürülsə köhnə kart görünüşü qayıdır, plan silinmir.
                                    {editForm.plan !== 'pro' && <b className="text-amber-600"> Yalnız Pro paketdə işləyir.</b>}
                                </p>
                            </div>
                                </div>
                            </section>
                        </div>
                        )}

                        {editTab === 'print' && (
                        <div className="space-y-7">
                            <section>
                                <SecTitle>Çap</SecTitle>
                                <div className="space-y-3">
                            {/* Webdən çap (LAN) — mobil/tablet/brauzer PC-dən çap edir */}
                            <div className="rounded-2xl border border-slate-200 bg-white p-4">
                                <label className="flex cursor-pointer items-center justify-between gap-2 text-sm font-semibold text-slate-800">
                                    <span className="flex items-center gap-1.5"><span>Webdən çap (LAN): mobil/tablet PC-dən çap etsin</span></span>
                                    <input
                                        type="checkbox"
                                        checked={Boolean(editForm.web_print_enabled)}
                                        onChange={(e) => setEditForm({ ...editForm, web_print_enabled: e.target.checked })}
                                        className="sa-switch"
                                    />
                                </label>
                                {editForm.web_print_enabled && (
                                    <div className="mt-2 space-y-2">
                                        <div>
                                            <label className="mb-1 block text-xs font-medium text-slate-600">PC-nin LAN IP-si</label>
                                            <input
                                                type="text"
                                                inputMode="decimal"
                                                autoComplete="off"
                                                value={editForm.pc_print_ip || ''}
                                                onChange={(e) => setEditForm({ ...editForm, pc_print_ip: e.target.value })}
                                                placeholder="məs. 192.168.1.50"
                                                className="w-full rounded-lg border border-gray-200 px-3 py-2 font-mono text-sm focus:border-sky-400 focus:outline-none"
                                            />
                                        </div>
                                        <p className="text-[10px] text-slate-500">
                                            PC-də Fiscal POS proqramı açıq olmalıdır. Adətən IP-ni desktop proqram özü avtomatik yazır — boşdursa yuxarıda əl ilə yaz (Windows-da <b>ipconfig</b> → IPv4). Aktivdirsə mobil/tablet/brauzer bu IP-yə (:6795) çap göndərir, PC çap edir.
                                        </p>
                                    </div>
                                )}
                                <label className="mt-2 flex cursor-pointer items-center justify-between gap-2 border-t border-orange-200/60 pt-2 text-sm font-semibold text-slate-800">
                                    <span className="flex items-center gap-1.5"><span>Brauzerin öz çap dialoqu (C#/LAN yoxdursa)</span></span>
                                    <input
                                        type="checkbox"
                                        checked={Boolean(editForm.web_native_print_enabled)}
                                        onChange={(e) => setEditForm({ ...editForm, web_native_print_enabled: e.target.checked })}
                                        className="sa-switch"
                                    />
                                </label>
                                {editForm.web_native_print_enabled && (
                                    <p className="mt-1 text-[10px] text-slate-500">
                                        Desktop proqram (C#) və LAN PC tapılmayanda brauzerin adi çap pəncərəsi açılır — müştəri printeri özü seçib çap edir (istənilən USB/şəbəkə printeri, PDF).
                                    </p>
                                )}
                            </div>
                            {/* Müştəri çeki iri font */}
                            <div className="rounded-2xl border border-slate-200 bg-white p-4">
                                <label className="flex cursor-pointer items-center justify-between gap-2 text-sm font-semibold text-slate-800">
                                    <span className="flex items-center gap-1.5"><span>Müştəri çeki iri font (oxunaqlı)</span></span>
                                    <input
                                        type="checkbox"
                                        checked={Boolean(editForm.receipt_big_font)}
                                        onChange={(e) => setEditForm({ ...editForm, receipt_big_font: e.target.checked })}
                                        className="sa-switch"
                                    />
                                </label>
                                <p className="mt-1 text-[10px] text-slate-500">
                                    Aktivdirsə, müştəri çeki daha iri fontla çap olunur (masa, məhsullar və cəmlər daha böyük görünür).
                                </p>
                            </div>
                            {/* Mətbəx çeki iri font */}
                            <div className="rounded-2xl border border-slate-200 bg-white p-4">
                                <label className="flex cursor-pointer items-center justify-between gap-2 text-sm font-semibold text-slate-800">
                                    <span className="flex items-center gap-1.5"><span>Mətbəx çeki iri font (oxunaqlı)</span></span>
                                    <input
                                        type="checkbox"
                                        checked={Boolean(editForm.kitchen_big_font)}
                                        onChange={(e) => setEditForm({ ...editForm, kitchen_big_font: e.target.checked })}
                                        className="sa-switch"
                                    />
                                </label>
                                <p className="mt-1 text-[10px] text-slate-500">
                                    Aktivdirsə, mətbəxlərə gedən çeklər daha iri fontla çap olunur (məhsullar və qeydlər daha böyük görünür).
                                </p>
                            </div>
                            {/* Mətbəx çekində miqdar "X" formatı */}
                            <div className="rounded-2xl border border-slate-200 bg-white p-4">
                                <label className="flex cursor-pointer items-center justify-between gap-2 text-sm font-semibold text-slate-800">
                                    <span className="flex items-center gap-1.5"><span>Mətbəx çekində miqdar "X" kimi</span></span>
                                    <input
                                        type="checkbox"
                                        checked={Boolean(editForm.kitchen_qty_x)}
                                        onChange={(e) => setEditForm({ ...editForm, kitchen_qty_x: e.target.checked })}
                                        className="sa-switch"
                                    />
                                </label>
                                <p className="mt-1 text-[10px] text-slate-500">
                                    Aktivdirsə, mətbəx çekində ədəd "əd" yerinə "X" yazılır (məs. Kabab 1X, Cola 2X). Default: "əd".
                                </p>
                            </div>
                                </div>
                            </section>
                            <section>
                                <SecTitle>Mətbəx ekranı</SecTitle>
                                <div className="space-y-3">
                            {/* Mətbəx ekranı görünüş növü */}
                            <div className="rounded-2xl border border-slate-200 bg-white p-4">
                                <p className="mb-2 flex items-center gap-1.5 text-sm font-bold text-slate-800">
                                    <span>Mətbəx ekranı görünüşü</span>
                                </p>
                                <div className="grid grid-cols-2 gap-2">
                                    {[
                                        { key: 'v1', title: 'V1 · İnteqrasiya', desc: 'Böyük kartlar, status düymələri (Gözləyir/Hazırlanır/Hazır), toxunaraq açılır.' },
                                        { key: 'v2', title: 'V2 · TV rejimi', desc: 'Kiçik kartlar, hamısı açıq, məhsullar görünür. Düymə yoxdur — sırf göstəriş.' },
                                    ].map((opt) => {
                                        const active = (editForm.kitchen_display_mode || 'v1') === opt.key;
                                        return (
                                            <button
                                                key={opt.key}
                                                type="button"
                                                onClick={() => setEditForm({ ...editForm, kitchen_display_mode: opt.key })}
                                                className={`rounded-lg border-2 p-3 text-left transition-all ${active ? 'border-[#0090bb] bg-sky-50/50 shadow-sm' : 'border-slate-200 bg-white hover:border-slate-300'}`}
                                            >
                                                <div className={`text-sm font-bold ${active ? 'text-slate-600' : 'text-gray-600'}`}>{active ? '✓ ' : ''}{opt.title}</div>
                                                <div className="mt-1 text-[10px] leading-snug text-gray-500">{opt.desc}</div>
                                            </button>
                                        );
                                    })}
                                </div>
                            </div>
                            {/* Zala qayıdanda mətbəxə göndərməzdən əvvəl soruş */}
                            <div className="rounded-2xl border border-slate-200 bg-white p-4">
                                <label className="flex cursor-pointer items-center justify-between gap-2 text-sm font-semibold text-slate-800">
                                    <span className="flex items-center gap-1.5"><span>Zala qayıdanda mətbəxə göndərməzdən əvvəl soruş</span></span>
                                    <input
                                        type="checkbox"
                                        checked={Boolean(editForm.kitchen_confirm_on_exit)}
                                        onChange={(e) => setEditForm({ ...editForm, kitchen_confirm_on_exit: e.target.checked })}
                                        className="sa-switch"
                                    />
                                </label>
                                <p className="mt-1 text-[10px] text-slate-500">
                                    Aktivdirsə, ofisiant "← Zal" vuranda çap olunmamış məhsullar varsa popup çıxır (göndər / göndərmə). Bağlıdırsa, köhnə davranış — Zala vuran kimi avtomatik göndərilir.
                                </p>
                            </div>
                                </div>
                            </section>
                        </div>
                        )}

                        {editTab === 'staff' && (
                        <div className="space-y-3">
                            {/* Kassir ofisiant idarəsi */}
                            <label className="flex items-start gap-2 rounded-2xl border border-slate-200 bg-white p-3 text-sm font-semibold text-slate-800 cursor-pointer">
                                <input type="checkbox" checked={Boolean(editForm.kassir_manage_waiters)}
                                    onChange={(e) => setEditForm({ ...editForm, kassir_manage_waiters: e.target.checked })}
                                    className="sa-switch mt-0.5" />
                                <span>Kassir ofisiant əlavə/silə bilsin <span className="block text-[10px] font-normal text-slate-500">(yalnız maaşı 0 olanı)</span></span>
                            </label>
                            {editForm.kassir_manage_waiters && (
                                <div className="rounded-xl border border-slate-200 bg-white p-3 grid grid-cols-1 gap-2 sm:grid-cols-2">
                                    <div className="sm:col-span-2 text-[11px] font-semibold text-slate-500">Kassir əlavə etdiyi ofisiant üçün default ödəniş:</div>
                                    <select value={editForm.default_waiter_payment_type || 'service_pct'}
                                        onChange={(e) => setEditForm({ ...editForm, default_waiter_payment_type: e.target.value })}
                                        className="rounded-lg border border-slate-200 px-2 py-1.5 text-xs">
                                        <option value="service_pct">Faiz (%)</option>
                                        <option value="salary">Maaş (₼)</option>
                                    </select>
                                    {editForm.default_waiter_payment_type === 'salary' ? (
                                        <input type="number" min="0" step="0.01" value={editForm.default_waiter_salary ?? 0}
                                            onChange={(e) => setEditForm({ ...editForm, default_waiter_salary: e.target.value })}
                                            placeholder="Maaş ₼" className="rounded-lg border border-slate-200 px-2 py-1.5 text-xs font-mono" />
                                    ) : (
                                        <input type="number" min="0" step="0.01" value={editForm.default_waiter_service_pct ?? 0}
                                            onChange={(e) => setEditForm({ ...editForm, default_waiter_service_pct: e.target.value })}
                                            placeholder="Faiz %" className="rounded-lg border border-slate-200 px-2 py-1.5 text-xs font-mono" />
                                    )}
                                </div>
                            )}

                            {/* Cərimə → ofis */}
                            <label className="flex items-start gap-2 rounded-2xl border border-slate-200 bg-white p-3 text-sm font-semibold text-slate-800 cursor-pointer">
                                <input type="checkbox" checked={Boolean(editForm.fine_to_ofis)}
                                    onChange={(e) => setEditForm({ ...editForm, fine_to_ofis: e.target.checked })}
                                    className="sa-switch mt-0.5" />
                                <span>İşçi cəriməsi ofisə köçsün</span>
                            </label>
                        </div>
                        )}

                        {editTab === 'waiter_perms' && (
                        <div className="space-y-3">
                            <p className="text-xs text-gray-400">Ofisiant (waiter) hansı əməliyyatları edə bilsin — tik qoyulmayıbsa edə bilməz.</p>

                            {/* 1) Əlavə menyu (manual) */}
                            <label className="flex items-start gap-2 rounded-2xl border border-slate-200 bg-white p-3 text-sm font-semibold text-slate-800 cursor-pointer">
                                <input type="checkbox" checked={Boolean(editForm.waiter_add_custom_item)}
                                    onChange={(e) => setEditForm({ ...editForm, waiter_add_custom_item: e.target.checked })}
                                    className="sa-switch mt-0.5" />
                                <span>Əlavə menyu (manual) yaza bilsin<span className="block text-[10px] font-normal text-slate-500">Menyuda olmayan məhsulu əl ilə əlavə etmək</span></span>
                            </label>

                            {/* 2) Sifariş silmə qadağası */}
                            <label className="flex items-start gap-2 rounded-2xl border border-slate-200 bg-white p-3 text-sm font-semibold text-slate-800 cursor-pointer">
                                <input type="checkbox" checked={Boolean(editForm.waiter_no_delete)}
                                    onChange={(e) => setEditForm({ ...editForm, waiter_no_delete: e.target.checked })}
                                    className="sa-switch mt-0.5" />
                                <span>Ofisiant sifarişi SİLƏ/AZALDA bilməsin<span className="block text-[10px] font-normal text-slate-500">Vurulan məhsulu silmək/sayını azaltmaq qadağandır (mətbəxə getsin-getməsin). Yalnız admin/kassir edə bilər.</span></span>
                            </label>

                            {/* 3) Kassir PIN ilə girəndə silmə qadağası */}
                            <label className="flex items-start gap-2 rounded-2xl border border-slate-200 bg-white p-3 text-sm font-semibold text-slate-800 cursor-pointer">
                                <input type="checkbox" checked={Boolean(editForm.kassir_pin_no_delete)}
                                    onChange={(e) => setEditForm({ ...editForm, kassir_pin_no_delete: e.target.checked })}
                                    className="sa-switch mt-0.5" />
                                <span>Kassir PIN ilə girəndə SİLƏ/AZALDA bilməsin<span className="block text-[10px] font-normal text-slate-500">Kassir PIN ilə girəndə adi ofisiant kimi olur (bütün masaları görür, sifariş əlavə edir) amma silmək/azaltmaq qadağandır. Parol ilə girəndə tam icazə.</span></span>
                            </label>

                            {/* 3b) BAŞ ADMIN silmə qadağası (admin PIN/şifrə) */}
                            <label className="flex items-start gap-2 rounded-2xl border border-slate-200 bg-white p-3 text-sm font-semibold text-slate-800 cursor-pointer">
                                <input type="checkbox" checked={Boolean(editForm.admin_no_delete)}
                                    onChange={(e) => setEditForm({ ...editForm, admin_no_delete: e.target.checked })}
                                    className="sa-switch mt-0.5" />
                                <span>Baş admin da SİLƏ/AZALDA bilməsin<span className="block text-[10px] font-normal text-slate-500">Tikilsə — admin PIN (məs. 9222) və admin şifrəsi ilə girəndə də məhsul silmək/azaltmaq (X ilə silmək daxil) qadağandır. Adətən admin tam icazəlidir; bu tik onu da bağlayır.</span></span>
                            </label>

                            {/* 4) Yalnız ofisiant PIN girişi */}
                            <label className="flex items-start gap-2 rounded-2xl border border-slate-200 bg-white p-3 text-sm font-semibold text-slate-800 cursor-pointer">
                                <input type="checkbox" checked={Boolean(editForm.pin_only_login)}
                                    onChange={(e) => setEditForm({ ...editForm, pin_only_login: e.target.checked })}
                                    className="sa-switch mt-0.5" />
                                <span>Yalnız ofisiant PIN girişi<span className="block text-[10px] font-normal text-slate-500">Login səhifəsində yalnız "Ofisiant (PIN)" qalır. Admin/işçi/mətbəx (parol) girişi həm gizlənir, həm backend-də bloklanır.</span></span>
                            </label>

                            {/* 5) Kassir anbar "Stok idarəsi" baxışı (yalnız Pro / warehouse) */}
                            <label className="flex items-start gap-2 rounded-2xl border border-slate-200 bg-white p-3 text-sm font-semibold text-slate-800 cursor-pointer">
                                <input type="checkbox" checked={Boolean(editForm.kassir_stock_view)}
                                    onChange={(e) => setEditForm({ ...editForm, kassir_stock_view: e.target.checked })}
                                    className="sa-switch mt-0.5" />
                                <span>Kassir "Stok idarəsi"ni görə bilsin (yalnız baxış)<span className="block text-[10px] font-normal text-slate-500">Kassir menyusunda "Stok idarəsi" düyməsi çıxır, anbarı BAXA bilir amma heç bir dəyişiklik edə bilmir. Yalnız Pro paket (anbar) aktivdirsə işləyir.</span></span>
                            </label>

                            {/* Sifarişi silib/azalda bilən rollar (Administrator / Kassir) */}
                            <div className="rounded-2xl border border-slate-200 bg-white p-3">
                                <p className="mb-2 text-sm font-semibold text-slate-800">Sifarişi silib/azalda bilən rollar</p>
                                <label className="flex items-start gap-2 cursor-pointer mb-2">
                                    <input type="checkbox" checked={Boolean(editForm.administrator_can_delete)}
                                        onChange={(e) => setEditForm({ ...editForm, administrator_can_delete: e.target.checked })}
                                        className="sa-switch mt-0.5" />
                                    <span className="text-sm font-semibold text-slate-800">Administrator<span className="block text-[10px] font-normal text-slate-500">Waiter (masalar) panelindən sifarişi silib/azalda bilər. Söndürülsə yalnız əlavə edə bilər.</span></span>
                                </label>
                                <label className="flex items-start gap-2 cursor-pointer">
                                    <input type="checkbox" checked={Boolean(editForm.kassir_can_delete)}
                                        onChange={(e) => setEditForm({ ...editForm, kassir_can_delete: e.target.checked })}
                                        className="sa-switch mt-0.5" />
                                    <span className="text-sm font-semibold text-slate-800">Kassir<span className="block text-[10px] font-normal text-slate-500">Öz panelindən (əvvəlki qayda) sifarişi silib/azalda bilər. Söndürülsə silə/azalda bilməz. (PIN girişində əlavə "kassir PIN silə bilməsin" tənzimləməsi də tətbiq olunur.)</span></span>
                                </label>
                            </div>

                            {/* Sifarişə əlavə edə/artıra bilən rollar — söndürülsə «yalnız baxış» */}
                            <div className="rounded-2xl border border-slate-200 bg-white p-3">
                                <p className="mb-2 text-sm font-semibold text-slate-800">Sifarişə məhsul əlavə edə/artıra bilən rollar</p>
                                <label className="flex items-start gap-2 cursor-pointer mb-2">
                                    <input type="checkbox" checked={Boolean(editForm.administrator_can_add)}
                                        onChange={(e) => setEditForm({ ...editForm, administrator_can_add: e.target.checked })}
                                        className="sa-switch mt-0.5" />
                                    <span className="text-sm font-semibold text-slate-800">Administrator<span className="block text-[10px] font-normal text-slate-500">Söndürülsə waiter sistemində yalnız baxış: masaları və sifarişləri görür, amma məhsul vura, artıra, silə, köçürə bilmir. Hesab bağlamaq və çap açıqdır.</span></span>
                                </label>
                                <label className="flex items-start gap-2 cursor-pointer">
                                    <input type="checkbox" checked={Boolean(editForm.kassir_can_add)}
                                        onChange={(e) => setEditForm({ ...editForm, kassir_can_add: e.target.checked })}
                                        className="sa-switch mt-0.5" />
                                    <span className="text-sm font-semibold text-slate-800">Kassir<span className="block text-[10px] font-normal text-slate-500">Söndürülsə kassir PIN/şifrə ilə waiter sisteminə girəndə yalnız baxış. Kassirin öz kassa paneli dəyişmir. Növbəti girişdən etibarən işləyir.</span></span>
                                </label>
                            </div>

                            {/* Zal ekranından Fiskal Kassa idarəsi — admin / ofisiant üçün ayrıca */}
                            <div className="rounded-2xl border border-slate-200 bg-white p-4">
                                <p className="mb-2 flex items-center gap-1.5 text-sm font-bold text-slate-800">
                                    <span>Zal ekranından Fiskal Kassa idarəsi</span>
                                </p>
                                <label className="flex cursor-pointer items-center justify-between gap-2 text-sm font-semibold text-slate-800">
                                    <span className="flex items-center gap-1.5"><span>Admin idarə edə bilsin</span></span>
                                    <input
                                        type="checkbox"
                                        checked={Boolean(editForm.fiscal_waiter_admin)}
                                        onChange={(e) => setEditForm({ ...editForm, fiscal_waiter_admin: e.target.checked })}
                                        className="sa-switch"
                                    />
                                </label>
                                <label className="mt-2 flex cursor-pointer items-center justify-between gap-2 text-sm font-semibold text-slate-800">
                                    <span className="flex items-center gap-1.5"><span>Ofisiant idarə edə bilsin</span></span>
                                    <input
                                        type="checkbox"
                                        checked={Boolean(editForm.fiscal_waiter_ofisiant)}
                                        onChange={(e) => setEditForm({ ...editForm, fiscal_waiter_ofisiant: e.target.checked })}
                                        className="sa-switch"
                                    />
                                </label>
                                <p className="mt-1 text-[10px] text-slate-500">
                                    Seçilən rol Zal (ofisiant) ekranında "Fiskal Kassa" düyməsini görür — shift aç/bağla, X/Z hesabat, son qaimə. Hər ikisi seçilsə həm admin, həm ofisiant idarə edə bilər.
                                </p>
                            </div>
                        </div>
                        )}

                        {editTab === 'notify' && (
                        <div className="space-y-7">
                            <section>
                                <SecTitle>WhatsApp</SecTitle>
                                <div className="space-y-3">
                            {/* ── WhatsApp bildiriş ── */}
                            <div className="rounded-2xl border border-slate-200 bg-white p-4">
                                <label className="flex items-center justify-between text-sm font-medium text-slate-800">
                                    <span className="flex items-center gap-1.5"><span>WhatsApp bildiriş</span></span>
                                    <input
                                        type="checkbox"
                                        checked={Boolean(editForm.whatsapp_enabled)}
                                        onChange={(e) => setEditForm({ ...editForm, whatsapp_enabled: e.target.checked })}
                                        className="sa-switch"
                                    />
                                </label>
                                {editForm.whatsapp_enabled && (
                                    <div className="mt-3 space-y-2.5">
                                        <div>
                                            <label className="mb-1 block text-xs font-medium text-slate-600">WhatsApp API açarı</label>
                                            <input
                                                type="text"
                                                value={editForm.whatsapp_api_key || ''}
                                                onChange={(e) => setEditForm({ ...editForm, whatsapp_api_key: e.target.value })}
                                                placeholder="fiscalwhatsapp api açarı"
                                                className="w-full rounded-lg border border-slate-200 bg-white px-2.5 py-1.5 font-mono text-xs focus:outline-none focus:border-sky-400"
                                            />
                                        </div>
                                        <div>
                                            <label className="mb-1 block text-xs font-medium text-slate-600">Alıcı nömrələr (ofis + günlük üçün)</label>
                                            <textarea
                                                rows={2}
                                                value={editForm.whatsapp_recipients || ''}
                                                onChange={(e) => setEditForm({ ...editForm, whatsapp_recipients: e.target.value })}
                                                placeholder="0994111199, 0504111199"
                                                className="w-full rounded-lg border border-slate-200 bg-white px-2.5 py-1.5 font-mono text-xs focus:outline-none focus:border-sky-400"
                                            />
                                            <p className="mt-1 text-[10px] text-slate-500">Vergül və ya sətirlə ayır. 994 formatına avtomatik çevrilir.</p>
                                        </div>
                                        <div className="flex items-end gap-2 rounded-lg border border-slate-200 bg-white p-2.5">
                                            <div className="flex-1">
                                                <label className="mb-1 block text-xs font-medium text-slate-600">Test nömrəsi</label>
                                                <input
                                                    type="text"
                                                    value={waTestNum}
                                                    onChange={(e) => setWaTestNum(e.target.value)}
                                                    placeholder="0994111199"
                                                    className="w-full rounded-lg border border-slate-200 px-2.5 py-1.5 font-mono text-xs focus:outline-none focus:border-sky-400"
                                                />
                                            </div>
                                            <button
                                                type="button"
                                                onClick={sendWhatsappTest}
                                                disabled={waTesting}
                                                className="rounded-lg bg-slate-900 px-3 py-1.5 text-xs font-bold text-white hover:bg-slate-800 disabled:opacity-50"
                                            >
                                                {waTesting ? 'Göndərilir...' : 'Test göndər'}
                                            </button>
                                        </div>
                                    </div>
                                )}
                            </div>
                            {/* ── Tam Kassa hesabatı (WhatsApp) — ayrıca toggle + nömrələr + saat ── */}
                            {editForm.whatsapp_enabled && (
                                <div className="rounded-2xl border border-slate-200 bg-white p-4">
                                    <label className="flex items-start gap-2 cursor-pointer">
                                        <input type="checkbox" checked={Boolean(editForm.kassa_report_enabled)}
                                            onChange={(e) => setEditForm({ ...editForm, kassa_report_enabled: e.target.checked })}
                                            className="sa-switch mt-0.5" />
                                        <span className="text-sm font-semibold text-slate-800">Tam Kassa hesabatını WhatsApp-a göndər<span className="block text-[10px] font-normal text-slate-500">Kassa səhifəsindəki tam breakdown (əvvələ qalıq, restoran/servis/xidmət/satış nağd-POS, bazarlıq, xərclər, maaş, ofis, sona qalıq) hər gün göndərilir.</span></span>
                                    </label>
                                    {editForm.kassa_report_enabled && (
                                        <>
                                            <div className="mt-3">
                                                <label className="mb-1 block text-xs font-medium text-slate-600">Alıcı nömrələr (kassa hesabatı üçün)</label>
                                                <textarea rows={2}
                                                    value={editForm.kassa_report_recipients || ''}
                                                    onChange={(e) => setEditForm({ ...editForm, kassa_report_recipients: e.target.value })}
                                                    placeholder="994501234567, 994551234567 (vergül və ya sətir ilə)"
                                                    className="w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm focus:outline-none focus:border-sky-400" />
                                            </div>
                                            <div className="mt-2 flex items-center justify-between gap-2">
                                                <label className="text-xs font-semibold text-slate-800">Kassa hesabatı vaxtı (Baku)</label>
                                                <input
                                                    type="time"
                                                    value={`${String(Number(editForm.kassa_report_hour || 0)).padStart(2, '0')}:${String(Number(editForm.kassa_report_minute || 0)).padStart(2, '0')}`}
                                                    onChange={(e) => {
                                                        const [h, m] = String(e.target.value || '23:00').split(':').map(Number);
                                                        setEditForm({ ...editForm, kassa_report_hour: h || 0, kassa_report_minute: m || 0 });
                                                    }}
                                                    className="rounded-lg border border-slate-200 bg-white px-3 py-1.5 text-sm font-bold text-slate-800 focus:outline-none focus:border-sky-400" />
                                            </div>
                                            <p className="mt-1.5 text-[10px] text-slate-500">WhatsApp aktiv + açar olmalıdır. Bu hesabat yuxarıdakı bildiriş kanalından ASILI DEYİL — həmişə WhatsApp ilə, yuxarıdakı nömrələrə gedir.</p>
                                        </>
                                    )}
                                </div>
                            )}
                            {/* ── WhatsApp OTP (admin girişi 2FA) ── */}
                            <div className="rounded-2xl border border-slate-200 bg-white p-3">
                                <label className="flex items-center justify-between">
                                    <span className="flex items-center gap-1.5"><span>Admin girişində WhatsApp OTP</span></span>
                                    <input type="checkbox"
                                        checked={Boolean(editForm.otp_login_enabled)}
                                        onChange={(e) => setEditForm({ ...editForm, otp_login_enabled: e.target.checked })}
                                        className="sa-switch" />
                                </label>
                                {editForm.otp_login_enabled && (
                                    <div className="mt-2 space-y-2">
                                        <div>
                                            <label className="mb-1 block text-xs font-medium text-slate-600">OTP nömrələri</label>
                                            <input
                                                type="text"
                                                value={editForm.otp_numbers || ''}
                                                onChange={(e) => setEditForm({ ...editForm, otp_numbers: e.target.value })}
                                                placeholder="0994111199, 0504111199"
                                                className="w-full rounded-lg border border-slate-200 bg-white px-2.5 py-1.5 font-mono text-xs focus:outline-none focus:border-sky-400"
                                            />
                                            <p className="mt-1 text-[10px] text-slate-500">
                                                Vergüllə ayır. Admin parol VƏ admin PIN girişində bu nömrələrə 6 rəqəmli kod gedir.
                                                Kod tenantın öz WhatsApp açarı ilə göndərilir — <b>WhatsApp bildiriş aktiv + açar olmalıdır</b>.
                                            </p>
                                        </div>
                                        {Number(editForm.otp_login_blocked) === 1 && (
                                            <div className="flex items-center justify-between rounded-lg border border-slate-200 bg-rose-50 px-3 py-2">
                                                <span className="text-xs font-semibold text-rose-700">Giriş bloklanıb (3 dəfə səhv kod)</span>
                                                <button type="button"
                                                    onClick={async () => {
                                                        try {
                                                            await api.post(`/superadmin/tenants/${selectedTenant.id}/otp-unblock`);
                                                            setEditForm({ ...editForm, otp_login_blocked: 0 });
                                                            toast.success('Blok açıldı.');
                                                        } catch { toast.error('Alınmadı.'); }
                                                    }}
                                                    className="rounded-lg bg-rose-600 px-3 py-1.5 text-xs font-bold text-white hover:bg-rose-700">
                                                    Blokdan çıxar
                                                </button>
                                            </div>
                                        )}
                                    </div>
                                )}
                            </div>
                                </div>
                            </section>
                            <section>
                                <SecTitle>iOS push</SecTitle>
                                <div className="space-y-3">
                            <div className="rounded-2xl border border-slate-200 bg-white p-4">
                                <label className="flex items-center justify-between text-sm font-medium text-slate-800">
                                    <span className="flex items-center gap-1.5">
                                        <span>iOS Push bildiriş (APNs)</span>
                                    </span>
                                    <input
                                        type="checkbox"
                                        checked={Boolean(editForm.apns_enabled)}
                                        onChange={(e) => setEditForm({ ...editForm, apns_enabled: e.target.checked })}
                                        className="sa-switch"
                                    />
                                </label>
                                {editForm.apns_enabled && (
                                    <div className="mt-3 grid grid-cols-1 gap-2.5 sm:grid-cols-2">
                                        <div className="sm:col-span-2 rounded-lg border border-blue-300 bg-white p-2.5">
                                            <label className="mb-1 block text-xs font-medium text-slate-600">Bildiriş gedəcək user ID-ləri</label>
                                            <input
                                                type="text"
                                                value={editForm.apns_config?.recipient_ids || ''}
                                                onChange={(e) => setEditForm((p) => ({ ...p, apns_config: { ...p.apns_config, recipient_ids: e.target.value } }))}
                                                placeholder="105, 107, 109"
                                                className="w-full rounded-lg border border-slate-200 bg-white px-2.5 py-1.5 font-mono text-xs focus:outline-none focus:border-sky-400"
                                            />
                                            <p className="mt-1 text-[10px] text-slate-500">Vergüllə ayır. Neçə ID yazsan, bildiriş hamısına gedəcək.</p>
                                        </div>
                                        {[
                                            { key: 'key_id',        label: 'Key ID',            ph: 'SCV5BKJ5P8' },
                                            { key: 'team_id',       label: 'Team ID',           ph: 'KLF3BBP563' },
                                            { key: 'topic',         label: 'Bundle ID (Topic)', ph: 'FiscalBusiness' },
                                            { key: 'p8_path',       label: '.p8 fayl yolu (serverdə)', ph: '/var/www/fiscalpos/fc.p8' },
                                            { key: 'token_api_url', label: 'Device token API URL', ph: 'https://api.fiscalbusiness.net/telefonid-by-reqemid' },
                                            { key: 'token_api_key', label: 'Device token API key', ph: 'Qv9eiJwe...' },
                                            { key: 'async_secret',  label: 'Async push secret', ph: 'QF_async_2026_secret' }
                                        ].map((f) => (
                                            <div key={f.key} className={f.key === 'token_api_url' || f.key === 'p8_path' ? 'sm:col-span-2' : ''}>
                                                <label className="mb-1 block text-xs font-medium text-slate-600">{f.label}</label>
                                                <input
                                                    type="text"
                                                    value={editForm.apns_config?.[f.key] || ''}
                                                    onChange={(e) => setEditForm((p) => ({ ...p, apns_config: { ...p.apns_config, [f.key]: e.target.value } }))}
                                                    placeholder={f.ph}
                                                    className="w-full rounded-lg border border-slate-200 bg-white px-2.5 py-1.5 font-mono text-xs focus:outline-none focus:border-sky-400"
                                                />
                                            </div>
                                        ))}
                                        <label className="flex items-center justify-between rounded-lg border border-slate-200 bg-white px-2.5 py-2 text-xs font-medium text-slate-800 sm:col-span-2">
                                            <span>Sandbox (test) rejimi. Production üçün söndürün</span>
                                            <input
                                                type="checkbox"
                                                checked={Boolean(editForm.apns_config?.use_sandbox)}
                                                onChange={(e) => setEditForm((p) => ({ ...p, apns_config: { ...p.apns_config, use_sandbox: e.target.checked } }))}
                                                className="sa-switch"
                                            />
                                        </label>
                                        <p className="mt-0.5 text-[10px] text-slate-500 sm:col-span-2">
                                            Apple Developer → Keys-dən Key ID/Team ID/.p8, App Store Connect → App Info-dan Bundle ID götürülür. .p8 faylı serverə əl ilə kopyalanmalıdır.
                                        </p>
                                    </div>
                                )}
                            </div>
                                </div>
                            </section>
                            {/* ── Bildiriş kanalı (ofis + günlük 23:00) ── */}
                            {(editForm.apns_enabled || editForm.whatsapp_enabled) && (
                                <div className="rounded-2xl border border-slate-200 bg-white p-4">
                                    <p className="mb-2 text-sm font-medium text-slate-800">Ofis ödənişləri + günlük yekun hansı kanalla getsin?</p>
                                    <div className="flex gap-2">
                                        {[
                                            { v: 'apns', label: 'iOS Push', dis: !editForm.apns_enabled },
                                            { v: 'whatsapp', label: 'WhatsApp', dis: !editForm.whatsapp_enabled },
                                        ].map((o) => (
                                            <button
                                                key={o.v}
                                                type="button"
                                                disabled={o.dis}
                                                onClick={() => setEditForm({ ...editForm, notify_channel: o.v })}
                                                className={`flex-1 rounded-lg border px-3 py-2 text-sm font-bold transition-all ${
                                                    editForm.notify_channel === o.v
                                                        ? 'border-slate-900 bg-slate-900 text-white'
                                                        : o.dis ? 'border-gray-100 bg-gray-50 text-gray-300' : 'border-indigo-200 bg-white text-slate-600 hover:bg-slate-50'
                                                }`}
                                            >
                                                {o.label}
                                            </button>
                                        ))}
                                    </div>

                                    {/* Günlük yekun saatı:dəqiqəsi (Baku) — hər tenant özü seçir */}
                                    <div className="mt-3 flex items-center justify-between gap-2">
                                        <label className="text-xs font-semibold text-slate-800">Günlük yekun vaxtı (Baku)</label>
                                        <input
                                            type="time"
                                            value={`${String(Number(editForm.daily_summary_hour || 0)).padStart(2, '0')}:${String(Number(editForm.daily_summary_minute || 0)).padStart(2, '0')}`}
                                            onChange={(e) => {
                                                const [h, m] = String(e.target.value || '23:00').split(':').map(Number);
                                                setEditForm({ ...editForm, daily_summary_hour: h || 0, daily_summary_minute: m || 0 });
                                            }}
                                            className="rounded-lg border border-slate-200 bg-white px-3 py-1.5 text-sm font-bold text-slate-800 focus:outline-none focus:border-sky-400"
                                        />
                                    </div>

                                    <p className="mt-1.5 text-[10px] text-slate-500">Seçilən kanal aktiv olmalıdır. WhatsApp seçilsə bildirişlər yuxarıdakı nömrələrə gedəcək. Günlük yekun hər gün seçdiyiniz saatda göndərilir (standart 23:00).</p>
                                </div>
                            )}
                        </div>
                        )}

                        {editTab === 'more' && (
                        <div className="space-y-7">
                            {/* Payriff ödəniş şlüzü (rezervasiya behi) */}
                            <div className="rounded-2xl border border-slate-200 bg-white p-4">
                                <label className="flex cursor-pointer items-center justify-between gap-2 text-sm font-semibold text-slate-800">
                                    <span className="flex items-center gap-1.5"><span>Payriff ödəniş (rezervasiya + banket behi)</span></span>
                                    <input
                                        type="checkbox"
                                        checked={Boolean(editForm.payriff_enabled)}
                                        onChange={(e) => setEditForm({ ...editForm, payriff_enabled: e.target.checked })}
                                        className="sa-switch"
                                    />
                                </label>
                                {editForm.payriff_enabled && (
                                    <div className="mt-2 space-y-2">
                                        <div>
                                            <label className="mb-1 block text-xs font-medium text-slate-600">Secret açar (Payriff → Applications)</label>
                                            <input
                                                type="text"
                                                autoComplete="off"
                                                value={editForm.payriff_secret || ''}
                                                onChange={(e) => setEditForm({ ...editForm, payriff_secret: e.target.value })}
                                                placeholder="məs. 918B3ABE... (test açarı)"
                                                className="w-full rounded-lg border border-gray-200 px-3 py-2 font-mono text-xs focus:border-sky-400 focus:outline-none"
                                            />
                                        </div>
                                        <div className="flex items-center gap-2">
                                            <label className="text-xs font-medium text-slate-600">Beh faizi</label>
                                            <input
                                                type="number"
                                                min={1}
                                                max={100}
                                                value={editForm.booking_deposit_percent ?? 30}
                                                onChange={(e) => setEditForm({ ...editForm, booking_deposit_percent: e.target.value })}
                                                className="w-20 rounded-lg border border-gray-200 px-2 py-1.5 text-sm font-semibold focus:border-sky-400 focus:outline-none"
                                            />
                                            <span className="text-sm font-bold text-slate-600">%</span>
                                        </div>
                                        <p className="text-[10px] text-slate-500">
                                            Aktivdirsə: /evler rezervasiyasında VƏ /banket sifarişində cəmin {editForm.booking_deposit_percent ?? 30}%-i (yuxarı 10-a yuvarlanmış) beh kimi Payriff ilə ödənilir, sonra təsdiq görünür.
                                        </p>
                                    </div>
                                )}
                            </div>
                            <div className="rounded-2xl border border-slate-200 bg-white p-4">
                                <label className="flex items-center justify-between text-sm font-medium text-slate-800">
                                    <span className="flex items-center gap-1.5">
                                        <span>Qizil Farel rezervasiya inteqrasiyası</span>
                                    </span>
                                    <input
                                        type="checkbox"
                                        checked={Boolean(editForm.qizilfarel_enabled)}
                                        onChange={(e) => setEditForm({ ...editForm, qizilfarel_enabled: e.target.checked })}
                                        className="sa-switch"
                                    />
                                </label>
                                {editForm.qizilfarel_enabled && (
                                    <div className="mt-2.5">
                                        <label className="mb-1 block text-xs font-medium text-slate-600">API Key</label>
                                        <input
                                            type="text"
                                            value={editForm.qizilfarel_api_key || ''}
                                            onChange={(e) => setEditForm({ ...editForm, qizilfarel_api_key: e.target.value })}
                                            placeholder="qf_live_XXXXXXXXXXXXXXXXXXX"
                                            className="w-full rounded-lg border border-slate-200 bg-white px-2.5 py-1.5 font-mono text-xs focus:outline-none focus:border-sky-400"
                                        />
                                        <p className="mt-1 text-[10px] text-slate-500">
                                            Aktiv olduqda Evlər bölməsi manuala bağlanır, Qizil Farel-dən qonaqlar avtomatik gəlir.
                                        </p>
                                    </div>
                                )}
                            </div>
                        </div>
                        )}

                        </div>
                        <div className="flex shrink-0 items-center justify-end gap-2 border-t border-slate-100 bg-white px-4 py-3.5 sm:px-7">
                            <button type="button" onClick={() => setEditModal(false)} className="rounded-xl border border-slate-200 px-4 py-2.5 text-sm font-semibold text-slate-600 transition-colors hover:bg-slate-50">
                                Ləğv et
                            </button>
                            <button type="submit" className="rounded-xl bg-[#0090bb] px-6 py-2.5 text-sm font-semibold text-white shadow-sm shadow-sky-900/20 transition-colors hover:bg-[#007ea4]">
                                Yadda saxla
                            </button>
                        </div>
                        </form>
                        </div>
                    </div>
                </div>
            )}

            {/* ── Mətbəx ekranı şifrəsiz linkləri ── */}
            {kitchenLinks && (() => {
                const base = window.location.origin;
                const link = (suffix) => `${base}/kitchen/?t=${kitchenLinks.token}${suffix ? '_' + suffix : ''}`;
                const rows = [
                    { label: 'Bütün mətbəx (hamısı)', url: link('') },
                    ...(kitchenLinks.printers || []).map((p) => ({ label: p.label || p.name, url: link(p.name) })),
                ];
                const copy = (u) => navigator.clipboard?.writeText(u).then(() => toast.success('Kopyalandı.')).catch(() => toast.error('Kopyalana bilmədi.'));
                return (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/50 backdrop-blur-sm p-4" onClick={(e) => e.target === e.currentTarget && setKitchenLinks(null)}>
                    <div className="flex max-h-[92vh] w-full max-w-2xl flex-col rounded-2xl bg-white shadow-xl">
                        <div className="border-b border-gray-100 p-5">
                            <h3 className="text-base font-semibold text-slate-900">Mətbəx ekranı linkləri</h3>
                            <p className="mt-0.5 text-xs text-slate-500">{kitchenLinks.tenant?.name} · şifrəsiz, uzun tokenli linklər (login tələb etmir)</p>
                        </div>
                        <div className="flex-1 overflow-y-auto p-5 space-y-3">
                            {Number(kitchenLinks.enabled) !== 1 && (
                                <div className="rounded-xl border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-700">
                                    Bu restoranda "Mətbəx ekranı" (kitchen_display) aktiv deyil, linklər açılmayacaq. Redaktə et → QR menyu → Public linklər bölməsindən aktivləşdirin.
                                </div>
                            )}
                            {kitchenLinksLoading ? (
                                <div className="py-8 text-center text-gray-400">Yüklənir...</div>
                            ) : rows.map((r) => (
                                <div key={r.url} className="rounded-xl border border-gray-200 p-3">
                                    <div className="mb-1.5 text-sm font-semibold text-slate-800">{r.label}</div>
                                    <div className="flex items-center gap-2">
                                        <input readOnly value={r.url} onFocus={(e) => e.target.select()}
                                            className="flex-1 min-w-0 rounded-lg border border-gray-200 bg-gray-50 px-2.5 py-1.5 text-[11px] font-mono text-gray-600" />
                                        <button type="button" onClick={() => copy(r.url)}
                                            className="shrink-0 rounded-lg border border-slate-200 bg-white px-3 py-1.5 text-xs font-semibold text-slate-700 hover:bg-slate-50">Kopyala</button>
                                        <a href={r.url} target="_blank" rel="noopener noreferrer"
                                            className="shrink-0 rounded-lg bg-slate-900 px-3 py-1.5 text-xs font-semibold text-white hover:bg-slate-800">Aç</a>
                                    </div>
                                </div>
                            ))}
                            {!kitchenLinksLoading && (kitchenLinks.printers || []).length === 0 && (
                                <p className="text-xs text-gray-400">Bu restoranda ayrıca mətbəx printeri yoxdur — yalnız "hamısı" linki var.</p>
                            )}
                        </div>
                        <div className="flex justify-end border-t border-gray-100 p-5">
                            <button type="button" onClick={() => setKitchenLinks(null)}
                                className="rounded-xl border border-gray-200 px-5 py-2.5 text-sm font-semibold text-gray-500 hover:bg-gray-50">Bağla</button>
                        </div>
                    </div>
                </div>
                );
            })()}

            {fiscalModal && selectedTenant && (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/50 backdrop-blur-sm p-4" onClick={(e) => e.target === e.currentTarget && setFiscalModal(false)}>
                    <div className="flex max-h-[94vh] w-full max-w-3xl flex-col overflow-hidden rounded-2xl bg-white shadow-2xl shadow-slate-900/20">
                        <div className="flex shrink-0 items-center gap-3 border-b border-slate-100 px-5 py-4 sm:px-6">
                            <div className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-xl bg-emerald-50 text-emerald-600">
                                <Receipt className="h-5 w-5" />
                            </div>
                            <div className="min-w-0 flex-1">
                                <h3 className="truncate text-base font-semibold text-slate-900">Kassa və fiskal konfiqurasiya</h3>
                                <p className="truncate text-xs text-slate-500">{selectedTenant.name} · tenant səviyyəsində ayar</p>
                            </div>
                            <button type="button" onClick={() => setFiscalModal(false)} aria-label="Bağla"
                                className="flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-xl text-slate-400 transition-colors hover:bg-slate-100 hover:text-slate-700">
                                <X className="h-5 w-5" />
                            </button>
                        </div>

                        <form onSubmit={handleSaveFiscalConfig} className="flex min-h-0 flex-1 flex-col">
                            <div className="min-h-0 flex-1 space-y-6 overflow-y-auto px-5 py-5 sm:px-6">
                                <div>
                                    <label className="mb-3 block text-[11px] font-semibold uppercase tracking-[0.12em] text-slate-400">Kassa tipi</label>
                                    <div className="grid grid-cols-2 gap-3">
                                        {FISCAL_PROVIDERS.map((provider) => (
                                            <button
                                                key={provider.value}
                                                type="button"
                                                onClick={() => setFiscalForm((prev) => ({ ...prev, provider: provider.value }))}
                                                className={`rounded-xl border-2 px-4 py-3 text-sm font-bold transition-all ${
                                                    fiscalForm.provider === provider.value
                                                        ? 'border-[#0090bb] bg-sky-50 text-[#007ea4]'
                                                        : 'border-slate-200 text-slate-500 hover:border-slate-300'
                                                }`}
                                            >
                                                {provider.label}
                                            </button>
                                        ))}
                                    </div>
                                </div>

                                {fiscalForm.provider === 'smartone' ? (
                                    <>
                                        <div className="-mb-3 text-[11px] font-semibold uppercase tracking-[0.12em] text-slate-400">Rejim</div>
                                        <div className={`rounded-2xl border border-slate-200 bg-white p-4 ${fiscalForm.smartone.manage_only ? 'opacity-50' : ''}`}>
                                            <label className={`flex items-center justify-between gap-4 ${fiscalForm.smartone.manage_only ? 'cursor-not-allowed' : 'cursor-pointer'}`}>
                                                <div>
                                                    <div className="text-sm font-bold text-slate-800">Kassa ilə işləsin</div>
                                                    <div className="text-xs text-slate-500">
                                                        Bu seçim aktivdirsə, hesab bağlananda satış SmartOne kassaya göndəriləcək.
                                                    </div>
                                                </div>
                                                <input
                                                    type="checkbox"
                                                    disabled={Boolean(fiscalForm.smartone.manage_only)}
                                                    checked={Boolean(fiscalForm.smartone.enabled)}
                                                    onChange={(e) => updateSmartoneField('enabled', e.target.checked)}
                                                    className="sa-switch"
                                                />
                                            </label>
                                            {fiscalForm.smartone.enabled && (
                                                <div className="mt-3 border-t border-slate-100 pt-3">
                                                    <div className="mb-2 text-xs font-semibold text-slate-600">Kassaya nə göndərilsin?</div>
                                                    <div className="grid grid-cols-1 gap-1.5 sm:grid-cols-3">
                                                        {[
                                                            { key: 'both', label: 'Hər ikisi' },
                                                            { key: 'pos',  label: 'Yalnız POS' },
                                                            { key: 'cash', label: 'Yalnız nağd' },
                                                        ].map((m) => (
                                                            <button key={m.key} type="button"
                                                                onClick={() => updateSmartoneField('send_mode', m.key)}
                                                                className={`rounded-xl border-2 px-3 py-2 text-xs font-semibold transition-all ${
                                                                    (fiscalForm.smartone.send_mode || 'both') === m.key
                                                                        ? 'border-[#0090bb] bg-sky-50 text-[#007ea4]'
                                                                        : 'border-gray-200 bg-white text-gray-500 hover:border-gray-300'
                                                                }`}>
                                                                {m.label}
                                                            </button>
                                                        ))}
                                                    </div>
                                                    <p className="mt-1.5 text-[11px] text-slate-500">
                                                        "Yalnız POS" — bölünmüş hesabda yalnız kart hissəsi kassaya/terminala gedir (məs. 50 nağd + 20 pos → terminala 20).
                                                    </p>
                                                </div>
                                            )}
                                        </div>

                                        <div className={`rounded-2xl border border-slate-200 bg-white p-4 ${fiscalForm.smartone.enabled ? 'opacity-50' : ''}`}>
                                            <label className={`flex items-center justify-between gap-4 ${fiscalForm.smartone.enabled ? 'cursor-not-allowed' : 'cursor-pointer'}`}>
                                                <div>
                                                    <div className="text-sm font-bold text-slate-800">Yalnız kassa idarəetməsi</div>
                                                    <div className="text-xs text-slate-500">
                                                        Aktiv olsa: shift aç/bağla, X/Z hesabat işləyir, AMMA satışlar avtomatik kassaya GETMƏZ.
                                                    </div>
                                                </div>
                                                <input
                                                    type="checkbox"
                                                    disabled={Boolean(fiscalForm.smartone.enabled)}
                                                    checked={Boolean(fiscalForm.smartone.manage_only)}
                                                    onChange={(e) => updateSmartoneField('manage_only', e.target.checked)}
                                                    className="sa-switch"
                                                />
                                            </label>
                                        </div>

                                        {/* POS terminala qoşulu kassa */}
                                        <div className="rounded-2xl border border-slate-200 bg-white p-4">
                                            <label className="flex cursor-pointer items-center justify-between gap-4">
                                                <div>
                                                    <div className="text-sm font-bold text-slate-800">POS terminala qoşulu kassadır</div>
                                                    <div className="mt-0.5 text-xs text-slate-500">
                                                        Aktivdirsə, ofisiantda <b>"POS-a göndər"</b> düyməsi görünür (kart terminaldan çəkilir). Söndürülsə, ofisiantda POS düyməsi olmur — yalnız admin/kassir hesabı bağlayır.
                                                    </div>
                                                </div>
                                                <input
                                                    type="checkbox"
                                                    checked={Boolean(fiscalForm.smartone.pos_terminal)}
                                                    onChange={(e) => updateSmartoneField('pos_terminal', e.target.checked)}
                                                    className="sa-switch"
                                                />
                                            </label>
                                        </div>

                                        <div className="-mb-3 text-[11px] font-semibold uppercase tracking-[0.12em] text-slate-400">Bağlantı parametrləri</div>
                                        <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                                            <div>
                                                <label className="mb-1.5 block text-xs font-medium text-slate-600">Protocol</label>
                                                <select value={fiscalForm.smartone.protocol} onChange={(e) => updateSmartoneField('protocol', e.target.value)} className={inputClass}>
                                                    <option value="http">http</option>
                                                    <option value="https">https</option>
                                                </select>
                                            </div>
                                            <div>
                                                <label className="mb-1.5 block text-xs font-medium text-slate-600">Host / IP</label>
                                                <input type="text" value={fiscalForm.smartone.host} onChange={(e) => updateSmartoneField('host', e.target.value)} className={inputClass} required />
                                            </div>
                                            <div>
                                                <label className="mb-1.5 block text-xs font-medium text-slate-600">Port</label>
                                                <input type="text" value={fiscalForm.smartone.port} onChange={(e) => updateSmartoneField('port', e.target.value)} className={inputClass} required />
                                            </div>
                                            <div>
                                                <label className="mb-1.5 block text-xs font-medium text-slate-600">Merchant ID</label>
                                                <input type="text" value={fiscalForm.smartone.merchantId} onChange={(e) => updateSmartoneField('merchantId', e.target.value)} className={inputClass} required />
                                            </div>
                                            <div>
                                                <label className="mb-1.5 block text-xs font-medium text-slate-600">Employee name</label>
                                                <input type="text" value={fiscalForm.smartone.employeeName} onChange={(e) => updateSmartoneField('employeeName', e.target.value)} className={inputClass} required />
                                            </div>
                                            <div>
                                                <label className="mb-1.5 block text-xs font-medium text-slate-600">WS name</label>
                                                <input type="text" value={fiscalForm.smartone.wsName} onChange={(e) => updateSmartoneField('wsName', e.target.value)} placeholder="Məs: Main POS" className={inputClass} />
                                            </div>
                                            <div>
                                                <label className="mb-1.5 block text-xs font-medium text-slate-600">Department name</label>
                                                <input type="text" value={fiscalForm.smartone.departmentName} onChange={(e) => updateSmartoneField('departmentName', e.target.value)} placeholder="Məs: Restaurant" className={inputClass} />
                                            </div>
                                            <div>
                                                <label className="mb-1.5 block text-xs font-medium text-slate-600">Department code</label>
                                                <input type="text" value={fiscalForm.smartone.departmentCode} onChange={(e) => updateSmartoneField('departmentCode', e.target.value)} placeholder="Məs: 1" className={inputClass} />
                                            </div>
                                        </div>

                                        <div>
                                            <label className="mb-3 block text-[11px] font-semibold uppercase tracking-[0.12em] text-slate-400">Vergi tipi</label>
                                            <div className="grid grid-cols-1 gap-2 md:grid-cols-2">
                                                {fiscalTaxProfiles.map((tax) => (
                                                    <button
                                                        key={tax.code}
                                                        type="button"
                                                        onClick={() => updateSmartoneField('taxProfileCode', tax.code)}
                                                        className={`rounded-xl border-2 px-4 py-3 text-left transition-all ${
                                                            fiscalForm.smartone.taxProfileCode === tax.code
                                                                ? 'border-[#0090bb] bg-sky-50/60'
                                                                : 'border-gray-200 hover:border-gray-300'
                                                        }`}
                                                    >
                                                        <div className="text-sm font-bold text-gray-800">{tax.label}</div>
                                                        <div className="text-xs text-gray-500">Kod: {tax.code}</div>
                                                        <div className="mt-1 text-xs text-gray-400">{tax.description}</div>
                                                    </button>
                                                ))}
                                            </div>
                                        </div>

                                        <div className="rounded-2xl border border-slate-200 bg-slate-50 p-4 text-sm text-slate-700">
                                            <div className="mb-2 font-bold">SmartOne üçün əsas dinamik ayarlar</div>
                                            <ul className="list-disc space-y-1 pl-5">
                                                <li>Host / IP</li>
                                                <li>Port</li>
                                                <li>Merchant ID</li>
                                                <li>Employee name</li>
                                                <li>WS name və Department məlumatları</li>
                                            </ul>
                                            <div className="mt-3 text-xs text-slate-500">Bu LAN API axınında user ID və token tələb olunmur.</div>
                                        </div>

                                        {fiscalNotes.length > 0 && (
                                            <div className="rounded-2xl border border-amber-200 bg-amber-50/60 p-4 text-xs text-amber-800">
                                                <div className="mb-2 font-semibold text-amber-900">Qeyd</div>
                                                <ul className="list-disc space-y-1 pl-5">
                                                    {fiscalNotes.map((note, index) => (
                                                        <li key={`${note}-${index}`}>{note}</li>
                                                    ))}
                                                </ul>
                                            </div>
                                        )}
                                    </>
                                ) : (
                                    <>
                                        <div className="-mb-3 text-[11px] font-semibold uppercase tracking-[0.12em] text-slate-400">Rejim</div>
                                        <div className={`rounded-2xl border border-slate-200 bg-white p-4 ${fiscalForm.caspos.manage_only ? 'opacity-50' : ''}`}>
                                            <label className={`flex items-center justify-between gap-4 ${fiscalForm.caspos.manage_only ? 'cursor-not-allowed' : 'cursor-pointer'}`}>
                                                <div>
                                                    <div className="text-sm font-bold text-slate-800">Kassa ilə işləsin</div>
                                                    <div className="text-xs text-slate-500">
                                                        Bu seçim aktivdirsə, hesab bağlananda satış Caspos kassaya göndəriləcək.
                                                    </div>
                                                </div>
                                                <input
                                                    type="checkbox"
                                                    disabled={Boolean(fiscalForm.caspos.manage_only)}
                                                    checked={Boolean(fiscalForm.caspos.enabled)}
                                                    onChange={(e) => updateCasposField('enabled', e.target.checked)}
                                                    className="sa-switch"
                                                />
                                            </label>
                                            {fiscalForm.caspos.enabled && (
                                                <div className="mt-3 border-t border-slate-100 pt-3">
                                                    <div className="mb-2 text-xs font-semibold text-slate-600">Kassaya nə göndərilsin?</div>
                                                    <div className="grid grid-cols-1 gap-1.5 sm:grid-cols-3">
                                                        {[
                                                            { key: 'both', label: 'Hər ikisi' },
                                                            { key: 'pos',  label: 'Yalnız POS' },
                                                            { key: 'cash', label: 'Yalnız nağd' },
                                                        ].map((m) => (
                                                            <button key={m.key} type="button"
                                                                onClick={() => updateCasposField('send_mode', m.key)}
                                                                className={`rounded-xl border-2 px-3 py-2 text-xs font-semibold transition-all ${
                                                                    (fiscalForm.caspos.send_mode || 'both') === m.key
                                                                        ? 'border-[#0090bb] bg-sky-50 text-[#007ea4]'
                                                                        : 'border-gray-200 bg-white text-gray-500 hover:border-gray-300'
                                                                }`}>
                                                                {m.label}
                                                            </button>
                                                        ))}
                                                    </div>
                                                    <p className="mt-1.5 text-[11px] text-slate-500">
                                                        "Yalnız POS" — bölünmüş hesabda yalnız kart hissəsi kassaya/terminala (TapXPhone) gedir (məs. 50 nağd + 20 pos → terminala 20).
                                                    </p>
                                                </div>
                                            )}
                                        </div>

                                        {/* Yalnız kassa idarəetməsi (manage_only) — satış getmir, yalnız shift/X/Z */}
                                        <div className={`rounded-2xl border border-slate-200 bg-white p-4 ${fiscalForm.caspos.enabled ? 'opacity-50' : ''}`}>
                                            <label className={`flex items-center justify-between gap-4 ${fiscalForm.caspos.enabled ? 'cursor-not-allowed' : 'cursor-pointer'}`}>
                                                <div>
                                                    <div className="text-sm font-bold text-slate-800">Yalnız kassa idarəetməsi</div>
                                                    <div className="text-xs text-slate-500">
                                                        Aktiv olsa: shift aç/bağla, X/Z hesabat işləyir, AMMA satışlar avtomatik kassaya GETMƏZ.
                                                    </div>
                                                </div>
                                                <input
                                                    type="checkbox"
                                                    disabled={Boolean(fiscalForm.caspos.enabled)}
                                                    checked={Boolean(fiscalForm.caspos.manage_only)}
                                                    onChange={(e) => updateCasposField('manage_only', e.target.checked)}
                                                    className="sa-switch"
                                                />
                                            </label>
                                        </div>

                                        {/* POS terminala birləşsin (TapXPhone) — yalnız Caspos + Kassa ilə işləsin */}
                                        {fiscalForm.caspos.enabled && (
                                            <div className="rounded-2xl border border-slate-200 bg-white p-4">
                                                <label className="flex cursor-pointer items-center justify-between gap-4">
                                                    <div>
                                                        <div className="text-sm font-bold text-slate-800">POS terminala birləşsin</div>
                                                        <div className="text-xs text-slate-500">
                                                            Kart/POS satışında məbləğ bank terminalına göndərilir (müştəri kartı yaxınlaşdırır). Uğursuz/yetərsiz balansda satış tamamlanmır. Yalnız Kapital Bank / Pasha Bank (TapXPhone) cihazları.
                                                        </div>
                                                    </div>
                                                    <input
                                                        type="checkbox"
                                                        checked={Boolean(fiscalForm.caspos.pos_terminal)}
                                                        onChange={(e) => updateCasposField('pos_terminal', e.target.checked)}
                                                        className="sa-switch"
                                                    />
                                                </label>
                                                {fiscalForm.caspos.pos_terminal && (
                                                    <div className="mt-3">
                                                        <label className="mb-1.5 block text-xs font-semibold uppercase tracking-wider text-slate-500">Bank</label>
                                                        <div className="grid grid-cols-2 gap-2">
                                                            {[['kapital', 'Kapital Bank'], ['pasha', 'Pasha Bank']].map(([v, l]) => (
                                                                <button key={v} type="button" onClick={() => updateCasposField('posBank', v)}
                                                                    className={`rounded-xl border-2 px-4 py-2.5 text-sm font-bold transition-all ${(fiscalForm.caspos.posBank || 'kapital') === v ? 'border-[#0090bb] bg-sky-50 text-[#007ea4]' : 'border-gray-200 text-gray-600 hover:border-gray-300'}`}>
                                                                    {l}
                                                                </button>
                                                            ))}
                                                        </div>
                                                    </div>
                                                )}
                                            </div>
                                        )}

                                        {/* Bağlantı üsulu (transport) */}
                                        <div>
                                            <label className="mb-3 block text-[11px] font-semibold uppercase tracking-[0.12em] text-slate-400">Bağlantı üsulu</label>
                                            <div className="grid grid-cols-1 gap-2 md:grid-cols-2">
                                                {[['local', 'Lokal (Fiscal POS proqramı)', 'Cihaz mağaza şəbəkəsindədir (192.168.x.x)'], ['server', 'Server (birbaşa)', 'Cihaz internetdən əlçatandır (public IP)']].map(([v, t, d]) => (
                                                    <button key={v} type="button" onClick={() => updateCasposField('transport', v)}
                                                        className={`rounded-xl border-2 px-4 py-3 text-left transition-all ${(fiscalForm.caspos.transport || 'local') === v ? 'border-[#0090bb] bg-sky-50/60' : 'border-gray-200 hover:border-gray-300'}`}>
                                                        <div className="text-sm font-bold text-gray-800">{t}</div>
                                                        <div className="mt-0.5 text-xs text-gray-400">{d}</div>
                                                    </button>
                                                ))}
                                            </div>
                                        </div>

                                        <div className="-mb-3 text-[11px] font-semibold uppercase tracking-[0.12em] text-slate-400">Bağlantı parametrləri</div>
                                        <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                                            <div>
                                                <label className="mb-1.5 block text-xs font-medium text-slate-600">Protocol</label>
                                                <select value={fiscalForm.caspos.protocol} onChange={(e) => updateCasposField('protocol', e.target.value)} className={inputClass}>
                                                    <option value="http">http</option>
                                                    <option value="https">https</option>
                                                </select>
                                            </div>
                                            <div>
                                                <label className="mb-1.5 block text-xs font-medium text-slate-600">Host / IP</label>
                                                <input type="text" value={fiscalForm.caspos.host} onChange={(e) => updateCasposField('host', e.target.value)} placeholder="Məs: 192.168.1.120" className={inputClass} required />
                                            </div>
                                            <div>
                                                <label className="mb-1.5 block text-xs font-medium text-slate-600">Port</label>
                                                <input type="text" value={fiscalForm.caspos.port} onChange={(e) => updateCasposField('port', e.target.value)} placeholder="Məs: 5544" className={inputClass} required />
                                            </div>
                                            <div>
                                                <label className="mb-1.5 block text-xs font-medium text-slate-600">Username</label>
                                                <input type="text" value={fiscalForm.caspos.username} onChange={(e) => updateCasposField('username', e.target.value)} className={inputClass} required />
                                            </div>
                                            <div>
                                                <label className="mb-1.5 block text-xs font-medium text-slate-600">Password</label>
                                                <input type="text" value={fiscalForm.caspos.password} onChange={(e) => updateCasposField('password', e.target.value)} className={inputClass} required />
                                            </div>
                                            <div>
                                                <label className="mb-1.5 block text-xs font-medium text-slate-600">Cashier name</label>
                                                <input type="text" value={fiscalForm.caspos.cashierName} onChange={(e) => updateCasposField('cashierName', e.target.value)} placeholder="Məs: Fiscal POS" className={inputClass} required />
                                            </div>
                                            <div>
                                                <label className="mb-1.5 block text-xs font-medium text-slate-600">Açılış məbləği</label>
                                                <input type="number" step="0.01" min="0" value={fiscalForm.caspos.shiftOpenAmount} onChange={(e) => updateCasposField('shiftOpenAmount', e.target.value)} placeholder="Məs: 0.00" className={inputClass} />
                                            </div>
                                            <div>
                                                <label className="mb-1.5 block text-xs font-medium text-slate-600">Barkod tipi</label>
                                                <input type="text" value={fiscalForm.caspos.codeType} onChange={(e) => updateCasposField('codeType', e.target.value)} placeholder="Məs: 1" className={inputClass} />
                                            </div>
                                            <div>
                                                <label className="mb-1.5 block text-xs font-medium text-slate-600">Ədəd məhsul tipi</label>
                                                <input type="text" value={fiscalForm.caspos.quantityTypePiece} onChange={(e) => updateCasposField('quantityTypePiece', e.target.value)} placeholder="Məs: 1" className={inputClass} />
                                            </div>
                                            <div>
                                                <label className="mb-1.5 block text-xs font-medium text-slate-600">Çəkili məhsul tipi</label>
                                                <input type="text" value={fiscalForm.caspos.quantityTypeWeight} onChange={(e) => updateCasposField('quantityTypeWeight', e.target.value)} placeholder="Məs: 3" className={inputClass} />
                                            </div>
                                        </div>

                                        <div>
                                            <label className="mb-3 block text-[11px] font-semibold uppercase tracking-[0.12em] text-slate-400">Vergi tipi</label>
                                            <div className="grid grid-cols-1 gap-2 md:grid-cols-3">
                                                {casposVatTypes.map((tax) => (
                                                    <button
                                                        key={tax.code}
                                                        type="button"
                                                        onClick={() => updateCasposField('vatType', tax.code)}
                                                        className={`rounded-xl border-2 px-4 py-3 text-left transition-all ${
                                                            fiscalForm.caspos.vatType === tax.code
                                                                ? 'border-[#0090bb] bg-sky-50/60'
                                                                : 'border-gray-200 hover:border-gray-300'
                                                        }`}
                                                    >
                                                        <div className="text-sm font-bold text-gray-800">{tax.label}</div>
                                                        <div className="text-xs text-gray-500">Kod: {tax.code}</div>
                                                        <div className="mt-1 text-xs text-gray-400">{tax.description}</div>
                                                    </button>
                                                ))}
                                            </div>
                                            <div className="mt-2 text-xs text-gray-500">
                                                Caspos sənədinə görə satışda `vatType` göndərilir. Burada seçdiyin vergi tipi hazırda bütün restoran satışlarına tətbiq olunacaq.
                                            </div>
                                        </div>

                                        <div className="rounded-2xl border border-slate-200 bg-slate-50 p-4 text-sm text-slate-700">
                                            <div className="mb-2 font-bold">Caspos üçün əsas dinamik ayarlar</div>
                                            <ul className="list-disc space-y-1 pl-5">
                                                <li>Host / IP</li>
                                                <li>Port</li>
                                                <li>Username</li>
                                                <li>Password</li>
                                                <li>Cashier name</li>
                                                <li>Vergi tipi</li>
                                            </ul>
                                            <div className="mt-3 text-xs text-slate-500">
                                                Cihazda əvvəlcə Integration Service aktiv olmalıdır. Standart port adətən 5544, protocol isə http olur.
                                            </div>
                                        </div>

                                        {casposNotes.length > 0 && (
                                            <div className="rounded-2xl border border-amber-200 bg-amber-50/60 p-4 text-xs text-amber-800">
                                                <div className="mb-2 font-semibold text-amber-900">Qeyd</div>
                                                <ul className="list-disc space-y-1 pl-5">
                                                    {casposNotes.map((note, index) => (
                                                        <li key={`${note}-${index}`}>{note}</li>
                                                    ))}
                                                </ul>
                                            </div>
                                        )}
                                    </>
                                )}

                                {fiscalTestResult && (
                                    <div className="rounded-2xl bg-slate-950 p-4 text-xs text-emerald-300">
                                        <div className="mb-2 font-bold text-white">Test cavabı</div>
                                        <pre className="overflow-auto whitespace-pre-wrap break-all">{JSON.stringify(fiscalTestResult, null, 2)}</pre>
                                    </div>
                                )}
                            </div>

                            <div className="flex shrink-0 flex-wrap items-center justify-between gap-3 border-t border-slate-100 px-5 py-3.5 sm:px-6">
                                <button
                                    type="button"
                                    onClick={handleTestFiscalConfig}
                                    disabled={testingFiscal}
                                    className="flex items-center gap-1.5 rounded-xl border border-slate-200 px-4 py-2.5 text-sm font-semibold text-slate-700 transition-colors hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-50"
                                >
                                    <Stethoscope className="h-4 w-4 text-emerald-600" />
                                    {testingFiscal
                                        ? 'Test edilir...'
                                        : fiscalForm.provider === 'smartone'
                                            ? 'Test et (/get_info)'
                                            : 'Test et (getInfo)'}
                                </button>
                                <div className="flex gap-2">
                                    <button type="button" onClick={() => setFiscalModal(false)} className="rounded-xl border border-slate-200 px-4 py-2.5 text-sm font-semibold text-slate-600 transition-colors hover:bg-slate-50">
                                        Ləğv et
                                    </button>
                                    <button type="submit" disabled={savingFiscal} className="rounded-xl px-5 py-2.5 text-sm font-semibold text-white disabled:opacity-60 bg-[#0090bb] font-semibold transition-colors hover:bg-[#007ea4]">
                                        {savingFiscal ? 'Yadda saxlanır...' : 'Yadda saxla'}
                                    </button>
                                </div>
                            </div>
                        </form>
                    </div>
                </div>
            )}

            {passModal && selectedTenant && (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/50 backdrop-blur-sm p-4" onClick={(e) => e.target === e.currentTarget && setPassModal(false)}>
                    <div className="w-full max-w-xs rounded-2xl bg-white p-6 shadow-xl">
                        <h3 className="mb-2 text-lg font-bold text-gray-800">Şifrə sıfırla</h3>
                        <p className="mb-5 text-sm text-gray-400">{selectedTenant.name}</p>
                        <form onSubmit={handleResetPass} className="flex flex-col gap-4">
                            <div>
                                <label className="mb-1.5 block text-xs font-medium text-slate-600">Yeni şifrə</label>
                                <input
                                    type="password"
                                    value={newPass}
                                    onChange={(e) => setNewPass(e.target.value)}
                                    placeholder="Minimum 4 simvol"
                                    className={inputClass}
                                    required
                                    minLength={4}
                                    autoFocus
                                />
                            </div>
                            <div className="flex gap-3">
                                <button type="button" onClick={() => setPassModal(false)} className="flex-1 rounded-xl border border-gray-200 py-2.5 text-sm font-semibold text-gray-500 hover:bg-gray-50">
                                    Ləğv et
                                </button>
                                <button type="submit" className="flex-1 rounded-xl py-2.5 text-sm font-semibold text-white bg-[#0090bb] font-semibold transition-colors hover:bg-[#007ea4]">
                                    Yadda saxla
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}
            {/* DİAQNOSTİKA MODAL */}
            {diagModal && selectedTenant && (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/50 backdrop-blur-sm p-4" onClick={(e) => e.target === e.currentTarget && setDiagModal(false)}>
                    <div className="flex max-h-[92vh] w-full max-w-4xl flex-col rounded-2xl bg-white shadow-xl">
                        <div className="flex items-center justify-between border-b border-gray-100 px-6 py-4 shrink-0">
                            <div className="min-w-0">
                                <h3 className="text-base font-semibold text-slate-900 truncate">Diaqnostika</h3>
                                <p className="text-xs text-gray-400 truncate">{selectedTenant.name}</p>
                            </div>
                            <button onClick={() => setDiagModal(false)} className="text-gray-400 hover:text-gray-600 text-xl">✕</button>
                        </div>

                        <div className="flex-1 min-h-0 overflow-y-auto px-6 py-5">
                            {diagLoading ? (
                                <div className="py-10 text-center text-gray-400">Yüklənir...</div>
                            ) : diagData ? (
                                <div className="space-y-5">
                                    {/* Üst stat kartları */}
                                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                                        <div className="rounded-xl border border-rose-100 bg-rose-50/40 p-4">
                                            <div className="text-[10px] font-bold uppercase tracking-wider text-rose-700">Ofis transferləri</div>
                                            <div className="text-2xl font-black text-rose-800 mt-1">
                                                {Number(diagData.ofis_transfers.total || 0).toFixed(2)} ₼
                                            </div>
                                            <div className="text-[11px] text-rose-600/80 mt-0.5">
                                                {diagData.ofis_transfers.count} yazı
                                            </div>
                                        </div>
                                        <div className="rounded-xl border border-amber-100 bg-amber-50/40 p-4">
                                            <div className="text-[10px] font-bold uppercase tracking-wider text-amber-700">Ofis xərcləri</div>
                                            <div className="text-2xl font-black text-amber-800 mt-1">
                                                {Number(diagData.ofis_expenses.total || 0).toFixed(2)} ₼
                                            </div>
                                            <div className="text-[11px] text-amber-600/80 mt-0.5">
                                                {diagData.ofis_expenses.count} yazı
                                            </div>
                                        </div>
                                        <div className="rounded-xl border border-blue-100 bg-blue-50/40 p-4">
                                            <div className="text-[10px] font-bold uppercase tracking-wider text-blue-700">Daxili transfer (2→3)</div>
                                            <div className="text-2xl font-black text-blue-800 mt-1">
                                                {Number(diagData.ofis_internal_transfers.total || 0).toFixed(2)} ₼
                                            </div>
                                            <div className="text-[11px] text-blue-600/80 mt-0.5">
                                                {diagData.ofis_internal_transfers.count} yazı
                                            </div>
                                        </div>
                                    </div>

                                    {/* Note-a görə qruplaşma */}
                                    {diagData.ofis_transfers.by_note?.length > 0 && (
                                        <div>
                                            <h4 className="text-sm font-bold text-gray-700 mb-2">Note üzrə qruplaşma</h4>
                                            <div className="rounded-xl border border-gray-200 overflow-hidden">
                                                <table className="w-full text-sm">
                                                    <thead className="bg-gray-50">
                                                        <tr>
                                                            <th className="px-3 py-2 text-left text-[11px] font-bold uppercase text-gray-500">Note</th>
                                                            <th className="px-3 py-2 text-center text-[11px] font-bold uppercase text-gray-500">Sayı</th>
                                                            <th className="px-3 py-2 text-right text-[11px] font-bold uppercase text-gray-500">Cəm</th>
                                                        </tr>
                                                    </thead>
                                                    <tbody>
                                                        {diagData.ofis_transfers.by_note.map((r, i) => (
                                                            <tr key={i} className="border-t border-gray-100">
                                                                <td className="px-3 py-2 text-gray-700">{r.note}</td>
                                                                <td className="px-3 py-2 text-center text-gray-500">{r.cnt}</td>
                                                                <td className="px-3 py-2 text-right font-mono font-bold text-gray-800">{Number(r.total).toFixed(2)} ₼</td>
                                                            </tr>
                                                        ))}
                                                    </tbody>
                                                </table>
                                            </div>
                                        </div>
                                    )}

                                    {/* Transferlər siyahısı */}
                                    <div>
                                        <h4 className="text-sm font-bold text-gray-700 mb-2">Ofis transferlər siyahısı (son 200)</h4>
                                        <div className="rounded-xl border border-gray-200 overflow-hidden">
                                            <table className="w-full text-sm">
                                                <thead className="bg-gray-50">
                                                    <tr>
                                                        <th className="px-3 py-2 text-left text-[11px] font-bold uppercase text-gray-500">ID</th>
                                                        <th className="px-3 py-2 text-left text-[11px] font-bold uppercase text-gray-500">Tarix</th>
                                                        <th className="px-3 py-2 text-center text-[11px] font-bold uppercase text-gray-500">Ofis</th>
                                                        <th className="px-3 py-2 text-right text-[11px] font-bold uppercase text-gray-500">Məbləğ</th>
                                                        <th className="px-3 py-2 text-left text-[11px] font-bold uppercase text-gray-500">Note</th>
                                                        <th className="px-3 py-2 text-center text-[11px] font-bold uppercase text-gray-500">Sil</th>
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    {diagData.ofis_transfers.list.map((t) => (
                                                        <tr key={t.id} className="border-t border-gray-100">
                                                            <td className="px-3 py-2 text-gray-400 font-mono">{t.id}</td>
                                                            <td className="px-3 py-2 text-gray-700 font-mono text-xs">{String(t.date).slice(0, 10)}</td>
                                                            <td className="px-3 py-2 text-center text-gray-500">{t.ofis_no || 2}</td>
                                                            <td className="px-3 py-2 text-right font-mono font-bold text-rose-700">{Number(t.amount).toFixed(2)}</td>
                                                            <td className="px-3 py-2 text-gray-600 text-xs truncate max-w-[200px]">{t.note || '—'}</td>
                                                            <td className="px-3 py-2 text-center">
                                                                <button
                                                                    onClick={() => deleteOneTransfer(t.id)}
                                                                    className="text-red-500 hover:text-red-700 font-bold"
                                                                    title="Bu transferi sil"
                                                                >✕</button>
                                                            </td>
                                                        </tr>
                                                    ))}
                                                    {diagData.ofis_transfers.list.length === 0 && (
                                                        <tr>
                                                            <td colSpan={6} className="px-3 py-6 text-center text-gray-400 text-sm">Heç bir transfer yoxdur</td>
                                                        </tr>
                                                    )}
                                                </tbody>
                                            </table>
                                        </div>
                                    </div>
                                </div>
                            ) : null}
                        </div>

                        <div className="flex flex-wrap gap-2 border-t border-gray-100 px-6 py-4 shrink-0">
                            <button
                                type="button"
                                onClick={reloadDiagnostics}
                                className="rounded-xl border border-gray-200 px-4 py-2 text-sm font-semibold text-gray-600 hover:bg-gray-50"
                            >
                                Yenilə
                            </button>
                            <div className="flex-1" />
                            <button
                                type="button"
                                onClick={deleteAllTransfers}
                                disabled={!diagData?.ofis_transfers?.count}
                                className="rounded-xl bg-red-600 hover:bg-red-700 px-4 py-2 text-sm font-bold text-white disabled:opacity-50"
                            >
                                Hamısını sıfırla ({diagData?.ofis_transfers?.count || 0})
                            </button>
                            <button
                                type="button"
                                onClick={() => setDiagModal(false)}
                                className="rounded-xl border border-gray-200 px-4 py-2 text-sm font-semibold text-gray-500 hover:bg-gray-50"
                            >
                                Bağla
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </SuperadminLayout>
    );
};

export default Tenants;
